import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  Box,
  Typography,
  Paper,
  Grid,
  Stack,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Button,
  Chip,
  Divider,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Skeleton,
  Alert,
  IconButton,
  Tooltip,
  Tabs,
  Tab,
  LinearProgress,
} from '@mui/material';
import { alpha } from '@mui/material/styles';
import RefreshIcon from '@mui/icons-material/Refresh';
import DownloadIcon from '@mui/icons-material/Download';
import PointOfSaleIcon from '@mui/icons-material/PointOfSale';
import ReceiptLongIcon from '@mui/icons-material/ReceiptLong';
import TrendingUpIcon from '@mui/icons-material/TrendingUp';
import DiscountOutlinedIcon from '@mui/icons-material/DiscountOutlined';
import LocalShippingOutlinedIcon from '@mui/icons-material/LocalShippingOutlined';
import CancelOutlinedIcon from '@mui/icons-material/CancelOutlined';
import PaymentsOutlinedIcon from '@mui/icons-material/PaymentsOutlined';
import FactCheckOutlinedIcon from '@mui/icons-material/FactCheckOutlined';
import StorefrontOutlinedIcon from '@mui/icons-material/StorefrontOutlined';
import ErrorOutlineOutlinedIcon from '@mui/icons-material/ErrorOutlineOutlined';
import AccountBalanceWalletOutlinedIcon from '@mui/icons-material/AccountBalanceWalletOutlined';
import SyncOutlinedIcon from '@mui/icons-material/SyncOutlined';
import BalanceOutlinedIcon from '@mui/icons-material/BalanceOutlined';
import RequestQuoteOutlinedIcon from '@mui/icons-material/RequestQuoteOutlined';
import AddCircleOutlineOutlinedIcon from '@mui/icons-material/AddCircleOutlineOutlined';
import AutorenewOutlinedIcon from '@mui/icons-material/AutorenewOutlined';
import CategoryOutlinedIcon from '@mui/icons-material/CategoryOutlined';
import CheckCircleOutlineOutlinedIcon from '@mui/icons-material/CheckCircleOutlineOutlined';
import WarningAmberOutlinedIcon from '@mui/icons-material/WarningAmberOutlined';
import AssessmentOutlinedIcon from '@mui/icons-material/AssessmentOutlined';
import PercentOutlinedIcon from '@mui/icons-material/PercentOutlined';
import CloudSyncOutlinedIcon from '@mui/icons-material/CloudSyncOutlined';
import AccountBalanceOutlinedIcon from '@mui/icons-material/AccountBalanceOutlined';
import SearchOutlinedIcon from '@mui/icons-material/SearchOutlined';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import EditOutlinedIcon from '@mui/icons-material/EditOutlined';
import { getApiUrl } from '../utils/api';
import { apiFetch } from '../utils/apiFetch';
import { useAuth } from '../auth/AuthContext';
import PageMenuButton from '../components/PageMenuButton';
import { isAdminLike } from '../utils/auth';

const API_URL = getApiUrl();
const IFOOD_FINANCIAL_HOMOLOGATION_START = '2025-08-01';
const IFOOD_FINANCIAL_HOMOLOGATION_END = '2025-08-31';
const IFOOD_FINANCIAL_HOMOLOGATION_COMPETENCE = '2025-08';
const IFOOD_FINANCIAL_HOMOLOGATION_LABEL = 'Agosto/2025';

const CHANNELS = [
  { key: 'ifood', label: 'iFood', color: 'primary' },
  { key: 'balcao', label: 'Balcão', color: 'success' },
  { key: 'salao', label: 'Salão', color: 'error' },
  { key: 'site', label: 'Site', color: 'warning' },
  { key: 'pedidos10', label: 'Pedidos10', color: 'secondary' },
  { key: '99food', label: '99Food', color: 'warning' },
];

const ACCOUNT_QUICK_FILTERS = [
  { key: 'pending', label: 'Pendentes', type: 'all', status: 'pending' },
  { key: 'receivable', label: 'A receber', type: 'receivable', status: 'pending' },
  { key: 'payable', label: 'A pagar', type: 'payable', status: 'pending' },
  { key: 'overdue', label: 'Vencidas', type: 'all', status: 'overdue' },
  { key: 'paid', label: 'Pagas', type: 'all', status: 'paid' },
  { key: 'all', label: 'Todas', type: 'all', status: 'all' },
];

const FREQUENCY_LABELS = {
  weekly: 'Semanal',
  biweekly: 'Quinzenal',
  monthly: 'Mensal',
  yearly: 'Anual',
};

const DRE_GROUP_OPTIONS = [
  { key: 'sales_revenue', label: 'Receita de vendas' },
  { key: 'other_revenue', label: 'Outras receitas' },
  { key: 'cogs', label: 'CMV' },
  { key: 'tax', label: 'Impostos/taxas' },
  { key: 'marketplace_fee', label: 'Marketplace/pagamento' },
  { key: 'delivery_cost', label: 'Entrega/subsídios' },
  { key: 'variable_expense', label: 'Despesa variável' },
  { key: 'fixed_expense', label: 'Despesa fixa' },
  { key: 'operational_expense', label: 'Despesa operacional' },
  { key: 'financial_expense', label: 'Despesa financeira' },
  { key: 'out_of_dre', label: 'Fora da DRE' },
];

const COST_BEHAVIOR_OPTIONS = [
  { key: 'fixed', label: 'Fixo' },
  { key: 'variable', label: 'Variável' },
  { key: 'mixed', label: 'Misto' },
  { key: 'none', label: 'Não aplica' },
];

const DEFAULT_CATEGORY_FORM = {
  name: '',
  type: 'both',
  color: '#2e7d32',
  dreGroup: 'operational_expense',
  costBehavior: 'fixed',
  includeInDre: true,
};

const DEFAULT_PARTY_FORM = {
  name: '',
  type: 'supplier',
  document: '',
  phone: '',
  email: '',
  notes: '',
  isActive: true,
};

const ACCOUNT_TYPE_LABELS = {
  both: 'Ambos',
  payable: 'Pagar',
  receivable: 'Receber',
};

const GOAL_DEFINITIONS = [
  { metric: 'revenue', label: 'Receita gerencial', format: 'currency', comparison: 'minimum' },
  { metric: 'net_result', label: 'Resultado gerencial', format: 'currency', comparison: 'minimum' },
  { metric: 'net_margin', label: 'Margem líquida', format: 'percent', comparison: 'minimum' },
  { metric: 'cogs_ratio', label: 'CMV sobre receita', format: 'percent', comparison: 'maximum' },
  { metric: 'expense_ratio', label: 'Despesa sobre receita', format: 'percent', comparison: 'maximum' },
];

const IFOOD_FINANCIAL_RECORD_TYPES = [
  { key: 'all', label: 'Todos' },
  { key: 'sale', label: 'Vendas' },
  { key: 'financial_event', label: 'Eventos' },
  { key: 'settlement', label: 'Repasses' },
  { key: 'anticipation', label: 'Antecipações' },
  { key: 'reconciliation', label: 'Conciliação' },
];

const IFOOD_FINANCIAL_TYPE_LABELS = IFOOD_FINANCIAL_RECORD_TYPES.reduce((acc, item) => {
  acc[item.key] = item.label;
  return acc;
}, {});

const DEFAULT_COST_CENTER_FORM = {
  name: '',
  code: '',
  color: '#1976d2',
  isActive: true,
};

const DEFAULT_QUICK_CATEGORY_FORM = {
  name: '',
  dreGroup: 'operational_expense',
  costBehavior: 'fixed',
  includeInDre: true,
};

const DEFAULT_QUICK_SUPPLIER_FORM = {
  name: '',
  document: '',
  phone: '',
  email: '',
};

const DEFAULT_QUICK_COST_CENTER_FORM = {
  name: '',
  code: '',
  color: '#1976d2',
};

const moneyColumnsSx = {
  fontVariantNumeric: 'tabular-nums',
  whiteSpace: 'nowrap',
};

const financePageSx = {
  width: '100%',
  maxWidth: '100%',
  minWidth: 0,
  boxSizing: 'border-box',
  px: { xs: 1.5, sm: 2 },
  pt: 1.5,
  pb: 3,
  overflowX: 'hidden',
  '& .MuiPaper-root': {
    minWidth: 0,
    maxWidth: '100%',
    boxSizing: 'border-box',
  },
  '& .MuiAlert-root': {
    minWidth: 0,
    maxWidth: '100%',
    alignItems: 'flex-start',
  },
  '& .MuiAlert-message': {
    minWidth: 0,
    overflowWrap: 'anywhere',
  },
  '& .MuiTableContainer-root': {
    width: '100%',
    maxWidth: '100%',
    overflowX: 'auto',
  },
  '& .MuiFormControl-root, & .MuiTextField-root, & .MuiInputBase-root, & .MuiButton-root, & .MuiChip-root': {
    maxWidth: '100%',
  },
  '& .MuiInputBase-root': {
    minWidth: 0,
  },
  '& .MuiChip-label': {
    minWidth: 0,
    overflow: 'hidden',
    textOverflow: 'ellipsis',
  },
  '& .MuiGrid-container': {
    minWidth: 0,
  },
  '& .MuiGrid-item': {
    minWidth: 0,
  },
};

const financeTabsSx = {
  minHeight: 46,
  width: '100%',
  maxWidth: '100%',
  minWidth: 0,
  px: { xs: 0.25, sm: 0.5 },
  '& .MuiTabs-scroller': {
    overflowX: 'auto !important',
  },
  '& .MuiTabs-flexContainer': {
    width: 'max-content',
    gap: { xs: 0.25, sm: 0.5 },
  },
  '& .MuiTab-root': {
    minHeight: 46,
    minWidth: 'auto',
    maxWidth: { xs: 210, sm: 'none' },
    px: { xs: 1, sm: 1.35, md: 1.5 },
    flexShrink: 0,
    fontWeight: 800,
    fontSize: { xs: 14, sm: 15 },
    lineHeight: 1.15,
    textTransform: 'none',
    whiteSpace: 'nowrap',
  },
  '& .MuiTab-iconWrapper': {
    mr: { xs: 0.5, sm: 0.75 },
  },
};

const formatCurrency = (value) => {
  const numeric = Number(value || 0);
  return numeric.toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' });
};

const formatPercent = (value) => {
  const numeric = Number(value || 0);
  return numeric.toLocaleString('pt-BR', { style: 'percent', maximumFractionDigits: 0 });
};

const formatDateTime = (value) => {
  if (!value) return '-';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return '-';
  return date.toLocaleString('pt-BR', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
};

const toDateInputValue = (date) => {
  const parsed = new Date(date);
  const year = parsed.getFullYear();
  const month = String(parsed.getMonth() + 1).padStart(2, '0');
  const day = String(parsed.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
};

const dateInputToLocalBoundary = (dateValue, dayOffset = 0) => {
  if (!dateValue) return null;
  const [year, month, day] = dateValue.split('-').map(Number);
  if (!year || !month || !day) return null;
  return new Date(year, month - 1, day + dayOffset, 0, 0, 0, 0).toISOString();
};

const toNumber = (value) => {
  if (value === null || value === undefined || value === '') return 0;
  if (typeof value === 'number') return Number.isFinite(value) ? value : 0;
  if (typeof value === 'string') {
    const normalized = value.includes(',')
      ? value.replace(/\./g, '').replace(',', '.')
      : value;
    const num = Number(normalized);
    return Number.isFinite(num) ? num : 0;
  }
  const num = Number(value);
  return Number.isFinite(num) ? num : 0;
};

const hasMoneyValue = (value) => value !== null && value !== undefined && value !== '';

const firstKnownNumber = (...values) => {
  for (const value of values) {
    if (hasMoneyValue(value)) return toNumber(value);
  }
  return 0;
};

const firstNumber = (...values) => {
  for (const value of values) {
    const numeric = toNumber(value);
    if (numeric) return numeric;
  }
  return 0;
};

const downloadCsv = (filename, rows) => {
  const escapeCell = (cell) => {
    const raw = cell === null || cell === undefined ? '' : String(cell);
    const escaped = raw.replaceAll('"', '""');
    return `"${escaped}"`;
  };
  const csv = rows.map((row) => row.map(escapeCell).join(';')).join('\n');
  const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
  const url = URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = filename;
  document.body.appendChild(link);
  link.click();
  link.remove();
  URL.revokeObjectURL(url);
};

const getInitialFilters = () => {
  const end = new Date();
  const start = new Date();
  start.setDate(end.getDate() - 6);
  return {
    start: toDateInputValue(start),
    end: toDateInputValue(end),
    channel: 'all',
    onlyFinished: true,
  };
};

const getMonthInputValue = (date = new Date()) => toDateInputValue(date).slice(0, 7);

const getMonthRange = (monthValue) => {
  const raw = String(monthValue || getMonthInputValue()).trim();
  const [year, month] = raw.split('-').map(Number);
  const safeDate = year && month
    ? new Date(year, month - 1, 1)
    : new Date();
  const start = new Date(safeDate.getFullYear(), safeDate.getMonth(), 1);
  const end = new Date(safeDate.getFullYear(), safeDate.getMonth() + 1, 0);
  return {
    month: toDateInputValue(start).slice(0, 7),
    start: toDateInputValue(start),
    end: toDateInputValue(end),
  };
};

const normalizeChannelLabel = (channel, fallback) => {
  const option = CHANNELS.find((item) => item.key === channel);
  return fallback || option?.label || channel || '-';
};

const getDreGroupLabel = (group) => DRE_GROUP_OPTIONS.find((item) => item.key === group)?.label || 'Despesa operacional';

const getGoalDefinition = (metric) => GOAL_DEFINITIONS.find((item) => item.metric === metric) || GOAL_DEFINITIONS[0];

const formatGoalValue = (value, format) => (format === 'percent' ? formatPercent(value) : formatCurrency(value));

const formatGoalInputValue = (goal) => {
  const definition = getGoalDefinition(goal.metric);
  const value = toNumber(goal.targetValue);
  return definition.format === 'percent' ? String(Math.round(value * 1000) / 10 || '') : String(value || '');
};

const normalizeStatusLabel = (status) => {
  const raw = String(status || '').toLowerCase();
  if (!raw) return '-';
  if (['finished', 'concluded', 'completed', 'delivered'].includes(raw)) return 'Concluído';
  if (raw.includes('cancel')) return 'Cancelado';
  if (['pending', 'created', 'placed'].includes(raw)) return 'Aberto';
  if (['confirmed', 'preparing', 'ready', 'dispatching'].includes(raw)) return 'Em operação';
  return status;
};

const isCanceledStatus = (status) => {
  const raw = String(status || '').toLowerCase();
  return raw.includes('cancel') || raw.includes('refund') || raw.includes('chargeback');
};

const normalizePaymentMethod = (movement) => {
  const raw =
    movement?.paymentMethod ||
    movement?.payment_method ||
    movement?.payment?.method ||
    movement?.payment?.type ||
    movement?.payments?.method ||
    movement?.payments?.methods?.[0]?.method ||
    movement?.payments?.methods?.[0]?.type ||
    '';

  const method = String(raw || '').toLowerCase();
  if (method.includes('pix')) return 'Pix';
  if (method.includes('credit') || method.includes('crédito') || method.includes('credito')) return 'Crédito';
  if (method.includes('debit') || method.includes('débito') || method.includes('debito')) return 'Débito';
  if (method.includes('cash') || method.includes('dinheiro')) return 'Dinheiro';
  if (method.includes('voucher') || method.includes('meal') || method.includes('refeição') || method.includes('refeicao')) {
    return 'Vale-refeição';
  }
  if (method.includes('online')) return 'Online';
  if (raw) return String(raw);
  if (movement?.prepaid === true) return 'Online';
  if (movement?.prepaid === false) return 'Na entrega';
  return 'Não informado';
};

const normalizeFulfillment = (movement) => {
  const raw =
    movement?.fulfillment ||
    movement?.fulfillmentType ||
    movement?.deliveryType ||
    movement?.orderType ||
    movement?.serviceType ||
    movement?.salesChannel ||
    '';
  const normalized = String(raw || '').toLowerCase();

  if (normalized.includes('dine') || normalized.includes('salao') || normalized.includes('salão') || normalized.includes('mesa')) return 'Salão';
  if (normalized.includes('pickup') || normalized.includes('takeout') || normalized.includes('retirada')) return 'Retirada';
  if (normalized.includes('delivery') || normalized.includes('entrega')) return 'Delivery';
  if (movement?.channel === 'salao') return 'Salão';
  if (movement?.channel === 'balcao') return 'Balcão/retirada';
  return 'Não informado';
};

const getMovementAmounts = (movement) => {
  const totalNode = movement?.total || {};
  const gross = firstNumber(
    movement?.grossAmount,
    movement?.gross_amount,
    movement?.gross,
    movement?.totalGross,
    totalNode?.grossAmount,
    totalNode?.orderAmount,
    totalNode?.subTotal,
    movement?.netEstimatedAmount,
    movement?.netAmount,
    movement?.totalAmount,
    movement?.amount
  );
  const discount = Math.abs(
    firstNumber(
      movement?.discountAmount,
      movement?.discount_amount,
      movement?.discounts,
      movement?.discount,
      movement?.benefitsAmount,
      totalNode?.discountAmount,
      totalNode?.discounts,
      totalNode?.benefitsAmount,
      totalNode?.benefits
    )
  );
  const delivery = firstNumber(
    movement?.deliveryFee,
    movement?.delivery_fee,
    movement?.deliveryAmount,
    totalNode?.deliveryFee,
    totalNode?.deliveryAmount,
    totalNode?.delivery?.fee
  );
  const fees = Math.abs(
    firstNumber(
      movement?.fees,
      movement?.feeAmount,
      movement?.channelFee,
      movement?.paymentFee,
      movement?.marketplaceFee
    )
  );
  const refund = Math.abs(firstNumber(movement?.refundAmount, movement?.chargebackAmount, movement?.refundedAmount));
  const total =
    firstNumber(
      movement?.netEstimatedAmount,
      movement?.net_estimated_amount,
      movement?.netAmount,
      movement?.totalAmount,
      movement?.amount,
      movement?.value,
      totalNode?.netAmount,
      totalNode?.orderAmount
    ) || Math.max(gross - discount - refund, 0);

  return { gross, discount, delivery, fees, refund, total };
};

const getQualityFlags = (movement, amounts, paymentLabel) => {
  const flags = [];
  const sourceFlags = movement?.qualityFlags || movement?.quality_flags || movement?.dataQualityFlags || [];
  const normalizedSourceFlags = Array.isArray(sourceFlags) ? sourceFlags : [sourceFlags];
  normalizedSourceFlags.filter(Boolean).forEach((flag) => flags.push(({
    'total-nao-informado-pela-99food': 'Total não informado pela 99Food',
    'pagamento-nao-detalhado-pela-99food': 'Pagamento não detalhado pela 99Food',
  })[String(flag)] || String(flag)));

  if (!movement?.movementAt && !movement?.createdAt) flags.push('Data ausente');
  if (movement?.amountKnown === false) flags.push('Total não informado pela 99Food');
  else if (amounts.total <= 0 && amounts.gross <= 0) flags.push('Total zerado');
  if (paymentLabel === 'Não informado') flags.push('Pagamento ausente');
  if (isCanceledStatus(movement?.status) && amounts.total > 0) flags.push('Cancelado com valor');
  if (!movement?.grossAmount && !movement?.netEstimatedAmount && movement?.netAmount) flags.push('Total estimado');

  return [...new Set(flags)];
};

const getColorValue = (theme, color, main = 'main') => theme.palette[color]?.[main] || theme.palette.primary[main];

const MetricCard = ({ icon, label, value, helper, color = 'primary', loading }) => (
  <Paper
    elevation={0}
    sx={(theme) => ({
      height: '100%',
      width: '100%',
      minWidth: 0,
      maxWidth: '100%',
      boxSizing: 'border-box',
      minHeight: 126,
      p: 2,
      border: '1px solid',
      borderColor: 'divider',
      borderRadius: 2,
      backgroundColor: alpha(getColorValue(theme, color), 0.045),
      position: 'relative',
      overflow: 'hidden',
      '&:before': {
        content: '""',
        position: 'absolute',
        left: 0,
        top: 0,
        bottom: 0,
        width: 4,
        backgroundColor: getColorValue(theme, color),
      },
    })}
  >
    <Stack spacing={1.25} sx={{ height: '100%' }}>
      <Stack direction="row" spacing={1.25} alignItems="center">
        <Box
          sx={(theme) => ({
            width: 38,
            height: 38,
            borderRadius: 2,
            display: 'grid',
            placeItems: 'center',
            backgroundColor: alpha(getColorValue(theme, color), 0.12),
            color: getColorValue(theme, color),
            flex: '0 0 auto',
          })}
        >
          {icon}
        </Box>
        <Typography
          variant="body2"
          color="text.secondary"
          sx={{ minWidth: 0, fontWeight: 700, lineHeight: 1.25, overflowWrap: 'anywhere' }}
        >
          {label}
        </Typography>
      </Stack>
      {loading ? (
        <Skeleton width="72%" height={34} />
      ) : (
        <Typography
          variant="h5"
          sx={{ maxWidth: '100%', fontSize: { xs: '1.45rem', sm: '1.5rem' }, fontWeight: 900, lineHeight: 1.1, ...moneyColumnsSx }}
        >
          {value}
        </Typography>
      )}
      <Typography variant="caption" color="text.secondary" sx={{ minHeight: 18, overflowWrap: 'anywhere' }}>
        {helper}
      </Typography>
    </Stack>
  </Paper>
);

const BarList = ({ rows, color = 'primary', valueFormatter = formatCurrency, emptyLabel = 'Sem dados no período' }) => {
  const total = rows.reduce((acc, row) => acc + Math.max(toNumber(row.total), 0), 0) || 1;
  const visibleRows = rows.filter((row) => toNumber(row.total) > 0 || toNumber(row.count) > 0);

  if (!visibleRows.length) {
    return (
      <Box sx={{ py: 3, textAlign: 'center' }}>
        <Typography variant="body2" color="text.secondary">
          {emptyLabel}
        </Typography>
      </Box>
    );
  }

  return (
    <Stack spacing={1.25}>
      {visibleRows.map((row) => {
        const ratio = Math.max(toNumber(row.total), 0) / total;
        return (
          <Box key={row.label}>
            <Stack direction="row" spacing={1} justifyContent="space-between" alignItems="center" sx={{ minWidth: 0, mb: 0.5 }}>
              <Stack direction="row" spacing={1} alignItems="center" sx={{ minWidth: 0 }}>
                <Typography variant="body2" sx={{ fontWeight: 800 }} noWrap>
                  {row.label}
                </Typography>
                {row.count !== undefined ? (
                  <Chip size="small" label={row.count} variant="outlined" sx={{ height: 22, borderRadius: 1.5 }} />
                ) : null}
              </Stack>
              <Typography variant="body2" color="text.secondary" sx={{ flex: '0 0 auto', fontWeight: 700, ...moneyColumnsSx }}>
                {valueFormatter(row.total)}
              </Typography>
            </Stack>
            <Box
              sx={(theme) => ({
                height: 9,
                borderRadius: 999,
                backgroundColor: alpha(theme.palette.text.primary, 0.08),
                overflow: 'hidden',
              })}
            >
              <Box
                sx={(theme) => ({
                  height: '100%',
                  width: `${Math.max(4, Math.round(ratio * 100))}%`,
                  borderRadius: 999,
                  backgroundColor: getColorValue(theme, color),
                })}
              />
            </Box>
          </Box>
        );
      })}
    </Stack>
  );
};

const groupRows = (rows, getLabel) => {
  const grouped = rows.reduce((acc, row) => {
    const label = getLabel(row) || 'Não informado';
    if (!acc[label]) acc[label] = { label, total: 0, count: 0 };
    acc[label].total += row.amounts.total;
    acc[label].count += 1;
    return acc;
  }, {});

  return Object.values(grouped).sort((a, b) => b.total - a.total);
};

const parseFinanceError = async (response, fallback) => {
  let serverMessage = '';
  try {
    const contentType = response.headers.get('content-type') || '';
    if (contentType.includes('application/json')) {
      const payload = await response.json();
      serverMessage = payload?.error || payload?.message || JSON.stringify(payload);
    } else {
      serverMessage = await response.text();
    }
  } catch (parseErr) {
    serverMessage = '';
  }

  const restartHint =
    response.status === 404
      ? ' (endpoint não encontrado — confirme que o backend foi reiniciado)'
      : '';
  const suffix = serverMessage ? `: ${serverMessage}` : '';
  return new Error(`${fallback} (${response.status})${restartHint}${suffix}`);
};

const getPayloadRows = (payload, keys) => {
  if (Array.isArray(payload)) return payload;
  for (const key of keys) {
    if (Array.isArray(payload?.[key])) return payload[key];
  }
  if (Array.isArray(payload?.data)) return payload.data;
  if (Array.isArray(payload?.items)) return payload.items;
  if (Array.isArray(payload?.rows)) return payload.rows;
  return [];
};

const getLedgerRows = (payload) => getPayloadRows(payload, ['ledger', 'entries', 'movements', 'records']);

const getCashClosingRows = (payload) => {
  const rows = getPayloadRows(payload, ['cashClosings', 'cash_closings', 'closings', 'closures', 'sessions']);
  if (rows.length) return rows;
  return payload?.businessDate || payload?.closing || payload?.expected ? [payload] : [];
};

const getLedgerAmount = (entry, fieldNames) => firstNumber(...fieldNames.map((field) => entry?.[field]));

const normalizeLedgerEntry = (entry) => {
  const amount = firstNumber(
    entry?.recognizedAmount,
    entry?.recognized_amount,
    entry?.netEstimatedAmount,
    entry?.net_estimated_amount,
    entry?.amount,
    entry?.value,
    entry?.total,
    entry?.totalAmount
  );
  const financialStatus = String(entry?.financialStatus || entry?.financial_status || '').toLowerCase();
  const credit = getLedgerAmount(entry, ['credit', 'creditAmount', 'credit_amount', 'inflow', 'income'])
    || (financialStatus === 'recognized' || amount > 0 ? Math.max(amount, 0) : 0);
  const debit = Math.abs(getLedgerAmount(entry, ['debit', 'debitAmount', 'debit_amount', 'outflow', 'expense']))
    || (financialStatus === 'cancelled'
      ? Math.abs(firstNumber(entry?.refundAmount, entry?.refund_amount, entry?.grossAmount, entry?.gross_amount, amount))
      : amount < 0
        ? Math.abs(amount)
        : 0);
  const balance = firstNumber(entry?.balance, entry?.runningBalance, entry?.running_balance, entry?.saldo);
  const sourceChannel = entry?.sourceChannel || entry?.source_channel || entry?.channel;
  const displayId = entry?.displayId || entry?.display_id || entry?.sourceOrderId || entry?.source_order_id || entry?.orderId;
  const status = entry?.orderStatus || entry?.order_status || entry?.status || entry?.financialStatus;

  return {
    ...entry,
    entryAt: entry?.entryAt || entry?.movementAt || entry?.date || entry?.createdAt || entry?.updatedAt,
    description:
      entry?.description ||
      entry?.memo ||
      entry?.reference ||
      displayId ||
      entry?.orderDisplayId ||
      entry?.orderId ||
      '-',
    type: entry?.type || entry?.kind || entry?.category || (financialStatus === 'cancelled' ? 'Cancelamento' : financialStatus === 'open' ? 'Em aberto' : 'Venda'),
    channelLabel: normalizeChannelLabel(sourceChannel, entry?.salesChannel || entry?.sales_channel),
    paymentLabel: normalizePaymentMethod({
      ...entry,
      paymentMethod: entry?.paymentMethod || entry?.payment_method,
      payments: { methods: entry?.paymentMethods || entry?.payment_methods || [] },
    }),
    statusLabel: normalizeStatusLabel(status),
    credit,
    debit,
    balance,
  };
};

const normalizeCashClosing = (record) => {
  const closing = record?.closing || record || {};
  const expectedNode = record?.expected || {};
  const sales = firstNumber(
    expectedNode?.recognizedSales,
    expectedNode?.recognized_sales,
    closing?.salesAmount,
    closing?.sales_amount,
    closing?.totalSales,
    closing?.total_sales,
    closing?.grossAmount,
    toNumber(closing?.cashSales) +
      toNumber(closing?.pixSales) +
      toNumber(closing?.creditSales) +
      toNumber(closing?.debitSales) +
      toNumber(closing?.onlineSales) +
      toNumber(closing?.otherSales)
  );
  const expected = firstNumber(
    expectedNode?.expectedTotal,
    expectedNode?.expected_total,
    closing?.expectedAmount,
    closing?.expected_amount,
    closing?.expectedCash,
    closing?.expected_cash,
    closing?.systemAmount,
    closing?.expectedTotal
  );
  const counted = firstNumber(
    closing?.reportedTotal,
    closing?.reported_total,
    closing?.countedAmount,
    closing?.counted_amount,
    closing?.actualAmount,
    closing?.actual_amount,
    closing?.cashAmount,
    closing?.cash_amount
  );
  const difference = firstNumber(closing?.difference, closing?.differenceAmount, closing?.difference_amount)
    || counted - expected;

  return {
    ...closing,
    businessDate: record?.businessDate || closing?.businessDate || closing?.business_date,
    closingAt: closing?.closingAt || closing?.closedAt || closing?.date || closing?.createdAt || record?.businessDate,
    operator: closing?.closedBy || closing?.openedBy || closing?.operatorName || closing?.operator || closing?.cashierName || closing?.cashier || '-',
    store: closing?.storeName || closing?.merchantName || closing?.store || closing?.merchant || '-',
    statusLabel: normalizeStatusLabel(closing?.status),
    opening: firstNumber(
      closing?.openingBalance,
      closing?.opening_balance,
      expectedNode?.openingBalance,
      expectedNode?.opening_balance,
      closing?.openingAmount,
      closing?.opening_amount,
      closing?.initialAmount,
      closing?.initial_amount
    ),
    sales,
    expected,
    counted,
    difference,
  };
};

const getAccountRows = (payload) => getPayloadRows(payload, ['accounts', 'items', 'rows']);

const getCategoryRows = (payload) => getPayloadRows(payload, ['categories', 'items', 'rows']);

const getPartyRows = (payload) => getPayloadRows(payload, ['parties', 'items', 'rows']);

const getCostCenterRows = (payload) => getPayloadRows(payload, ['costCenters', 'cost_centers', 'items', 'rows']);

const getRecurringRows = (payload) => getPayloadRows(payload, ['recurringRules', 'recurring_rules', 'rules', 'items']);

const getDreLineRows = (payload) => getPayloadRows(payload, ['lines', 'items', 'rows']);

const getReconciliationRows = (payload) => getPayloadRows(payload, ['items', 'rows']);

const getReconciliationStatusLabel = (status) => {
  const raw = String(status || '').toLowerCase();
  if (raw === 'reconciled') return 'Conciliado';
  if (raw === 'divergent') return 'Divergente';
  if (raw === 'pending') return 'Pendente';
  return 'Aberto';
};

const getReconciliationStatusColor = (status) => {
  const raw = String(status || '').toLowerCase();
  if (raw === 'reconciled') return 'success';
  if (raw === 'divergent') return 'warning';
  if (raw === 'pending') return 'default';
  return 'primary';
};

const formatDateOnly = (value) => {
  if (!value) return '-';
  const raw = String(value);
  if (/^\d{4}-\d{2}-\d{2}$/.test(raw)) {
    const [year, month, day] = raw.split('-');
    return `${day}/${month}/${year}`;
  }
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return '-';
  return date.toLocaleDateString('pt-BR');
};

const toDateOnly = (value) => {
  if (!value) return '';
  const raw = String(value);
  if (/^\d{4}-\d{2}-\d{2}$/.test(raw)) return raw;
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return '';
  return date.toISOString().slice(0, 10);
};

const compactIdentifier = (value) => {
  const text = String(value || '');
  if (!text) return '-';
  if (text.length <= 14) return text;
  return `${text.slice(0, 8)}...${text.slice(-4)}`;
};

const getIfoodFinancialRecordTypeLabel = (recordType) => (
  IFOOD_FINANCIAL_TYPE_LABELS[recordType] || recordType || '-'
);

const getIfoodFinancialRecordTypeColor = (recordType) => {
  if (recordType === 'sale') return 'success';
  if (recordType === 'financial_event') return 'primary';
  if (recordType === 'settlement') return 'info';
  if (recordType === 'anticipation') return 'warning';
  if (recordType === 'reconciliation') return 'secondary';
  return 'default';
};

const getIfoodFinancialOrderKey = (record = {}) => (
  record.financialDetails?.order?.shortId ||
  record.financialDetails?.order?.displayId ||
  record.financialDetails?.order?.id ||
  record.financialDetails?.order?.identifiers?.[0] ||
  ''
);

const getIfoodFinancialPrimaryRecord = (records = [], selectedRecord = null) => (
  records.find((record) => record.recordType === 'sale') ||
  selectedRecord ||
  records[0] ||
  null
);

const getIfoodFinancialRecordAmount = (record = {}) => {
  const details = record.financialDetails || {};
  if (record.recordType === 'sale') return toNumber(details.sale?.grossTotal);
  if (record.recordType === 'financial_event') return toNumber(details.financialEvent?.amount);
  if (['settlement', 'anticipation', 'reconciliation'].includes(record.recordType)) {
    return toNumber(details.settlement?.amount);
  }
  return 0;
};

const getIfoodFinancialImpactValue = (record = {}) => {
  const details = record.financialDetails || {};
  if (record.recordType === 'sale') return firstKnownNumber(details.billing?.saleBalance);
  if (record.recordType === 'financial_event') {
    return details.financialEvent?.hasTransferImpact === true
      ? toNumber(details.financialEvent?.amount)
      : 0;
  }
  if (['settlement', 'anticipation', 'reconciliation'].includes(record.recordType)) {
    return toNumber(details.settlement?.transferAmount);
  }
  return 0;
};

const getIfoodFinancialHasTransferImpact = (record = {}) => Math.abs(getIfoodFinancialImpactValue(record)) > 0.0001;

const getIfoodFinancialDescription = (record = {}) => {
  const details = record.financialDetails || {};
  return details.financialEvent?.name ||
    details.sale?.type ||
    details.sale?.category ||
    details.settlement?.type ||
    '-';
};

const getIfoodFinancialStatus = (record = {}) => {
  const details = record.financialDetails || {};
  return details.sale?.status ||
    details.financialEvent?.trigger ||
    details.settlement?.status ||
    'Sem status';
};

const isPendingFinancialAccount = (account) => ['open', 'partial'].includes(String(account?.status || '').toLowerCase());

const isOverdueFinancialAccount = (account) =>
  isPendingFinancialAccount(account) && toDateOnly(account?.dueDate) && toDateOnly(account?.dueDate) < toDateInputValue(new Date());

const normalizeAccountTypeLabel = (type) => (type === 'receivable' ? 'Receber' : 'Pagar');

const normalizeAccountStatusLabel = (account) => {
  if (isOverdueFinancialAccount(account)) return 'Vencida';
  const status = String(account?.status || '').toLowerCase();
  if (status === 'paid') return 'Paga';
  if (status === 'partial') return 'Parcial';
  if (status === 'cancelled') return 'Cancelada';
  return 'Aberta';
};

const getAccountStatusColor = (account) => {
  if (isOverdueFinancialAccount(account)) return 'error';
  if (account?.status === 'paid') return 'success';
  if (account?.status === 'partial') return 'warning';
  if (account?.status === 'cancelled') return 'default';
  return 'primary';
};

const getExpenseCategory = (categoryId, categories = []) =>
  categories.find((category) => String(category.id) === String(categoryId));

const getDefaultAccountForm = () => ({
  type: 'payable',
  description: '',
  amount: '',
  dueDate: toDateInputValue(new Date()),
  competenceDate: toDateInputValue(new Date()),
  status: 'open',
  paidAmount: '',
  categoryId: '',
  partyId: '',
  costCenterId: '',
  paymentMethod: '',
  reference: '',
  notes: '',
});

const getDefaultExpenseForm = (date = new Date()) => {
  const dateValue = toDateInputValue(date);
  return {
    id: null,
    description: '',
    amount: '',
    dueDate: dateValue,
    competenceDate: dateValue,
    status: 'open',
    paidAmount: '',
    paymentMethod: '',
    categoryId: '',
    partyId: '',
    costCenterId: '',
    reference: '',
    notes: '',
  };
};

const getDefaultRecurringForm = () => ({
  type: 'payable',
  description: '',
  amount: '',
  frequency: 'monthly',
  nextDueDate: toDateInputValue(new Date()),
  categoryId: '',
  partyId: '',
  costCenterId: '',
  paymentMethod: '',
  notes: '',
});

const SectionCard = ({ title, action, children, sx }) => (
  <Paper elevation={0} sx={{ p: 2, borderRadius: 2, border: '1px solid', borderColor: 'divider', height: '100%', minWidth: 0, overflow: 'hidden', ...sx }}>
    <Stack
      direction={{ xs: 'column', sm: 'row' }}
      justifyContent="space-between"
      alignItems={{ xs: 'stretch', sm: 'center' }}
      spacing={1.5}
      sx={{ minWidth: 0, mb: 1.5 }}
    >
      <Typography variant="subtitle1" sx={{ minWidth: 0, fontWeight: 900, overflowWrap: 'anywhere' }}>
        {title}
      </Typography>
      {action ? (
        <Box
          sx={{
            minWidth: 0,
            display: 'flex',
            justifyContent: { xs: 'flex-start', sm: 'flex-end' },
            '& > .MuiStack-root': {
              flexWrap: 'wrap',
              rowGap: 1,
            },
          }}
        >
          {action}
        </Box>
      ) : null}
    </Stack>
    {children}
  </Paper>
);

const EmptyState = ({ icon, title, helper }) => (
  <Box
    sx={(theme) => ({
      py: 5,
      px: 2,
      textAlign: 'center',
      borderRadius: 2,
      border: '1px dashed',
      borderColor: 'divider',
      backgroundColor: alpha(theme.palette.text.primary, 0.025),
    })}
  >
    <Box sx={{ mb: 1, color: 'text.secondary' }}>{icon}</Box>
    <Typography variant="subtitle1" sx={{ fontWeight: 900 }}>
      {title}
    </Typography>
    <Typography variant="body2" color="text.secondary">
      {helper}
    </Typography>
  </Box>
);

const CompactStat = ({ label, value, color = 'primary' }) => (
  <Paper
    elevation={0}
    sx={(theme) => ({
      minWidth: 0,
      maxWidth: '100%',
      p: 1.5,
      borderRadius: 2,
      border: '1px solid',
      borderColor: 'divider',
      backgroundColor: alpha(getColorValue(theme, color), 0.045),
    })}
  >
    <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 800, overflowWrap: 'anywhere' }}>
      {label}
    </Typography>
    <Typography variant="subtitle1" sx={{ maxWidth: '100%', fontWeight: 900, ...moneyColumnsSx }}>
      {value}
    </Typography>
  </Paper>
);

const IfoodFinancialOrderDetailPanel = ({ state }) => {
  const records = state.data?.records || (state.selectedRecord ? [state.selectedRecord] : []);
  const primaryRecord = getIfoodFinancialPrimaryRecord(records, state.selectedRecord);
  const details = primaryRecord?.financialDetails || {};
  const billing = details.billing || {};
  const sale = details.sale || {};
  const payments = details.payments || {};
  const delivery = details.delivery || {};
  const payload = details.payload || {};
  const order = details.order || {};
  const billingEntries = billing.entries || [];
  const paymentMethods = payments.methods || [];
  const officialNetValue = getIfoodFinancialImpactValue(primaryRecord);

  if (state.loading) {
    return (
      <Stack spacing={1.25}>
        <Skeleton height={34} />
        <Skeleton height={86} />
        <Skeleton height={140} />
      </Stack>
    );
  }

  if (state.error) {
    return (
      <Alert severity="warning">
        {state.error}
      </Alert>
    );
  }

  if (!primaryRecord && state.data) {
    return (
      <EmptyState
        icon={<SearchOutlinedIcon fontSize="large" />}
        title={`Pedido ${state.data.orderKey || ''} sem Financial`}
        helper="A API oficial não retornou venda, evento, repasse ou antecipação para a janela selecionada."
      />
    );
  }

  if (!primaryRecord) {
    return (
      <EmptyState
        icon={<InfoOutlinedIcon fontSize="large" />}
        title="Selecione um lançamento"
        helper="Venda, eventos, repasses e composição oficial aparecem aqui."
      />
    );
  }

  return (
    <Stack spacing={1.5}>
      <Stack direction="row" justifyContent="space-between" alignItems="flex-start" spacing={1}>
        <Box>
          <Typography variant="subtitle2" color="text.secondary" sx={{ fontWeight: 800 }}>
            Detalhe financeiro oficial
          </Typography>
          <Typography variant="h6" sx={{ fontWeight: 950, lineHeight: 1.15 }}>
            {order.shortId || order.displayId || compactIdentifier(order.id || order.identifiers?.[0] || primaryRecord.externalId)}
          </Typography>
        </Box>
        <Chip
          size="small"
          color={state.data?.live ? 'info' : 'default'}
          variant={state.data?.live ? 'filled' : 'outlined'}
          label={state.data?.live ? 'API ao vivo' : 'Persistido'}
        />
      </Stack>

      <Grid container spacing={1}>
        <Grid item xs={6}>
          <CompactStat label="Bruto da venda" value={formatCurrency(sale.grossTotal)} color="primary" />
        </Grid>
        <Grid item xs={6}>
          <CompactStat label="Pago pelo cliente" value={formatCurrency(payments.total)} color="info" />
        </Grid>
        <Grid item xs={6}>
          <CompactStat label="Débitos e taxas" value={formatCurrency(Math.abs(toNumber(billing.debitsTotal)))} color="warning" />
        </Grid>
        <Grid item xs={6}>
          <CompactStat label="Líquido da loja" value={formatCurrency(officialNetValue)} color="success" />
        </Grid>
      </Grid>

      <Alert severity="success" icon={<AccountBalanceOutlinedIcon />}>
        Valor oficial informado pela Financial API para este lançamento: <strong>{formatCurrency(officialNetValue)}</strong>.
        Se o campo não veio no payload do iFood, o sistema não estima por pedidos locais.
      </Alert>

      <Box>
        <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 900 }}>
          Como o valor da venda foi formado
        </Typography>
        <Stack spacing={0.75} sx={{ mt: 0.75 }}>
          {[
            ['Itens', sale.grossItems],
            ['Entrega', sale.grossDelivery],
            ['Taxa de serviço', sale.grossServiceFee],
            ['Benefícios totais', sale.benefitsTotal],
            ['Subsídio iFood', sale.ifoodBenefit],
            ['Subsídio loja', sale.merchantBenefit],
          ].map(([label, value]) => (
            <Stack key={label} direction="row" justifyContent="space-between" spacing={1}>
              <Typography variant="body2" color="text.secondary">{label}</Typography>
              <Typography variant="body2" sx={{ fontWeight: 850, ...moneyColumnsSx }}>{formatCurrency(value)}</Typography>
            </Stack>
          ))}
        </Stack>
      </Box>

      <Divider />

      <Box>
        <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 900 }}>
          Débitos e créditos oficiais
        </Typography>
        <Stack spacing={0.75} sx={{ mt: 0.75 }}>
          {billingEntries.length ? billingEntries.map((entry) => (
            <Stack key={`${entry.name}-${entry.value}`} direction="row" justifyContent="space-between" spacing={1}>
              <Typography variant="body2" sx={{ fontWeight: 750 }}>{entry.name}</Typography>
              <Typography
                variant="body2"
                sx={{
                  fontWeight: 900,
                  color: entry.value < 0 ? 'warning.main' : 'success.main',
                  ...moneyColumnsSx,
                }}
              >
                {formatCurrency(entry.value)}
              </Typography>
            </Stack>
          )) : (
            <Typography variant="body2" color="text.secondary">O iFood não detalhou débitos e créditos para este lançamento.</Typography>
          )}
        </Stack>
      </Box>

      <Divider />

      <Box>
        <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 900 }}>
          Pagamento e entrega
        </Typography>
        <Stack direction="row" spacing={1} sx={{ flexWrap: 'wrap', rowGap: 1, mt: 0.75 }}>
          {paymentMethods.map((payment, index) => (
            <Chip
              key={`${payment.method}-${index}`}
              size="small"
              variant="outlined"
              label={`${payment.method} ${formatCurrency(payment.value)}${payment.liability ? ` · ${payment.liability}` : ''}`}
            />
          ))}
          {delivery.logisticProvider ? (
            <Chip size="small" variant="outlined" label={`Entrega ${delivery.logisticProvider}`} />
          ) : null}
          {sale.status ? (
            <Chip size="small" variant="outlined" label={sale.status} />
          ) : null}
        </Stack>
      </Box>

      <Divider />

      <Box>
        <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 900 }}>
          Ajustes e eventos oficiais relacionados
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
          Eventos podem corrigir, descontar, acrescentar ou transferir valores fora da venda original.
        </Typography>
        <Stack spacing={0.8} sx={{ mt: 0.75 }}>
          {records.map((record) => {
            const color = getIfoodFinancialRecordTypeColor(record.recordType);
            return (
              <Box
                key={record.id || record.externalId}
                sx={{ borderLeft: '3px solid', borderColor: color === 'default' ? 'divider' : `${color}.main`, pl: 1 }}
              >
                <Stack direction="row" justifyContent="space-between" spacing={1}>
                  <Typography variant="body2" sx={{ fontWeight: 850 }}>
                    {getIfoodFinancialRecordTypeLabel(record.recordType)}
                  </Typography>
                  <Typography variant="body2" sx={{ fontWeight: 900, ...moneyColumnsSx }}>
                    {formatCurrency(getIfoodFinancialImpactValue(record))}
                  </Typography>
                </Stack>
                <Typography variant="caption" color="text.secondary">
                  {record.eventName || record.status || compactIdentifier(record.externalId)}
                </Typography>
              </Box>
            );
          })}
        </Stack>
      </Box>

      <Stack direction="row" spacing={1} sx={{ flexWrap: 'wrap', rowGap: 1 }}>
        {(payload.topLevelKeys || []).slice(0, 10).map((key) => (
          <Chip key={key} size="small" variant="outlined" label={key} />
        ))}
        {payload.topLevelKeys?.length > 10 ? (
          <Chip size="small" variant="outlined" label={`+${payload.topLevelKeys.length - 10} campos`} />
        ) : null}
      </Stack>
    </Stack>
  );
};

const Financeiro = () => {
  const auth = useAuth();
  const today = useMemo(() => new Date(), []);
  const [draftFilters, setDraftFilters] = useState(getInitialFilters);
  const [filters, setFilters] = useState(getInitialFilters);
  const [activeTab, setActiveTab] = useState('resumo');

  const [state, setState] = useState({
    loading: true,
    error: null,
    data: null,
    lastUpdatedAt: null,
  });
  const [ledgerState, setLedgerState] = useState({
    loading: false,
    syncing: false,
    error: null,
    data: null,
    lastUpdatedAt: null,
  });
  const [dreState, setDreState] = useState({
    loading: false,
    error: null,
    data: null,
    lastUpdatedAt: null,
  });
  const [managementState, setManagementState] = useState({
    loading: false,
    saving: false,
    error: null,
    data: null,
    lastUpdatedAt: null,
  });
  const [managementForm, setManagementForm] = useState({ goals: {} });
  const [cashClosingState, setCashClosingState] = useState({
    loading: false,
    saving: false,
    error: null,
    data: null,
    lastUpdatedAt: null,
  });
  const [reconciliationState, setReconciliationState] = useState({
    loading: false,
    saving: false,
    error: null,
    data: null,
    lastUpdatedAt: null,
  });
  const [reconciliationForm, setReconciliationForm] = useState({
    items: {},
    notes: '',
  });
  const [ifoodFinancialState, setIfoodFinancialState] = useState({
    contextLoading: false,
    loading: false,
    guideLoading: false,
    syncing: false,
    error: null,
    context: null,
    summary: null,
    guide: null,
    records: [],
    syncResult: null,
    livePreview: false,
    liveErrors: [],
    lastUpdatedAt: null,
  });
  const ifoodFinancialRequestRef = useRef(0);
  const [ifoodFinancialMerchantId, setIfoodFinancialMerchantId] = useState('');
  const [ifoodFinancialRecordType, setIfoodFinancialRecordType] = useState('all');
  const [ifoodFinancialOrderSearch, setIfoodFinancialOrderSearch] = useState('');
  const [ifoodFinancialOrderDetailState, setIfoodFinancialOrderDetailState] = useState({
    loading: false,
    error: null,
    data: null,
    selectedRecord: null,
    selectedExternalId: '',
  });
  const [cashForm, setCashForm] = useState({
    openingBalance: '0',
    supplyAmount: '0',
    withdrawalAmount: '0',
    reportedTotal: '',
    notes: '',
  });
  const [accountFilters, setAccountFilters] = useState({
    quick: 'pending',
    type: 'all',
    status: 'pending',
    categoryId: 'all',
    costCenterId: 'all',
    q: '',
  });
  const [expenseFilters, setExpenseFilters] = useState(() => ({
    status: 'all',
    categoryId: 'all',
    costCenterId: 'all',
    q: '',
    month: getMonthInputValue(),
  }));
  const [accountsState, setAccountsState] = useState({
    loading: false,
    saving: false,
    error: null,
    data: null,
    categories: [],
    parties: [],
    costCenters: [],
    recurringRules: [],
    lastUpdatedAt: null,
  });
  const [expensesState, setExpensesState] = useState({
    loading: false,
    saving: false,
    error: null,
    data: null,
    lastUpdatedAt: null,
  });
  const [accountForm, setAccountForm] = useState(getDefaultAccountForm);
  const [expenseForm, setExpenseForm] = useState(getDefaultExpenseForm);
  const [expenseSubTab, setExpenseSubTab] = useState('lancamentos');
  const [expenseCategoryFilterType, setExpenseCategoryFilterType] = useState('all');
  const [categoryEditingId, setCategoryEditingId] = useState(null);
  const [costCenterEditingId, setCostCenterEditingId] = useState(null);
  const [partyEditingId, setPartyEditingId] = useState(null);
  const [quickExpenseForms, setQuickExpenseForms] = useState({
    category: false,
    supplier: false,
    costCenter: false,
  });
  const [quickCategoryForm, setQuickCategoryForm] = useState(DEFAULT_QUICK_CATEGORY_FORM);
  const [quickSupplierForm, setQuickSupplierForm] = useState(DEFAULT_QUICK_SUPPLIER_FORM);
  const [quickCostCenterForm, setQuickCostCenterForm] = useState(DEFAULT_QUICK_COST_CENTER_FORM);
  const [categoryForm, setCategoryForm] = useState(DEFAULT_CATEGORY_FORM);
  const [costCenterForm, setCostCenterForm] = useState(DEFAULT_COST_CENTER_FORM);
  const [partyForm, setPartyForm] = useState(DEFAULT_PARTY_FORM);
  const [recurringForm, setRecurringForm] = useState(getDefaultRecurringForm);
  const canViewIfoodFinancial = isAdminLike(auth.user);

  const buildFinanceParams = useCallback(() => {
    const params = new URLSearchParams();
    const startBoundary = dateInputToLocalBoundary(filters.start, 0);
    const endBoundary = dateInputToLocalBoundary(filters.end, 1);
    if (startBoundary) params.set('start', startBoundary);
    if (endBoundary) params.set('end', endBoundary);
    if (filters.channel) params.set('channel', filters.channel);
    params.set('status', filters.onlyFinished ? 'finished' : 'all');
    return params;
  }, [filters]);

  const buildAccountParams = useCallback(() => {
    const params = new URLSearchParams();
    const startBoundary = dateInputToLocalBoundary(filters.start, 0);
    const endBoundary = dateInputToLocalBoundary(filters.end, 1);
    if (startBoundary) params.set('start', startBoundary);
    if (endBoundary) params.set('end', endBoundary);
    params.set('type', accountFilters.type);
    params.set('status', accountFilters.status);
    if (accountFilters.categoryId !== 'all') params.set('categoryId', accountFilters.categoryId);
    if (accountFilters.costCenterId !== 'all') params.set('costCenterId', accountFilters.costCenterId);
    if (accountFilters.q.trim()) params.set('q', accountFilters.q.trim());
    return params;
  }, [accountFilters, filters.end, filters.start]);

  const buildExpenseParams = useCallback(() => {
    const params = new URLSearchParams();
    const monthRange = getMonthRange(expenseFilters.month);
    const startBoundary = dateInputToLocalBoundary(monthRange.start, 0);
    const endBoundary = dateInputToLocalBoundary(monthRange.end, 1);
    if (startBoundary) params.set('start', startBoundary);
    if (endBoundary) params.set('end', endBoundary);
    params.set('type', 'payable');
    params.set('dateField', 'competence');
    params.set('status', expenseFilters.status);
    if (expenseFilters.categoryId !== 'all') params.set('categoryId', expenseFilters.categoryId);
    if (expenseFilters.costCenterId !== 'all') params.set('costCenterId', expenseFilters.costCenterId);
    if (expenseFilters.q.trim()) params.set('q', expenseFilters.q.trim());
    return params;
  }, [expenseFilters]);

  const fillCashForm = useCallback((data) => {
    const closing = data?.closing || {};
    const expected = data?.expected || {};
    setCashForm({
      openingBalance: String(closing.openingBalance ?? expected.openingBalance ?? 0),
      supplyAmount: String(closing.supplyAmount ?? expected.supplyAmount ?? 0),
      withdrawalAmount: String(closing.withdrawalAmount ?? expected.withdrawalAmount ?? 0),
      reportedTotal: closing.reportedTotal !== undefined && closing.reportedTotal !== null
        ? String(closing.reportedTotal)
        : String(expected.expectedTotal ?? ''),
      notes: closing.notes || '',
    });
  }, []);

  const fillReconciliationForm = useCallback((data) => {
    const nextItems = {};
    getReconciliationRows(data).forEach((item) => {
      nextItems[item.key] = {
        reportedAmount: item.checked ? String(item.reportedAmount ?? '') : '',
        notes: item.notes || '',
      };
    });
    setReconciliationForm({
      items: nextItems,
      notes: data?.reconciliation?.notes || '',
    });
  }, []);

  const fillManagementForm = useCallback((data) => {
    const goals = {};
    (data?.goals || []).forEach((goal) => {
      goals[goal.metric] = formatGoalInputValue(goal);
    });
    setManagementForm({ goals });
  }, []);

  const loadMovements = useCallback(async () => {
    setState((prev) => ({ ...prev, loading: true, error: null }));
    try {
      const params = buildFinanceParams();

      const response = await apiFetch(`${API_URL}/api/finance/movements?${params.toString()}`);
      if (!response.ok) {
        throw await parseFinanceError(response, 'Falha ao carregar movimentações');
      }
      const data = await response.json();
      setState({
        loading: false,
        error: null,
        data,
        lastUpdatedAt: new Date().toISOString(),
      });
    } catch (err) {
      setState((prev) => ({
        ...prev,
        loading: false,
        error: err?.message || 'Erro ao carregar movimentações',
      }));
    }
  }, [buildFinanceParams]);

  const loadLedger = useCallback(async () => {
    setLedgerState((prev) => ({ ...prev, loading: true, error: null }));
    try {
      const params = buildFinanceParams();
      const response = await apiFetch(`${API_URL}/api/finance/ledger?${params.toString()}`);
      if (!response.ok) {
        throw await parseFinanceError(response, 'Falha ao carregar ledger');
      }
      const data = await response.json();
      setLedgerState((prev) => ({
        ...prev,
        loading: false,
        error: null,
        data,
        lastUpdatedAt: new Date().toISOString(),
      }));
    } catch (err) {
      setLedgerState((prev) => ({
        ...prev,
        loading: false,
        error: err?.message || 'Erro ao carregar ledger',
      }));
    }
  }, [buildFinanceParams]);

  const syncLedger = useCallback(async () => {
    setLedgerState((prev) => ({ ...prev, syncing: true, error: null }));
    try {
      const payload = {
        start: dateInputToLocalBoundary(filters.start, 0),
        end: dateInputToLocalBoundary(filters.end, 1),
        channel: filters.channel,
        status: filters.onlyFinished ? 'finished' : 'all',
      };
      const response = await apiFetch(`${API_URL}/api/finance/ledger/sync`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload),
      });
      if (!response.ok) {
        throw await parseFinanceError(response, 'Falha ao sincronizar ledger');
      }
      await loadLedger();
    } catch (err) {
      setLedgerState((prev) => ({
        ...prev,
        syncing: false,
        error: err?.message || 'Erro ao sincronizar ledger',
      }));
      return;
    }
    setLedgerState((prev) => ({ ...prev, syncing: false }));
  }, [filters, loadLedger]);

  const loadDre = useCallback(async () => {
    setDreState((prev) => ({ ...prev, loading: true, error: null }));
    try {
      const params = buildFinanceParams();
      const response = await apiFetch(`${API_URL}/api/finance/dre?${params.toString()}`);
      if (!response.ok) {
        throw await parseFinanceError(response, 'Falha ao carregar DRE');
      }
      const data = await response.json();
      setDreState({
        loading: false,
        error: null,
        data,
        lastUpdatedAt: new Date().toISOString(),
      });
    } catch (err) {
      setDreState((prev) => ({
        ...prev,
        loading: false,
        error: err?.message || 'Erro ao carregar DRE',
      }));
    }
  }, [buildFinanceParams]);

  const loadManagement = useCallback(async () => {
    setManagementState((prev) => ({ ...prev, loading: true, error: null }));
    try {
      const params = buildFinanceParams();
      const response = await apiFetch(`${API_URL}/api/finance/management?${params.toString()}`);
      if (!response.ok) {
        throw await parseFinanceError(response, 'Falha ao carregar gerencial');
      }
      const data = await response.json();
      fillManagementForm(data);
      setManagementState({
        loading: false,
        saving: false,
        error: null,
        data,
        lastUpdatedAt: new Date().toISOString(),
      });
    } catch (err) {
      setManagementState((prev) => ({
        ...prev,
        loading: false,
        saving: false,
        error: err?.message || 'Erro ao carregar gerencial',
      }));
    }
  }, [buildFinanceParams, fillManagementForm]);

  const saveManagementGoals = useCallback(async () => {
    setManagementState((prev) => ({ ...prev, saving: true, error: null }));
    try {
      const goals = GOAL_DEFINITIONS.map((definition) => {
        const rawValue = managementForm.goals[definition.metric];
        const targetValue = definition.format === 'percent'
          ? toNumber(rawValue) / 100
          : toNumber(rawValue);
        return {
          metric: definition.metric,
          targetValue,
          comparison: definition.comparison,
        };
      });
      const response = await apiFetch(`${API_URL}/api/finance/management/goals`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          start: dateInputToLocalBoundary(filters.start, 0),
          end: dateInputToLocalBoundary(filters.end, 1),
          channel: filters.channel,
          goals,
        }),
      });
      if (!response.ok) {
        throw await parseFinanceError(response, 'Falha ao salvar metas');
      }
      const data = await response.json();
      fillManagementForm(data.management);
      setManagementState({
        loading: false,
        saving: false,
        error: null,
        data: data.management,
        lastUpdatedAt: new Date().toISOString(),
      });
    } catch (err) {
      setManagementState((prev) => ({
        ...prev,
        saving: false,
        error: err?.message || 'Erro ao salvar metas',
      }));
    }
  }, [fillManagementForm, filters.channel, filters.end, filters.start, managementForm.goals]);

  const loadReconciliation = useCallback(async () => {
    setReconciliationState((prev) => ({ ...prev, loading: true, error: null }));
    try {
      const params = buildFinanceParams();
      const response = await apiFetch(`${API_URL}/api/finance/reconciliation?${params.toString()}`);
      if (!response.ok) {
        throw await parseFinanceError(response, 'Falha ao carregar conciliação');
      }
      const data = await response.json();
      fillReconciliationForm(data);
      setReconciliationState({
        loading: false,
        saving: false,
        error: null,
        data,
        lastUpdatedAt: new Date().toISOString(),
      });
    } catch (err) {
      setReconciliationState((prev) => ({
        ...prev,
        loading: false,
        saving: false,
        error: err?.message || 'Erro ao carregar conciliação',
      }));
    }
  }, [buildFinanceParams, fillReconciliationForm]);

  const saveReconciliation = useCallback(async () => {
    setReconciliationState((prev) => ({ ...prev, saving: true, error: null }));
    try {
      const rows = getReconciliationRows(reconciliationState.data);
      const response = await apiFetch(`${API_URL}/api/finance/reconciliation`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          start: dateInputToLocalBoundary(filters.start, 0),
          end: dateInputToLocalBoundary(filters.end, 1),
          channel: filters.channel,
          notes: reconciliationForm.notes,
          items: rows.map((row) => ({
            key: row.key,
            reportedAmount: toNumber(reconciliationForm.items[row.key]?.reportedAmount),
            notes: reconciliationForm.items[row.key]?.notes || '',
          })),
        }),
      });
      if (!response.ok) {
        throw await parseFinanceError(response, 'Falha ao salvar conciliação');
      }
      const data = await response.json();
      fillReconciliationForm(data);
      setReconciliationState({
        loading: false,
        saving: false,
        error: null,
        data,
        lastUpdatedAt: new Date().toISOString(),
      });
    } catch (err) {
      setReconciliationState((prev) => ({
        ...prev,
        saving: false,
        error: err?.message || 'Erro ao salvar conciliação',
      }));
    }
  }, [fillReconciliationForm, filters.channel, filters.end, filters.start, reconciliationForm.items, reconciliationForm.notes, reconciliationState.data]);

  const loadIfoodFinancialContext = useCallback(async () => {
    setIfoodFinancialState((prev) => ({ ...prev, contextLoading: true, error: null }));
    try {
      const response = await apiFetch(`${API_URL}/api/finance/ifood/context`);
      if (!response.ok) {
        throw await parseFinanceError(response, 'Falha ao carregar contexto iFood Financial');
      }
      const context = await response.json();
      const merchants = context?.merchants || [];
      setIfoodFinancialState((prev) => ({
        ...prev,
        contextLoading: false,
        context,
      }));
      if (!ifoodFinancialMerchantId && merchants[0]?.merchantId) {
        setIfoodFinancialMerchantId(merchants[0].merchantId);
      }
    } catch (err) {
      setIfoodFinancialState((prev) => ({
        ...prev,
        contextLoading: false,
        error: err?.message || 'Erro ao carregar contexto iFood Financial',
      }));
    }
  }, [ifoodFinancialMerchantId]);

  const loadIfoodFinancialData = useCallback(async () => {
    if (!ifoodFinancialMerchantId) return;
    const requestId = ifoodFinancialRequestRef.current + 1;
    const merchantId = ifoodFinancialMerchantId;
    ifoodFinancialRequestRef.current = requestId;
    setIfoodFinancialState((prev) => ({
      ...prev,
      loading: true,
      guideLoading: true,
      error: null,
      guide: null,
      liveErrors: [],
    }));
    try {
      const params = new URLSearchParams();
      params.set('startDate', IFOOD_FINANCIAL_HOMOLOGATION_START);
      params.set('endDate', IFOOD_FINANCIAL_HOMOLOGATION_END);
      params.set('competence', IFOOD_FINANCIAL_HOMOLOGATION_COMPETENCE);
      params.set('recordType', ifoodFinancialRecordType);
      params.set('limit', '120');
      params.set('homologation', 'true');
      const summaryParams = new URLSearchParams(params);
      summaryParams.delete('recordType');
      summaryParams.delete('limit');

      const guidePromise = apiFetch(`${API_URL}/api/finance/ifood/${merchantId}/homologation-guide`)
        .then(async (response) => {
          if (!response.ok) {
            const error = await parseFinanceError(response, 'Falha ao montar respostas da homologação iFood');
            return { success: false, error: error.message };
          }
          return response.json();
        })
        .catch((error) => ({
          success: false,
          error: error?.message || 'Falha ao montar respostas da homologação iFood',
        }));

      guidePromise.then((guidePayload) => {
        if (ifoodFinancialRequestRef.current !== requestId) return;
        setIfoodFinancialState((prev) => ({
          ...prev,
          guideLoading: false,
          guide: guidePayload,
        }));
      });

      const [summaryResponse, recordsResponse] = await Promise.all([
        apiFetch(`${API_URL}/api/finance/ifood/${merchantId}/summary?${summaryParams.toString()}`),
        apiFetch(`${API_URL}/api/finance/ifood/${merchantId}/records?${params.toString()}`),
      ]);

      if (ifoodFinancialRequestRef.current !== requestId) return;

      if (!summaryResponse.ok) {
        throw await parseFinanceError(summaryResponse, 'Falha ao carregar resumo iFood Financial');
      }
      if (!recordsResponse.ok) {
        throw await parseFinanceError(recordsResponse, 'Falha ao carregar lançamentos iFood Financial');
      }

      const [summaryPayload, recordsPayload] = await Promise.all([
        summaryResponse.json(),
        recordsResponse.json(),
      ]);
      if (ifoodFinancialRequestRef.current !== requestId) return;
      let nextSummary = summaryPayload;
      let nextRecords = recordsPayload.records || [];
      let livePreview = false;
      let liveErrors = [];

      if (!nextRecords.length && !toNumber(summaryPayload?.summary?.totals?.records)) {
        const liveResponse = await apiFetch(`${API_URL}/api/finance/ifood/${merchantId}/live?${params.toString()}`);
        if (ifoodFinancialRequestRef.current !== requestId) return;
        if (liveResponse.ok) {
          const livePayload = await liveResponse.json();
          if (ifoodFinancialRequestRef.current !== requestId) return;
          if ((livePayload.records || []).length) {
            nextSummary = {
              period: livePayload.period,
              summary: livePayload.summary,
            };
            nextRecords = livePayload.records || [];
            livePreview = true;
          }
          liveErrors = livePayload.errors || [];
        }
      }

      setIfoodFinancialState((prev) => ({
        ...prev,
        loading: false,
        summary: nextSummary,
        records: nextRecords,
        livePreview,
        liveErrors,
        lastUpdatedAt: new Date().toISOString(),
      }));
    } catch (err) {
      if (ifoodFinancialRequestRef.current !== requestId) return;
      setIfoodFinancialState((prev) => ({
        ...prev,
        loading: false,
        error: err?.message || 'Erro ao carregar iFood Financial',
      }));
    }
  }, [ifoodFinancialMerchantId, ifoodFinancialRecordType]);

  const loadIfoodFinancialOrderDetails = useCallback(async (orderKey, selectedRecord = null) => {
    const safeOrderKey = String(orderKey || '').trim();
    if (!ifoodFinancialMerchantId || !safeOrderKey) return;
    setIfoodFinancialOrderDetailState((prev) => ({
      ...prev,
      loading: true,
      error: null,
      selectedRecord,
      selectedExternalId: selectedRecord?.externalId || selectedRecord?.id || '',
    }));
    try {
      const params = new URLSearchParams();
      params.set('startDate', IFOOD_FINANCIAL_HOMOLOGATION_START);
      params.set('endDate', IFOOD_FINANCIAL_HOMOLOGATION_END);
      params.set('competence', IFOOD_FINANCIAL_HOMOLOGATION_COMPETENCE);
      params.set('homologation', 'true');
      params.set('live', 'true');
      const response = await apiFetch(`${API_URL}/api/finance/ifood/${ifoodFinancialMerchantId}/orders/${encodeURIComponent(safeOrderKey)}/records?${params.toString()}`);
      if (!response.ok) {
        throw await parseFinanceError(response, 'Falha ao carregar detalhe oficial do pedido');
      }
      const data = await response.json();
      setIfoodFinancialOrderDetailState({
        loading: false,
        error: null,
        data,
        selectedRecord,
        selectedExternalId: selectedRecord?.externalId || selectedRecord?.id || '',
      });
    } catch (err) {
      setIfoodFinancialOrderDetailState((prev) => ({
        ...prev,
        loading: false,
        error: err?.message || 'Erro ao carregar detalhe oficial do pedido',
      }));
    }
  }, [ifoodFinancialMerchantId]);

  const syncIfoodFinancial = useCallback(async () => {
    if (!ifoodFinancialMerchantId) return;
    setIfoodFinancialState((prev) => ({
      ...prev,
      syncing: true,
      error: null,
      syncResult: null,
      livePreview: false,
      liveErrors: [],
    }));
    try {
      const response = await apiFetch(`${API_URL}/api/finance/ifood/${ifoodFinancialMerchantId}/sync`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          startDate: IFOOD_FINANCIAL_HOMOLOGATION_START,
          endDate: IFOOD_FINANCIAL_HOMOLOGATION_END,
          competence: IFOOD_FINANCIAL_HOMOLOGATION_COMPETENCE,
          homologation: true,
          sales: true,
          financialEvents: true,
          settlements: true,
          anticipations: true,
        }),
      });
      if (!response.ok) {
        throw await parseFinanceError(response, 'Falha ao sincronizar iFood Financial');
      }
      const syncResult = await response.json();
      setIfoodFinancialState((prev) => ({
        ...prev,
        syncing: false,
        syncResult,
      }));
      await loadIfoodFinancialData();
    } catch (err) {
      setIfoodFinancialState((prev) => ({
        ...prev,
        syncing: false,
        error: err?.message || 'Erro ao sincronizar iFood Financial',
      }));
    }
  }, [ifoodFinancialMerchantId, loadIfoodFinancialData]);

  const loadCashClosing = useCallback(async () => {
    setCashClosingState((prev) => ({ ...prev, loading: true, error: null }));
    try {
      const params = new URLSearchParams();
      params.set('date', filters.end);
      if (filters.channel) params.set('channel', filters.channel);
      const response = await apiFetch(`${API_URL}/api/finance/cash-closing?${params.toString()}`);
      if (!response.ok) {
        throw await parseFinanceError(response, 'Falha ao carregar fechamento de caixa');
      }
      const data = await response.json();
      fillCashForm(data);
      setCashClosingState({
        loading: false,
        saving: false,
        error: null,
        data,
        lastUpdatedAt: new Date().toISOString(),
      });
    } catch (err) {
      setCashClosingState((prev) => ({
        ...prev,
        loading: false,
        saving: false,
        error: err?.message || 'Erro ao carregar fechamento de caixa',
      }));
    }
  }, [fillCashForm, filters.channel, filters.end]);

  const saveCashClosing = useCallback(async (status = 'open') => {
    setCashClosingState((prev) => ({ ...prev, saving: true, error: null }));
    try {
      const response = await apiFetch(`${API_URL}/api/finance/cash-closing`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          businessDate: filters.end,
          channel: filters.channel,
          status,
          openingBalance: toNumber(cashForm.openingBalance),
          supplyAmount: toNumber(cashForm.supplyAmount),
          withdrawalAmount: toNumber(cashForm.withdrawalAmount),
          reportedTotal: toNumber(cashForm.reportedTotal),
          notes: cashForm.notes,
        }),
      });
      if (!response.ok) {
        throw await parseFinanceError(response, 'Falha ao salvar fechamento de caixa');
      }
      const data = await response.json();
      fillCashForm(data);
      setCashClosingState({
        loading: false,
        saving: false,
        error: null,
        data,
        lastUpdatedAt: new Date().toISOString(),
      });
    } catch (err) {
      setCashClosingState((prev) => ({
        ...prev,
        saving: false,
        error: err?.message || 'Erro ao salvar fechamento de caixa',
      }));
    }
  }, [cashForm, fillCashForm, filters.channel, filters.end]);

  const loadAccountSetup = useCallback(async () => {
    try {
      const [categoryResponse, partyResponse, costCenterResponse, recurringResponse] = await Promise.all([
        apiFetch(`${API_URL}/api/finance/categories?includeInactive=true`),
        apiFetch(`${API_URL}/api/finance/parties?includeInactive=true`),
        apiFetch(`${API_URL}/api/finance/cost-centers?includeInactive=true`),
        apiFetch(`${API_URL}/api/finance/recurring-rules`),
      ]);
      if (!categoryResponse.ok) throw await parseFinanceError(categoryResponse, 'Falha ao carregar categorias');
      if (!partyResponse.ok) throw await parseFinanceError(partyResponse, 'Falha ao carregar partes');
      if (!costCenterResponse.ok) throw await parseFinanceError(costCenterResponse, 'Falha ao carregar centros de custo');
      if (!recurringResponse.ok) throw await parseFinanceError(recurringResponse, 'Falha ao carregar recorrências');

      const [categoryData, partyData, costCenterData, recurringData] = await Promise.all([
        categoryResponse.json(),
        partyResponse.json(),
        costCenterResponse.json(),
        recurringResponse.json(),
      ]);
      setAccountsState((prev) => ({
        ...prev,
        categories: getCategoryRows(categoryData),
        parties: getPartyRows(partyData),
        costCenters: getCostCenterRows(costCenterData),
        recurringRules: getRecurringRows(recurringData),
      }));
    } catch (err) {
      setAccountsState((prev) => ({
        ...prev,
        error: err?.message || 'Erro ao carregar cadastros financeiros',
      }));
    }
  }, []);

  const loadAccounts = useCallback(async () => {
    setAccountsState((prev) => ({ ...prev, loading: true, error: null }));
    try {
      const params = buildAccountParams();
      const response = await apiFetch(`${API_URL}/api/finance/accounts?${params.toString()}`);
      if (!response.ok) {
        throw await parseFinanceError(response, 'Falha ao carregar contas');
      }
      const data = await response.json();
      setAccountsState((prev) => ({
        ...prev,
        loading: false,
        error: null,
        data,
        lastUpdatedAt: new Date().toISOString(),
      }));
    } catch (err) {
      setAccountsState((prev) => ({
        ...prev,
        loading: false,
        error: err?.message || 'Erro ao carregar contas',
      }));
    }
  }, [buildAccountParams]);

  const loadExpenses = useCallback(async () => {
    setExpensesState((prev) => ({ ...prev, loading: true, error: null }));
    try {
      const params = buildExpenseParams();
      const response = await apiFetch(`${API_URL}/api/finance/accounts?${params.toString()}`);
      if (!response.ok) {
        throw await parseFinanceError(response, 'Falha ao carregar despesas');
      }
      const data = await response.json();
      setExpensesState({
        loading: false,
        saving: false,
        error: null,
        data,
        lastUpdatedAt: new Date().toISOString(),
      });
    } catch (err) {
      setExpensesState((prev) => ({
        ...prev,
        loading: false,
        error: err?.message || 'Erro ao carregar despesas',
      }));
    }
  }, [buildExpenseParams]);

  const refreshAfterExpenseMutation = useCallback(async () => {
    await Promise.all([
      loadExpenses(),
      loadAccounts(),
      (dreState.data || activeTab === 'dre') ? loadDre() : Promise.resolve(),
      (managementState.data || activeTab === 'gerencial') ? loadManagement() : Promise.resolve(),
    ]);
  }, [activeTab, dreState.data, loadAccounts, loadDre, loadExpenses, loadManagement, managementState.data]);

  const saveAccount = useCallback(async () => {
    setAccountsState((prev) => ({ ...prev, saving: true, error: null }));
    try {
      const response = await apiFetch(`${API_URL}/api/finance/accounts`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          ...accountForm,
          categoryId: accountForm.categoryId || null,
          partyId: accountForm.partyId || null,
          costCenterId: accountForm.costCenterId || null,
          amount: toNumber(accountForm.amount),
        }),
      });
      if (!response.ok) throw await parseFinanceError(response, 'Falha ao salvar conta');
      setAccountForm(getDefaultAccountForm());
      await loadAccounts();
    } catch (err) {
      setAccountsState((prev) => ({
        ...prev,
        error: err?.message || 'Erro ao salvar conta',
      }));
    } finally {
      setAccountsState((prev) => ({ ...prev, saving: false }));
    }
  }, [accountForm, loadAccounts]);

  const saveExpense = useCallback(async () => {
    setExpensesState((prev) => ({ ...prev, saving: true, error: null }));
    try {
      const payload = {
        type: 'payable',
        description: expenseForm.description,
        amount: toNumber(expenseForm.amount),
        dueDate: expenseForm.dueDate,
        competenceDate: expenseForm.competenceDate || expenseForm.dueDate,
        status: expenseForm.status,
        paidAmount: toNumber(expenseForm.paidAmount),
        paymentMethod: expenseForm.paymentMethod,
        categoryId: expenseForm.categoryId || null,
        partyId: expenseForm.partyId || null,
        costCenterId: expenseForm.costCenterId || null,
        reference: expenseForm.reference,
        notes: expenseForm.notes,
        metadata: {
          source: 'financeiro-despesas',
          includeInDreHint: getExpenseCategory(expenseForm.categoryId, accountsState.categories)?.includeInDre !== false,
        },
      };
      const url = expenseForm.id
        ? `${API_URL}/api/finance/accounts/${expenseForm.id}`
        : `${API_URL}/api/finance/accounts`;
      const response = await apiFetch(url, {
        method: expenseForm.id ? 'PUT' : 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload),
      });
      if (!response.ok) throw await parseFinanceError(response, 'Falha ao salvar despesa');
      setExpenseForm(getDefaultExpenseForm());
      await refreshAfterExpenseMutation();
    } catch (err) {
      setExpensesState((prev) => ({ ...prev, error: err?.message || 'Erro ao salvar despesa' }));
    } finally {
      setExpensesState((prev) => ({ ...prev, saving: false }));
    }
  }, [accountsState.categories, expenseForm, refreshAfterExpenseMutation]);

  const editExpense = useCallback((account) => {
    setExpenseForm({
      id: account.id,
      description: account.description || '',
      amount: String(account.amount || ''),
      dueDate: toDateOnly(account.dueDate) || toDateInputValue(new Date()),
      competenceDate: toDateOnly(account.competenceDate || account.dueDate) || toDateInputValue(new Date()),
      status: account.status || 'open',
      paidAmount: account.paidAmount ? String(account.paidAmount) : '',
      paymentMethod: account.paymentMethod || '',
      categoryId: account.categoryId || '',
      partyId: account.partyId || '',
      costCenterId: account.costCenterId || '',
      reference: account.reference || '',
      notes: account.notes || '',
    });
  }, []);

  const settleExpense = useCallback(async (account) => {
    setExpensesState((prev) => ({ ...prev, saving: true, error: null }));
    try {
      const response = await apiFetch(`${API_URL}/api/finance/accounts/${account.id}/settle`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          paidAmount: account.amount,
          paymentMethod: account.paymentMethod,
        }),
      });
      if (!response.ok) throw await parseFinanceError(response, 'Falha ao baixar despesa');
      await refreshAfterExpenseMutation();
    } catch (err) {
      setExpensesState((prev) => ({ ...prev, error: err?.message || 'Erro ao baixar despesa' }));
    } finally {
      setExpensesState((prev) => ({ ...prev, saving: false }));
    }
  }, [refreshAfterExpenseMutation]);

  const cancelExpense = useCallback(async (account) => {
    setExpensesState((prev) => ({ ...prev, saving: true, error: null }));
    try {
      const response = await apiFetch(`${API_URL}/api/finance/accounts/${account.id}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ status: 'cancelled', paidAmount: 0 }),
      });
      if (!response.ok) throw await parseFinanceError(response, 'Falha ao cancelar despesa');
      await refreshAfterExpenseMutation();
    } catch (err) {
      setExpensesState((prev) => ({ ...prev, error: err?.message || 'Erro ao cancelar despesa' }));
    } finally {
      setExpensesState((prev) => ({ ...prev, saving: false }));
    }
  }, [refreshAfterExpenseMutation]);

  const refreshAfterSetupMutation = useCallback(async () => {
    await loadAccountSetup();
    await Promise.all([
      activeTab === 'despesas' ? loadExpenses() : Promise.resolve(),
      accountsState.data || activeTab === 'contas' ? loadAccounts() : Promise.resolve(),
      dreState.data || activeTab === 'dre' ? loadDre() : Promise.resolve(),
      managementState.data || activeTab === 'gerencial' ? loadManagement() : Promise.resolve(),
    ]);
  }, [
    accountsState.data,
    activeTab,
    dreState.data,
    loadAccountSetup,
    loadAccounts,
    loadDre,
    loadExpenses,
    loadManagement,
    managementState.data,
  ]);

  const saveCategory = useCallback(async () => {
    if (!categoryForm.name.trim()) return;
    setAccountsState((prev) => ({ ...prev, saving: true, error: null }));
    try {
      const payload = {
        ...categoryForm,
        includeInDre: categoryForm.dreGroup === 'out_of_dre' ? false : categoryForm.includeInDre !== false,
      };
      const response = await apiFetch(`${API_URL}/api/finance/categories${categoryEditingId ? `/${categoryEditingId}` : ''}`, {
        method: categoryEditingId ? 'PUT' : 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload),
      });
      if (!response.ok) throw await parseFinanceError(response, 'Falha ao salvar categoria');
      setCategoryForm(DEFAULT_CATEGORY_FORM);
      setCategoryEditingId(null);
      await refreshAfterSetupMutation();
    } catch (err) {
      setAccountsState((prev) => ({ ...prev, error: err?.message || 'Erro ao salvar categoria' }));
    } finally {
      setAccountsState((prev) => ({ ...prev, saving: false }));
    }
  }, [categoryEditingId, categoryForm, refreshAfterSetupMutation]);

  const editCategory = useCallback((category) => {
    setExpenseSubTab('categorias');
    setCategoryEditingId(category.id);
    setCategoryForm({
      name: category.name || '',
      type: category.type || 'both',
      color: category.color || '#2e7d32',
      dreGroup: category.dreGroup || 'operational_expense',
      costBehavior: category.costBehavior || 'fixed',
      includeInDre: category.includeInDre !== false,
    });
  }, []);

  const saveCostCenter = useCallback(async () => {
    if (!costCenterForm.name.trim()) return;
    setAccountsState((prev) => ({ ...prev, saving: true, error: null }));
    try {
      const response = await apiFetch(`${API_URL}/api/finance/cost-centers${costCenterEditingId ? `/${costCenterEditingId}` : ''}`, {
        method: costCenterEditingId ? 'PUT' : 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(costCenterForm),
      });
      if (!response.ok) throw await parseFinanceError(response, 'Falha ao salvar centro de custo');
      setCostCenterForm(DEFAULT_COST_CENTER_FORM);
      setCostCenterEditingId(null);
      await refreshAfterSetupMutation();
    } catch (err) {
      setAccountsState((prev) => ({ ...prev, error: err?.message || 'Erro ao salvar centro de custo' }));
    } finally {
      setAccountsState((prev) => ({ ...prev, saving: false }));
    }
  }, [costCenterEditingId, costCenterForm, refreshAfterSetupMutation]);

  const editCostCenter = useCallback((center) => {
    setExpenseSubTab('centros');
    setCostCenterEditingId(center.id);
    setCostCenterForm({
      name: center.name || '',
      code: center.code || '',
      color: center.color || '#1976d2',
      isActive: center.isActive !== false,
    });
  }, []);

  const saveParty = useCallback(async () => {
    if (!partyForm.name.trim()) return;
    setAccountsState((prev) => ({ ...prev, saving: true, error: null }));
    try {
      const response = await apiFetch(`${API_URL}/api/finance/parties${partyEditingId ? `/${partyEditingId}` : ''}`, {
        method: partyEditingId ? 'PUT' : 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(partyForm),
      });
      if (!response.ok) throw await parseFinanceError(response, 'Falha ao salvar pessoa/fornecedor');
      setPartyForm(DEFAULT_PARTY_FORM);
      setPartyEditingId(null);
      await refreshAfterSetupMutation();
    } catch (err) {
      setAccountsState((prev) => ({ ...prev, error: err?.message || 'Erro ao salvar pessoa/fornecedor' }));
    } finally {
      setAccountsState((prev) => ({ ...prev, saving: false }));
    }
  }, [partyEditingId, partyForm, refreshAfterSetupMutation]);

  const editParty = useCallback((party) => {
    setExpenseSubTab('fornecedores');
    setPartyEditingId(party.id);
    setPartyForm({
      name: party.name || '',
      type: party.type || 'supplier',
      document: party.document || '',
      phone: party.phone || '',
      email: party.email || '',
      notes: party.notes || '',
      isActive: party.isActive !== false,
    });
  }, []);

  const saveQuickCategory = useCallback(async () => {
    if (!quickCategoryForm.name.trim()) return;
    setAccountsState((prev) => ({ ...prev, saving: true, error: null }));
    try {
      const response = await apiFetch(`${API_URL}/api/finance/categories`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          ...quickCategoryForm,
          type: 'payable',
          includeInDre: quickCategoryForm.dreGroup === 'out_of_dre' ? false : quickCategoryForm.includeInDre !== false,
        }),
      });
      if (!response.ok) throw await parseFinanceError(response, 'Falha ao adicionar categoria');
      const data = await response.json();
      const category = data.category;
      setExpenseForm((prev) => ({ ...prev, categoryId: category?.id || prev.categoryId }));
      setQuickCategoryForm(DEFAULT_QUICK_CATEGORY_FORM);
      setQuickExpenseForms((prev) => ({ ...prev, category: false }));
      await refreshAfterSetupMutation();
    } catch (err) {
      setAccountsState((prev) => ({ ...prev, error: err?.message || 'Erro ao adicionar categoria' }));
    } finally {
      setAccountsState((prev) => ({ ...prev, saving: false }));
    }
  }, [quickCategoryForm, refreshAfterSetupMutation]);

  const saveQuickSupplier = useCallback(async () => {
    if (!quickSupplierForm.name.trim()) return;
    setAccountsState((prev) => ({ ...prev, saving: true, error: null }));
    try {
      const response = await apiFetch(`${API_URL}/api/finance/parties`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ ...quickSupplierForm, type: 'supplier' }),
      });
      if (!response.ok) throw await parseFinanceError(response, 'Falha ao adicionar fornecedor');
      const data = await response.json();
      const party = data.party;
      setExpenseForm((prev) => ({ ...prev, partyId: party?.id || prev.partyId }));
      setQuickSupplierForm(DEFAULT_QUICK_SUPPLIER_FORM);
      setQuickExpenseForms((prev) => ({ ...prev, supplier: false }));
      await refreshAfterSetupMutation();
    } catch (err) {
      setAccountsState((prev) => ({ ...prev, error: err?.message || 'Erro ao adicionar fornecedor' }));
    } finally {
      setAccountsState((prev) => ({ ...prev, saving: false }));
    }
  }, [quickSupplierForm, refreshAfterSetupMutation]);

  const saveQuickCostCenter = useCallback(async () => {
    if (!quickCostCenterForm.name.trim()) return;
    setAccountsState((prev) => ({ ...prev, saving: true, error: null }));
    try {
      const response = await apiFetch(`${API_URL}/api/finance/cost-centers`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(quickCostCenterForm),
      });
      if (!response.ok) throw await parseFinanceError(response, 'Falha ao adicionar centro');
      const data = await response.json();
      const costCenter = data.costCenter;
      setExpenseForm((prev) => ({ ...prev, costCenterId: costCenter?.id || prev.costCenterId }));
      setQuickCostCenterForm(DEFAULT_QUICK_COST_CENTER_FORM);
      setQuickExpenseForms((prev) => ({ ...prev, costCenter: false }));
      await refreshAfterSetupMutation();
    } catch (err) {
      setAccountsState((prev) => ({ ...prev, error: err?.message || 'Erro ao adicionar centro' }));
    } finally {
      setAccountsState((prev) => ({ ...prev, saving: false }));
    }
  }, [quickCostCenterForm, refreshAfterSetupMutation]);

  const saveRecurringRule = useCallback(async () => {
    setAccountsState((prev) => ({ ...prev, saving: true, error: null }));
    try {
      const response = await apiFetch(`${API_URL}/api/finance/recurring-rules`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          ...recurringForm,
          categoryId: recurringForm.categoryId || null,
          partyId: recurringForm.partyId || null,
          costCenterId: recurringForm.costCenterId || null,
          amount: toNumber(recurringForm.amount),
        }),
      });
      if (!response.ok) throw await parseFinanceError(response, 'Falha ao salvar recorrência');
      setRecurringForm(getDefaultRecurringForm());
      await loadAccountSetup();
    } catch (err) {
      setAccountsState((prev) => ({ ...prev, error: err?.message || 'Erro ao salvar recorrência' }));
    } finally {
      setAccountsState((prev) => ({ ...prev, saving: false }));
    }
  }, [loadAccountSetup, recurringForm]);

  const settleAccount = useCallback(async (account) => {
    setAccountsState((prev) => ({ ...prev, saving: true, error: null }));
    try {
      const response = await apiFetch(`${API_URL}/api/finance/accounts/${account.id}/settle`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ paidAmount: account.amount }),
      });
      if (!response.ok) throw await parseFinanceError(response, 'Falha ao baixar conta');
      await loadAccounts();
    } catch (err) {
      setAccountsState((prev) => ({ ...prev, error: err?.message || 'Erro ao baixar conta' }));
    } finally {
      setAccountsState((prev) => ({ ...prev, saving: false }));
    }
  }, [loadAccounts]);

  const generateRecurring = useCallback(async () => {
    setAccountsState((prev) => ({ ...prev, saving: true, error: null }));
    try {
      const response = await apiFetch(`${API_URL}/api/finance/recurring-rules/generate`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ until: filters.end }),
      });
      if (!response.ok) throw await parseFinanceError(response, 'Falha ao gerar recorrências');
      await Promise.all([loadAccounts(), loadAccountSetup()]);
    } catch (err) {
      setAccountsState((prev) => ({ ...prev, error: err?.message || 'Erro ao gerar recorrências' }));
    } finally {
      setAccountsState((prev) => ({ ...prev, saving: false }));
    }
  }, [filters.end, loadAccountSetup, loadAccounts]);

  useEffect(() => {
    loadMovements();
  }, [loadMovements]);

  useEffect(() => {
    if (activeTab === 'conciliacao') {
      setActiveTab('resumo');
      return;
    }
    if (activeTab === 'ifoodFinancial' && !canViewIfoodFinancial) {
      setActiveTab('resumo');
    }
  }, [activeTab, canViewIfoodFinancial]);

  useEffect(() => {
    if (activeTab === 'ledger') {
      loadLedger();
    }
  }, [activeTab, loadLedger]);

  useEffect(() => {
    if (activeTab === 'dre') {
      loadDre();
    }
  }, [activeTab, loadDre]);

  useEffect(() => {
    if (activeTab === 'despesas') {
      loadExpenses();
      loadAccountSetup();
    }
  }, [activeTab, loadAccountSetup, loadExpenses]);

  useEffect(() => {
    if (activeTab === 'gerencial') {
      loadManagement();
    }
  }, [activeTab, loadManagement]);

  useEffect(() => {
    if (activeTab === 'ifoodFinancial' && canViewIfoodFinancial) {
      loadIfoodFinancialContext();
    }
  }, [activeTab, canViewIfoodFinancial, loadIfoodFinancialContext]);

  useEffect(() => {
    if (activeTab === 'ifoodFinancial' && canViewIfoodFinancial && ifoodFinancialMerchantId) {
      loadIfoodFinancialData();
    }
  }, [activeTab, canViewIfoodFinancial, ifoodFinancialMerchantId, loadIfoodFinancialData]);

  useEffect(() => {
    setIfoodFinancialOrderDetailState({
      loading: false,
      error: null,
      data: null,
      selectedRecord: null,
      selectedExternalId: '',
    });
  }, [ifoodFinancialMerchantId]);

  useEffect(() => {
    if (activeTab === 'caixa') {
      loadCashClosing();
    }
  }, [activeTab, loadCashClosing]);

  useEffect(() => {
    if (activeTab === 'contas') {
      loadAccounts();
      loadAccountSetup();
    }
  }, [activeTab, loadAccountSetup, loadAccounts]);

  const movements = useMemo(() => state.data?.movements || [], [state.data]);

  const enrichedMovements = useMemo(
    () =>
      movements.map((movement) => {
        const amounts = getMovementAmounts(movement);
        const paymentLabel = normalizePaymentMethod(movement);
        const fulfillmentLabel = normalizeFulfillment(movement);
        const qualityFlags = getQualityFlags(movement, amounts, paymentLabel);
        return {
          ...movement,
          amounts,
          paymentLabel,
          fulfillmentLabel,
          statusLabel: normalizeStatusLabel(movement.status),
          canceled: movement.financialStatus === 'cancelled' || isCanceledStatus(movement.status),
          recognized: movement.isRecognizedSale === undefined
            ? !isCanceledStatus(movement.status)
            : Boolean(movement.isRecognizedSale),
          qualityFlags,
        };
      }),
    [movements]
  );

  const dashboard = useMemo(() => {
    const activeRows = enrichedMovements.filter((movement) => movement.recognized);
    const canceledRows = enrichedMovements.filter((movement) => movement.canceled);
    const openRows = enrichedMovements.filter((movement) => !movement.recognized && !movement.canceled);
    const totalSold = activeRows.reduce((acc, row) => acc + row.amounts.total, 0);
    const gross = activeRows.reduce((acc, row) => acc + row.amounts.gross, 0);
    const discounts = activeRows.reduce((acc, row) => acc + row.amounts.discount, 0);
    const delivery = activeRows.reduce((acc, row) => acc + row.amounts.delivery, 0);
    const canceledTotal = canceledRows.reduce((acc, row) => acc + row.amounts.total, 0);
    const qualityRows = enrichedMovements.filter((row) => row.qualityFlags.length > 0);

    const qualityCounts = qualityRows.reduce((acc, row) => {
      row.qualityFlags.forEach((flag) => {
        acc[flag] = (acc[flag] || 0) + 1;
      });
      return acc;
    }, {});

    return {
      activeRows,
      canceledRows,
      totalSold,
      gross,
      discounts,
      delivery,
      canceledTotal,
      openRows,
      orders: activeRows.length,
      averageTicket: activeRows.length ? totalSold / activeRows.length : 0,
      paymentRows: groupRows(activeRows, (row) => row.paymentLabel),
      fulfillmentRows: groupRows(activeRows, (row) => row.fulfillmentLabel),
      channelRows: CHANNELS.map((channel) => {
        const rows = activeRows.filter((row) => row.channel === channel.key);
        return {
          label: channel.label,
          total: rows.reduce((acc, row) => acc + row.amounts.total, 0),
          count: rows.length,
          color: channel.color,
        };
      }),
      qualityRows,
      qualityCounts,
      qualityScore: enrichedMovements.length
        ? Math.max(0, (enrichedMovements.length - qualityRows.length) / enrichedMovements.length)
        : 1,
    };
  }, [enrichedMovements]);

  const qualityCountRows = useMemo(
    () =>
      Object.entries(dashboard.qualityCounts)
        .map(([label, count]) => ({ label, total: count, count }))
        .sort((a, b) => b.count - a.count),
    [dashboard.qualityCounts]
  );

  const ledgerEntries = useMemo(
    () => getLedgerRows(ledgerState.data).map(normalizeLedgerEntry),
    [ledgerState.data]
  );

  const ledgerSummary = useMemo(() => {
    const credit = ledgerEntries.reduce((acc, entry) => acc + entry.credit, 0);
    const debit = ledgerEntries.reduce((acc, entry) => acc + entry.debit, 0);
    const lastBalance = [...ledgerEntries].reverse().find((entry) => entry.balance || entry.balance === 0)?.balance;
    return {
      credit,
      debit,
      balance: lastBalance ?? credit - debit,
      entries: ledgerEntries.length,
    };
  }, [ledgerEntries]);

  const reconciliationRows = useMemo(() => {
    const tolerance = toNumber(reconciliationState.data?.toleranceAmount || 0.05);
    return getReconciliationRows(reconciliationState.data).map((row) => {
      const formRow = reconciliationForm.items[row.key] || {};
      const hasFormValue = formRow.reportedAmount !== undefined && formRow.reportedAmount !== '';
      const reportedAmount = hasFormValue
        ? toNumber(formRow.reportedAmount)
        : row.checked
          ? toNumber(row.reportedAmount)
          : 0;
      const expectedAmount = toNumber(row.expectedAmount);
      const differenceAmount = reportedAmount - expectedAmount;
      const checked = row.checked || hasFormValue;
      const status = !checked
        ? 'pending'
        : Math.abs(differenceAmount) <= tolerance
          ? 'reconciled'
          : 'divergent';
      return {
        ...row,
        reportedAmount,
        differenceAmount,
        status,
        checked,
        notes: formRow.notes ?? row.notes ?? '',
      };
    });
  }, [reconciliationForm.items, reconciliationState.data]);

  const reconciliationSummary = useMemo(() => {
    const expectedTotal = toNumber(reconciliationState.data?.summary?.expectedTotal);
    const reportedTotal = reconciliationRows.reduce((sum, row) => sum + toNumber(row.reportedAmount), 0);
    const differenceAmount = reportedTotal - expectedTotal;
    return {
      expectedTotal,
      reportedTotal,
      differenceAmount,
      methodCount: reconciliationRows.length,
      entryCount: toNumber(reconciliationState.data?.summary?.entryCount),
      pendingCount: reconciliationRows.filter((row) => row.status === 'pending').length,
      divergentCount: reconciliationRows.filter((row) => row.status === 'divergent').length,
      reconciledCount: reconciliationRows.filter((row) => row.status === 'reconciled').length,
    };
  }, [reconciliationRows, reconciliationState.data]);

  const reconciliationDifferenceRows = useMemo(
    () =>
      reconciliationRows
        .filter((row) => Math.abs(toNumber(row.differenceAmount)) > toNumber(reconciliationState.data?.toleranceAmount || 0.05))
        .map((row) => ({
          label: row.label,
          total: Math.abs(toNumber(row.differenceAmount)),
          count: row.entryCount,
        }))
        .sort((a, b) => b.total - a.total),
    [reconciliationRows, reconciliationState.data]
  );

  const ifoodFinancialMerchants = ifoodFinancialState.context?.merchants || [];
  const ifoodFinancialSummary = ifoodFinancialState.summary?.summary || {};
  const ifoodFinancialTotals = ifoodFinancialSummary.totals || {};
  const ifoodFinancialLatestSync = ifoodFinancialSummary.latestSync;
  const ifoodFinancialSettlementGuideAnswer =
    ifoodFinancialState.guide?.sections?.settlements?.answers?.storeTransfer ??
    ifoodFinancialState.guide?.sections?.settlements?.answers?.balance;
  const ifoodFinancialSettlementGuideValue = (() => {
    const rawValue = ifoodFinancialSettlementGuideAnswer && typeof ifoodFinancialSettlementGuideAnswer === 'object'
      ? ifoodFinancialSettlementGuideAnswer.value
      : ifoodFinancialSettlementGuideAnswer;
    if (rawValue === null || rawValue === undefined || rawValue === '') return null;
    const value = Number(rawValue);
    return Number.isFinite(value) ? value : null;
  })();
  const ifoodFinancialTypeRows = useMemo(
    () => {
      const byType = ifoodFinancialState.summary?.summary?.byType || {};
      return IFOOD_FINANCIAL_RECORD_TYPES
        .filter((item) => item.key !== 'all')
        .map((item) => ({
          label: item.label,
          total: toNumber(byType[item.key]?.count),
          count: toNumber(byType[item.key]?.count),
        }))
        .filter((row) => row.count > 0);
    },
    [ifoodFinancialState.summary]
  );
  const ifoodFinancialStatusRows = useMemo(
    () =>
      (ifoodFinancialSummary.statuses || [])
        .map((row) => ({
          label: `${getIfoodFinancialRecordTypeLabel(row.recordType)} · ${row.status}`,
          total: toNumber(row.count),
          count: toNumber(row.count),
        }))
        .sort((a, b) => b.count - a.count)
        .slice(0, 8),
    [ifoodFinancialSummary.statuses]
  );
  const hasIfoodFinancialOrderDetail = Boolean(
    ifoodFinancialOrderDetailState.loading ||
    ifoodFinancialOrderDetailState.error ||
    ifoodFinancialOrderDetailState.data ||
    ifoodFinancialOrderDetailState.selectedRecord
  );

  const cashClosings = useMemo(
    () => getCashClosingRows(cashClosingState.data).map(normalizeCashClosing),
    [cashClosingState.data]
  );

  const cashClosingRecord = cashClosings[0] || null;
  const cashExpected = useMemo(() => cashClosingState.data?.expected || {}, [cashClosingState.data]);

  const cashClosingSummary = useMemo(() => {
    const sales = firstNumber(
      cashExpected.recognizedSales,
      cashExpected.recognized_sales,
      cashClosings.reduce((acc, closing) => acc + closing.sales, 0)
    );
    const expected =
      toNumber(cashForm.openingBalance) +
      toNumber(cashExpected.cashSales) +
      toNumber(cashExpected.pixSales) +
      toNumber(cashExpected.creditSales) +
      toNumber(cashExpected.debitSales) +
      toNumber(cashExpected.onlineSales) +
      toNumber(cashExpected.otherSales) +
      toNumber(cashForm.supplyAmount) -
      toNumber(cashForm.withdrawalAmount);
    const counted = cashForm.reportedTotal === ''
      ? cashClosings.reduce((acc, closing) => acc + closing.counted, 0)
      : toNumber(cashForm.reportedTotal);
    const difference = counted - expected;
    return {
      expected,
      counted,
      sales,
      difference,
      closings: cashClosings.length,
    };
  }, [cashClosings, cashExpected, cashForm.openingBalance, cashForm.reportedTotal, cashForm.supplyAmount, cashForm.withdrawalAmount]);

  const cashCompositionRows = useMemo(
    () => [
      { label: 'Dinheiro', value: toNumber(cashExpected.cashSales), color: 'success' },
      { label: 'Pix', value: toNumber(cashExpected.pixSales), color: 'primary' },
      { label: 'Crédito', value: toNumber(cashExpected.creditSales), color: 'warning' },
      { label: 'Débito', value: toNumber(cashExpected.debitSales), color: 'info' },
      { label: 'Online', value: toNumber(cashExpected.onlineSales), color: 'secondary' },
      { label: 'Outros', value: toNumber(cashExpected.otherSales), color: 'default' },
    ],
    [cashExpected]
  );

  const financialAccounts = useMemo(
    () => getAccountRows(accountsState.data),
    [accountsState.data]
  );

  const expenseAccounts = useMemo(
    () => getAccountRows(expensesState.data),
    [expensesState.data]
  );

  const expenseSummary = useMemo(() => {
    const paidRows = expenseAccounts.filter((account) => account.status === 'paid');
    const pendingRows = expenseAccounts.filter((account) => isPendingFinancialAccount(account));
    const overdueRows = expenseAccounts.filter((account) => isOverdueFinancialAccount(account));
    const dreRows = expenseAccounts.filter((account) => (
      account.status !== 'cancelled' &&
      account.categoryIncludeInDre !== false &&
      account.categoryDreGroup !== 'out_of_dre'
    ));
    return {
      count: expenseAccounts.length,
      total: expenseAccounts.reduce((sum, account) => sum + toNumber(account.amount), 0),
      paid: paidRows.reduce((sum, account) => sum + toNumber(account.paidAmount || account.amount), 0),
      pending: pendingRows.reduce((sum, account) => sum + toNumber(account.remainingAmount || account.amount), 0),
      overdue: overdueRows.reduce((sum, account) => sum + toNumber(account.remainingAmount || account.amount), 0),
      dreImpact: dreRows.reduce((sum, account) => sum + toNumber(account.amount), 0),
      missingCategoryCount: expenseAccounts.filter((account) => account.status !== 'cancelled' && !account.categoryId).length,
      outsideDre: expenseAccounts
        .filter((account) => account.status !== 'cancelled' && (account.categoryIncludeInDre === false || account.categoryDreGroup === 'out_of_dre'))
        .reduce((sum, account) => sum + toNumber(account.amount), 0),
    };
  }, [expenseAccounts]);

  const accountsSummary = accountsState.data?.summary || {
    openReceivable: 0,
    openPayable: 0,
    dueTodayAmount: 0,
    dueTodayCount: 0,
    overdueAmount: 0,
    overdueCount: 0,
    paidAmount: 0,
    byCategory: [],
    byCostCenter: [],
  };

  const accountCategoryRows = useMemo(
    () => accountsSummary.byCategory || [],
    [accountsSummary.byCategory]
  );

  const accountCostCenterRows = useMemo(
    () => accountsSummary.byCostCenter || [],
    [accountsSummary.byCostCenter]
  );

  const categoryOverviewRows = useMemo(() => {
    const totalsByName = new Map(accountCategoryRows.map((row) => [row.label, row]));
    return accountsState.categories
      .map((category) => {
        const totals = totalsByName.get(category.name) || {};
        return {
          ...category,
          total: toNumber(totals.total),
          count: toNumber(totals.count),
        };
      })
      .sort((a, b) => b.total - a.total || String(a.name).localeCompare(String(b.name)));
  }, [accountCategoryRows, accountsState.categories]);

  const costCenterOverviewRows = useMemo(() => {
    const totalsByName = new Map(accountCostCenterRows.map((row) => [row.label, row]));
    const registeredRows = accountsState.costCenters.map((center) => {
      const totals = totalsByName.get(center.name) || {};
      return {
        ...center,
        label: center.name,
        total: toNumber(totals.total),
        count: toNumber(totals.count),
      };
    });
    const registeredNames = new Set(registeredRows.map((row) => row.label));
    const unregisteredRows = accountCostCenterRows
      .filter((row) => !registeredNames.has(row.label))
      .map((row) => ({
        id: row.label,
        label: row.label,
        name: row.label,
        total: toNumber(row.total),
        count: toNumber(row.count),
      }));
    return [...registeredRows, ...unregisteredRows]
      .sort((a, b) => b.total - a.total || String(a.label).localeCompare(String(b.label)));
  }, [accountCostCenterRows, accountsState.costCenters]);

  const accountCostCenterOptions = useMemo(
    () => accountsState.costCenters.filter((center) => center.isActive !== false),
    [accountsState.costCenters]
  );

  const accountCategoryOptions = useMemo(
    () => accountsState.categories.filter((category) => category.isActive !== false && (category.type === 'both' || category.type === accountForm.type)),
    [accountForm.type, accountsState.categories]
  );

  const expenseCategoryOptions = useMemo(
    () => accountsState.categories.filter((category) => category.isActive !== false && (category.type === 'both' || category.type === 'payable')),
    [accountsState.categories]
  );

  const expenseManagedCategories = useMemo(
    () =>
      accountsState.categories.filter((category) => (
        expenseCategoryFilterType === 'all' ||
        category.type === expenseCategoryFilterType ||
        (expenseCategoryFilterType !== 'both' && category.type === 'both')
      )),
    [accountsState.categories, expenseCategoryFilterType]
  );

  const supplierOptions = useMemo(
    () => accountsState.parties.filter((party) => party.isActive !== false && (party.type === 'supplier' || !party.type)),
    [accountsState.parties]
  );

  const recurringCategoryOptions = useMemo(
    () => accountsState.categories.filter((category) => category.isActive !== false && (category.type === 'both' || category.type === recurringForm.type)),
    [accountsState.categories, recurringForm.type]
  );

  const dreSummary = dreState.data?.summary || {
    salesRevenue: 0,
    manualReceivables: 0,
    totalRevenue: 0,
    cogs: 0,
    cogsMargin: 0,
    grossProfit: 0,
    grossMargin: 0,
    expenses: 0,
    directDeductions: 0,
    variableExpenses: 0,
    fixedExpenses: 0,
    operationalExpenses: 0,
    financialExpenses: 0,
    netResult: 0,
    netMargin: 0,
    expenseRatio: 0,
    orderCount: 0,
    accountCount: 0,
    includedAccountCount: 0,
    missingCategoryCount: 0,
    missingDreGroupCount: 0,
    accountsOutsideDre: 0,
    qualityIssueCount: 0,
    ignoredManualReceivables: 0,
  };

  const dreLines = useMemo(
    () => getDreLineRows(dreState.data),
    [dreState.data]
  );

  const dreExpensesByCategory = useMemo(
    () => dreState.data?.expensesByCategory || [],
    [dreState.data]
  );

  const dreExpensesByDreGroup = useMemo(
    () => dreState.data?.expensesByDreGroup || dreExpensesByCategory,
    [dreExpensesByCategory, dreState.data]
  );

  const dreRevenueByChannel = useMemo(
    () => dreState.data?.byChannel || [],
    [dreState.data]
  );

  const dreQualityScore = useMemo(() => {
    const totalSignals = toNumber(dreSummary.orderCount) + toNumber(dreSummary.accountCount);
    if (!totalSignals) return 0;
    const issues = toNumber(dreSummary.missingCategoryCount)
      + toNumber(dreSummary.missingDreGroupCount)
      + toNumber(dreSummary.qualityIssueCount);
    return Math.max(0, Math.min(1, (totalSignals - issues) / totalSignals));
  }, [
    dreSummary.accountCount,
    dreSummary.missingCategoryCount,
    dreSummary.missingDreGroupCount,
    dreSummary.orderCount,
    dreSummary.qualityIssueCount,
  ]);

  const managementSummary = managementState.data?.summary || {
    totalRevenue: 0,
    netResult: 0,
    expenses: 0,
    netMargin: 0,
    cogsMargin: 0,
    expenseRatio: 0,
  };

  const managementGoals = useMemo(() => {
    const rowsByMetric = new Map((managementState.data?.goals || []).map((goal) => [goal.metric, goal]));
    return GOAL_DEFINITIONS.map((definition) => {
      const row = rowsByMetric.get(definition.metric) || {};
      return {
        ...definition,
        ...row,
        comparison: row.comparison || definition.comparison,
        actualValue: toNumber(row.actualValue),
        targetValue: toNumber(row.targetValue),
        variance: toNumber(row.variance),
        progress: Math.max(0, Math.min(1, toNumber(row.progress))),
        hasTarget: Boolean(row.hasTarget),
        achieved: Boolean(row.achieved),
      };
    });
  }, [managementState.data]);

  const managementCostCenterRows = useMemo(() => {
    const expenseRows = managementState.data?.expensesByCostCenter || [];
    const totalsByName = new Map(expenseRows.map((row) => [row.label, row]));
    const centers = (managementState.data?.costCenters || accountsState.costCenters || []).map((center) => {
      const totals = totalsByName.get(center.name) || {};
      return {
        ...center,
        label: center.name,
        total: toNumber(totals.total),
        count: toNumber(totals.count),
      };
    });
    const centerNames = new Set(centers.map((center) => center.label));
    const expenseOnlyRows = expenseRows
      .filter((row) => !centerNames.has(row.label))
      .map((row) => ({
        id: row.label,
        label: row.label,
        name: row.label,
        total: toNumber(row.total),
        count: toNumber(row.count),
      }));
    return [...centers, ...expenseOnlyRows]
      .sort((a, b) => b.total - a.total || String(a.label).localeCompare(String(b.label)));
  }, [accountsState.costCenters, managementState.data]);

  const managementDeviationRows = useMemo(
    () => managementGoals.filter((goal) => goal.hasTarget && !goal.achieved),
    [managementGoals]
  );

  const exportCsv = useCallback(() => {
    const header = [
      'Data',
      'Canal',
      'Pedido',
      'Cliente',
      'Status',
      'Bruto (R$)',
      'Desconto (R$)',
      'Entrega (R$)',
      'Total estimado (R$)',
      'Pagamento',
      'Entrega/Retirada',
      'Qualidade',
    ];
    const rows = enrichedMovements.map((movement) => [
      formatDateTime(movement.movementAt),
      normalizeChannelLabel(movement.channel, movement.salesChannel),
      movement.displayId || movement.orderId,
      movement.customerName,
      movement.statusLabel,
      movement.amountKnown === false ? 'Não informado pela 99Food' : movement.amounts.gross.toFixed(2).replace('.', ','),
      movement.amounts.discount.toFixed(2).replace('.', ','),
      movement.amounts.delivery.toFixed(2).replace('.', ','),
      movement.amountKnown === false ? 'Não informado pela 99Food' : movement.amounts.total.toFixed(2).replace('.', ','),
      movement.paymentLabel,
      movement.fulfillmentLabel,
      movement.qualityFlags.join(' | '),
    ]);
    const statusLabel = filters.onlyFinished ? 'concluidos' : 'todos';
    downloadCsv(`financeiro_${filters.start}_a_${filters.end}_${statusLabel}.csv`, [header, ...rows]);
  }, [enrichedMovements, filters.end, filters.onlyFinished, filters.start]);

  const exportExpensesCsv = useCallback(() => {
    const header = [
      'Descrição',
      'Categoria',
      'Grupo DRE',
      'Fornecedor',
      'Centro de custo',
      'Vencimento',
      'Competência',
      'Status',
      'Valor (R$)',
      'Pago (R$)',
      'Restante (R$)',
      'Referência',
    ];
    const rows = expenseAccounts.map((account) => [
      account.description,
      account.categoryName || 'Sem categoria',
      account.categoryDreGroup === 'out_of_dre' || account.categoryIncludeInDre === false
        ? 'Fora da DRE'
        : getDreGroupLabel(account.categoryDreGroup),
      account.partyName || '',
      account.costCenterName || '',
      formatDateOnly(account.dueDate),
      formatDateOnly(account.competenceDate || account.dueDate),
      normalizeAccountStatusLabel(account),
      toNumber(account.amount).toFixed(2).replace('.', ','),
      toNumber(account.paidAmount).toFixed(2).replace('.', ','),
      toNumber(account.remainingAmount).toFixed(2).replace('.', ','),
      account.reference || '',
    ]);
    downloadCsv(`financeiro_despesas_${expenseFilters.month || 'periodo'}.csv`, [header, ...rows]);
  }, [expenseAccounts, expenseFilters.month]);

  const exportAccountsCsv = useCallback(() => {
    const header = [
      'Vencimento',
      'Tipo',
      'Descrição',
      'Categoria',
      'Centro de custo',
      'Pessoa/origem',
      'Status',
      'Valor (R$)',
      'Pago (R$)',
      'Restante (R$)',
    ];
    const rows = financialAccounts.map((account) => [
      formatDateOnly(account.dueDate),
      normalizeAccountTypeLabel(account.type),
      account.description,
      account.categoryName || 'Sem categoria',
      account.costCenterName || 'Sem centro',
      account.partyName || '',
      normalizeAccountStatusLabel(account),
      toNumber(account.amount).toFixed(2).replace('.', ','),
      toNumber(account.paidAmount).toFixed(2).replace('.', ','),
      toNumber(account.remainingAmount).toFixed(2).replace('.', ','),
    ]);
    downloadCsv(`financeiro_contas_${filters.start}_a_${filters.end}.csv`, [header, ...rows]);
  }, [filters.end, filters.start, financialAccounts]);

  const applyFilters = useCallback(() => {
    setFilters((prev) => ({ ...prev, ...draftFilters }));
  }, [draftFilters]);

  const setPeriod = useCallback(
    (period) => {
      const end = new Date(today);
      const start = new Date(today);
      if (period === 'week') start.setDate(end.getDate() - 6);
      if (period === 'month') start.setDate(1);
      const next = {
        ...draftFilters,
        start: toDateInputValue(start),
        end: toDateInputValue(end),
      };
      setDraftFilters(next);
      setFilters(next);
    },
    [draftFilters, today]
  );

  const applyCompetenceMonth = useCallback((monthValue) => {
    const monthRange = getMonthRange(monthValue);
    const next = {
      ...draftFilters,
      start: monthRange.start,
      end: monthRange.end,
    };
    setDraftFilters(next);
    setFilters(next);
  }, [draftFilters]);

  const filtersDirty =
    draftFilters.start !== filters.start ||
    draftFilters.end !== filters.end ||
    draftFilters.channel !== filters.channel ||
    draftFilters.onlyFinished !== filters.onlyFinished;

  const tableColSpan = 12;
  const showOperationalFilters = ['resumo', 'dre', 'gerencial', 'pagamentos'].includes(activeTab);
  const showOperationalEstimateAlert = ['resumo', 'pagamentos'].includes(activeTab);
  const activeCsvExport =
    activeTab === 'resumo'
      ? { onClick: exportCsv, disabled: !enrichedMovements.length || state.loading }
      : activeTab === 'despesas'
        ? { onClick: exportExpensesCsv, disabled: !expenseAccounts.length || expensesState.loading }
        : activeTab === 'contas'
        ? { onClick: exportAccountsCsv, disabled: !financialAccounts.length || accountsState.loading }
        : null;

  const renderMetricCardGrid = (items) => (
    <Grid container spacing={2} sx={{ mb: 2 }}>
      {items.map((item) => (
        <Grid key={item.label} item xs={12} sm={6} md={4} xl={items.length <= 4 ? 3 : 2}>
          <MetricCard {...item} />
        </Grid>
      ))}
    </Grid>
  );

  const renderActiveTabMetrics = () => {
    if (activeTab === 'resumo') {
      return renderMetricCardGrid([
        {
          icon: <PointOfSaleIcon />,
          label: 'Venda operacional',
          value: formatCurrency(dashboard.totalSold),
          helper: 'Estimativa dos pedidos concluídos; não é repasse bancário.',
          color: 'success',
          loading: state.loading,
        },
        {
          icon: <ReceiptLongIcon />,
          label: 'Pedidos',
          value: dashboard.orders,
          helper: `${dashboard.canceledRows.length} cancelado(s), ${dashboard.openRows.length} aberto(s)`,
          color: 'primary',
          loading: state.loading,
        },
        {
          icon: <TrendingUpIcon />,
          label: 'Média por pedido',
          value: formatCurrency(dashboard.averageTicket),
          helper: 'Média da venda operacional sem cancelados.',
          color: 'warning',
          loading: state.loading,
        },
        {
          icon: <DiscountOutlinedIcon />,
          label: 'Descontos do pedido',
          value: formatCurrency(dashboard.discounts),
          helper: 'Cupons, benefícios ou promoções informados na origem.',
          color: 'secondary',
          loading: state.loading,
        },
        {
          icon: <LocalShippingOutlinedIcon />,
          label: 'Entrega cobrada',
          value: formatCurrency(dashboard.delivery),
          helper: 'Taxa no pedido; não significa custo final da loja.',
          color: 'info',
          loading: state.loading,
        },
        {
          icon: <CancelOutlinedIcon />,
          label: 'Cancelamentos',
          value: formatCurrency(dashboard.canceledTotal),
          helper: `${dashboard.canceledRows.length} ocorrência(s)`,
          color: 'error',
          loading: state.loading,
        },
      ]);
    }

    if (activeTab === 'dre') {
      return renderMetricCardGrid([
        {
          icon: <AssessmentOutlinedIcon />,
          label: 'Receita gerencial',
          value: formatCurrency(dreSummary.totalRevenue),
          helper: 'Receita reconhecida no período da DRE.',
          color: 'success',
          loading: dreState.loading,
        },
        {
          icon: <RequestQuoteOutlinedIcon />,
          label: 'CMV',
          value: formatCurrency(dreSummary.cogs),
          helper: 'Custos classificados como CMV.',
          color: dreSummary.cogs > 0 ? 'warning' : 'info',
          loading: dreState.loading,
        },
        {
          icon: <TrendingUpIcon />,
          label: 'Resultado',
          value: formatCurrency(dreSummary.netResult),
          helper: 'Resultado gerencial após custos e despesas.',
          color: dreSummary.netResult < 0 ? 'error' : dreSummary.netResult === 0 ? 'warning' : 'success',
          loading: dreState.loading,
        },
        {
          icon: <PercentOutlinedIcon />,
          label: 'Margem',
          value: formatPercent(dreSummary.netMargin),
          helper: 'Margem líquida gerencial.',
          color: dreSummary.netMargin < 0.1 ? 'warning' : 'success',
          loading: dreState.loading,
        },
        {
          icon: <BalanceOutlinedIcon />,
          label: 'Despesas',
          value: formatCurrency(dreSummary.expenses),
          helper: 'Custos e despesas que entram na DRE.',
          color: 'error',
          loading: dreState.loading,
        },
        {
          icon: <FactCheckOutlinedIcon />,
          label: 'Contas usadas',
          value: dreSummary.includedAccountCount || 0,
          helper: `${dreSummary.missingCategoryCount || 0} sem categoria; ${formatCurrency(dreSummary.accountsOutsideDre || 0)} fora da DRE.`,
          color: 'primary',
          loading: dreState.loading,
        },
      ]);
    }

    if (activeTab === 'despesas') {
      return renderMetricCardGrid([
        {
          icon: <ReceiptLongIcon />,
          label: 'Total no mês',
          value: formatCurrency(expenseSummary.total),
          helper: `${expenseSummary.count} despesa(s) na competência filtrada.`,
          color: 'error',
          loading: expensesState.loading,
        },
        {
          icon: <CheckCircleOutlineOutlinedIcon />,
          label: 'Pagas',
          value: formatCurrency(expenseSummary.paid),
          helper: 'Despesas já baixadas no financeiro.',
          color: 'success',
          loading: expensesState.loading,
        },
        {
          icon: <RequestQuoteOutlinedIcon />,
          label: 'Pendentes',
          value: formatCurrency(expenseSummary.pending),
          helper: 'Saldo ainda aberto ou parcial.',
          color: 'warning',
          loading: expensesState.loading,
        },
        {
          icon: <WarningAmberOutlinedIcon />,
          label: 'Vencidas',
          value: formatCurrency(expenseSummary.overdue),
          helper: 'Despesas em aberto com vencimento passado.',
          color: 'error',
          loading: expensesState.loading,
        },
      ]);
    }

    if (activeTab === 'gerencial') {
      return renderMetricCardGrid([
        {
          icon: <AssessmentOutlinedIcon />,
          label: 'Receita x meta',
          value: formatCurrency(managementSummary.totalRevenue),
          helper: 'Receita usada na leitura gerencial.',
          color: 'success',
          loading: managementState.loading,
        },
        {
          icon: <TrendingUpIcon />,
          label: 'Resultado x meta',
          value: formatCurrency(managementSummary.netResult),
          helper: 'Resultado comparado as metas do período.',
          color: managementSummary.netResult < 0 ? 'error' : managementSummary.netResult === 0 ? 'warning' : 'success',
          loading: managementState.loading,
        },
        {
          icon: <BalanceOutlinedIcon />,
          label: 'Despesas gerenciais',
          value: formatCurrency(managementSummary.expenses),
          helper: 'Despesas consideradas na análise gerencial.',
          color: 'error',
          loading: managementState.loading,
        },
        {
          icon: <PercentOutlinedIcon />,
          label: 'Margem gerencial',
          value: formatPercent(managementSummary.netMargin),
          helper: 'Margem líquida do período gerencial.',
          color: managementSummary.netMargin < 0.1 ? 'warning' : 'success',
          loading: managementState.loading,
        },
      ]);
    }

    if (activeTab === 'pagamentos') {
      const totalByPayments = dashboard.paymentRows.reduce((sum, row) => sum + toNumber(row.total), 0);
      const principalMethod = dashboard.paymentRows[0];
      const withoutPayment = dashboard.activeRows.filter((row) => row.paymentLabel === 'Não informado').length;
      const items = [
        {
          icon: <PaymentsOutlinedIcon />,
          label: 'Total por pagamentos carregados',
          value: formatCurrency(totalByPayments),
          helper: 'Total agrupado pelas formas de pagamento reconhecidas.',
          color: 'primary',
          loading: state.loading,
        },
        {
          icon: <FactCheckOutlinedIcon />,
          label: 'Métodos identificados',
          value: dashboard.paymentRows.length,
          helper: 'Quantidade de formas de pagamento no período.',
          color: 'info',
          loading: state.loading,
        },
        {
          icon: <TrendingUpIcon />,
          label: 'Principal método',
          value: principalMethod ? principalMethod.label : '-',
          helper: principalMethod ? formatCurrency(principalMethod.total) : 'Sem pagamentos carregados.',
          color: 'success',
          loading: state.loading,
        },
      ];
      if (withoutPayment) {
        items.push({
          icon: <WarningAmberOutlinedIcon />,
          label: 'Registros sem forma de pagamento',
          value: withoutPayment,
          helper: 'Pedidos sem forma de pagamento identificada.',
          color: 'warning',
          loading: state.loading,
        });
      }
      return renderMetricCardGrid(items);
    }

    if (activeTab === 'ifoodFinancial' && canViewIfoodFinancial) {
      return renderMetricCardGrid([
        {
          icon: <CloudSyncOutlinedIcon />,
          label: 'Líquido oficial de vendas',
          value: formatCurrency(ifoodFinancialTotals.salesBalance),
          helper: `Dados oficiais iFood ${IFOOD_FINANCIAL_HOMOLOGATION_LABEL}.`,
          color: 'success',
          loading: ifoodFinancialState.loading || ifoodFinancialState.syncing,
        },
        {
          icon: <SyncOutlinedIcon />,
          label: 'Ajustes oficiais',
          value: formatCurrency(ifoodFinancialTotals.eventImpact),
          helper: 'Eventos oficiais com impacto financeiro.',
          color: 'primary',
          loading: ifoodFinancialState.loading || ifoodFinancialState.syncing,
        },
        {
          icon: <AccountBalanceOutlinedIcon />,
          label: 'Repasse efetivamente recebido',
          value: ifoodFinancialSettlementGuideValue !== null
            ? formatCurrency(ifoodFinancialSettlementGuideValue)
            : '-',
          helper: ifoodFinancialSettlementGuideValue !== null
            ? 'Campo balance da API Settlements no período homologatório.'
            : 'Aguardando o campo balance da API Settlements.',
          color: 'info',
          loading: ifoodFinancialState.loading || ifoodFinancialState.syncing,
        },
        {
          icon: <FactCheckOutlinedIcon />,
          label: 'Registros oficiais',
          value: ifoodFinancialTotals.records || 0,
          helper: 'Quantidade de registros oficiais carregados.',
          color: 'secondary',
          loading: ifoodFinancialState.loading || ifoodFinancialState.syncing,
        },
      ]);
    }

    if (activeTab === 'contas') {
      return renderMetricCardGrid([
        {
          icon: <RequestQuoteOutlinedIcon />,
          label: 'A pagar',
          value: formatCurrency(accountsSummary.openPayable),
          helper: 'Contas a pagar em aberto.',
          color: 'error',
          loading: accountsState.loading,
        },
        {
          icon: <AccountBalanceWalletOutlinedIcon />,
          label: 'A receber',
          value: formatCurrency(accountsSummary.openReceivable),
          helper: 'Contas a receber em aberto.',
          color: 'success',
          loading: accountsState.loading,
        },
        {
          icon: <WarningAmberOutlinedIcon />,
          label: `Vencidas (${accountsSummary.overdueCount || 0})`,
          value: formatCurrency(accountsSummary.overdueAmount),
          helper: 'Contas abertas com vencimento passado.',
          color: 'error',
          loading: accountsState.loading,
        },
        {
          icon: <CheckCircleOutlineOutlinedIcon />,
          label: 'Pagas',
          value: formatCurrency(accountsSummary.paidAmount),
          helper: 'Contas baixadas no período.',
          color: 'success',
          loading: accountsState.loading,
        },
        {
          icon: <FactCheckOutlinedIcon />,
          label: 'Pendentes',
          value: formatCurrency(toNumber(accountsSummary.openPayable) + toNumber(accountsSummary.openReceivable)),
          helper: 'Soma de contas abertas a pagar e receber.',
          color: 'warning',
          loading: accountsState.loading,
        },
      ]);
    }

    return null;
  };

  const renderExpenseList = () => (
    <SectionCard
      title="Despesas do mês"
      action={
        <Stack direction="row" spacing={1} alignItems="center" sx={{ flexWrap: 'wrap', rowGap: 1 }}>
          {expensesState.lastUpdatedAt ? (
            <Chip size="small" variant="outlined" label={formatDateTime(expensesState.lastUpdatedAt)} />
          ) : null}
          <Tooltip title="Atualizar despesas">
            <span>
              <IconButton size="small" onClick={loadExpenses} disabled={expensesState.loading}>
                <RefreshIcon fontSize="small" />
              </IconButton>
            </span>
          </Tooltip>
        </Stack>
      }
    >
      {expensesState.error ? (
        <Alert severity="error" sx={{ mb: 2 }}>
          {expensesState.error}
        </Alert>
      ) : null}

      <Stack spacing={1.5} sx={{ mb: 2 }}>
        <Stack direction={{ xs: 'column', md: 'row' }} spacing={1} useFlexGap sx={{ flexWrap: 'wrap' }}>
          <TextField
            size="small"
            label="Competência"
            type="month"
            value={expenseFilters.month}
            onChange={(e) => setExpenseFilters((prev) => ({ ...prev, month: e.target.value }))}
            InputLabelProps={{ shrink: true }}
            sx={{ width: { xs: '100%', md: 160 } }}
          />
          <FormControl size="small" sx={{ width: { xs: '100%', md: 160 } }}>
            <InputLabel id="finance-expense-status">Status</InputLabel>
            <Select
              labelId="finance-expense-status"
              label="Status"
              value={expenseFilters.status}
              onChange={(e) => setExpenseFilters((prev) => ({ ...prev, status: e.target.value }))}
            >
              <MenuItem value="all">Todos</MenuItem>
              <MenuItem value="pending">Abertas/parciais</MenuItem>
              <MenuItem value="open">Aberta</MenuItem>
              <MenuItem value="partial">Parcial</MenuItem>
              <MenuItem value="paid">Paga</MenuItem>
              <MenuItem value="overdue">Vencida</MenuItem>
              <MenuItem value="cancelled">Cancelada</MenuItem>
            </Select>
          </FormControl>
          <FormControl size="small" sx={{ minWidth: { xs: '100%', md: 190 }, flex: { md: 1 } }}>
            <InputLabel id="finance-expense-category-filter">Categoria</InputLabel>
            <Select
              labelId="finance-expense-category-filter"
              label="Categoria"
              value={expenseFilters.categoryId}
              onChange={(e) => setExpenseFilters((prev) => ({ ...prev, categoryId: e.target.value }))}
            >
              <MenuItem value="all">Todas</MenuItem>
              {expenseCategoryOptions.map((category) => (
                <MenuItem key={category.id} value={category.id}>
                  {category.name}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
          <FormControl size="small" sx={{ minWidth: { xs: '100%', md: 180 }, flex: { md: 1 } }}>
            <InputLabel id="finance-expense-cost-center-filter">Centro</InputLabel>
            <Select
              labelId="finance-expense-cost-center-filter"
              label="Centro"
              value={expenseFilters.costCenterId}
              onChange={(e) => setExpenseFilters((prev) => ({ ...prev, costCenterId: e.target.value }))}
            >
              <MenuItem value="all">Todos</MenuItem>
              {accountCostCenterOptions.map((center) => (
                <MenuItem key={center.id} value={center.id}>
                  {center.name}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </Stack>
        <Stack direction={{ xs: 'column', md: 'row' }} spacing={1}>
          <TextField
            size="small"
            label="Buscar"
            value={expenseFilters.q}
            onChange={(e) => setExpenseFilters((prev) => ({ ...prev, q: e.target.value }))}
            sx={{ flex: 1 }}
          />
          <Button
            variant="contained"
            onClick={loadExpenses}
            disabled={expensesState.loading}
            sx={{ borderRadius: 2, fontWeight: 800, minWidth: 108 }}
          >
            Aplicar
          </Button>
        </Stack>
      </Stack>

      {expenseSummary.missingCategoryCount || expenseSummary.outsideDre ? (
        <Alert severity={expenseSummary.missingCategoryCount ? 'warning' : 'info'} sx={{ mb: 2 }}>
          {expenseSummary.missingCategoryCount ? `${expenseSummary.missingCategoryCount} despesa(s) sem categoria. ` : ''}
          {expenseSummary.outsideDre ? `${formatCurrency(expenseSummary.outsideDre)} fora da DRE.` : ''}
        </Alert>
      ) : null}

      {expensesState.loading ? (
        <Stack spacing={1}>
          <Skeleton height={32} />
          <Skeleton height={32} />
          <Skeleton height={32} />
          <Skeleton height={32} />
        </Stack>
      ) : expenseAccounts.length ? (
        <TableContainer sx={{ maxHeight: '62vh' }}>
          <Table stickyHeader size="small" sx={{ minWidth: 1280 }}>
            <TableHead>
              <TableRow>
                <TableCell>Descrição</TableCell>
                <TableCell>Categoria</TableCell>
                <TableCell>Grupo DRE</TableCell>
                <TableCell>Fornecedor</TableCell>
                <TableCell>Centro</TableCell>
                <TableCell>Vencimento</TableCell>
                <TableCell>Competência</TableCell>
                <TableCell>Status</TableCell>
                <TableCell align="right">Valor</TableCell>
                <TableCell align="right">Pago</TableCell>
                <TableCell align="right">Restante</TableCell>
                <TableCell align="right">Ações</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {expenseAccounts.map((account) => {
                const dreLabel = account.categoryDreGroup === 'out_of_dre' || account.categoryIncludeInDre === false
                  ? 'Fora da DRE'
                  : getDreGroupLabel(account.categoryDreGroup);
                return (
                  <TableRow key={account.id} hover>
                    <TableCell sx={{ minWidth: 210, fontWeight: 800 }}>
                      {account.description}
                      {account.reference ? (
                        <Typography variant="caption" color="text.secondary" display="block">
                          {account.reference}
                        </Typography>
                      ) : null}
                    </TableCell>
                    <TableCell>{account.categoryName || 'Sem categoria'}</TableCell>
                    <TableCell>
                      <Chip size="small" variant="outlined" label={dreLabel} sx={{ borderRadius: 2 }} />
                    </TableCell>
                    <TableCell>{account.partyName || '-'}</TableCell>
                    <TableCell>{account.costCenterName || '-'}</TableCell>
                    <TableCell sx={{ whiteSpace: 'nowrap' }}>{formatDateOnly(account.dueDate)}</TableCell>
                    <TableCell sx={{ whiteSpace: 'nowrap' }}>{formatDateOnly(account.competenceDate || account.dueDate)}</TableCell>
                    <TableCell>
                      <Chip
                        size="small"
                        color={getAccountStatusColor(account)}
                        variant={account.status === 'paid' ? 'filled' : 'outlined'}
                        label={normalizeAccountStatusLabel(account)}
                        sx={{ borderRadius: 2, fontWeight: 800 }}
                      />
                    </TableCell>
                    <TableCell align="right" sx={{ fontWeight: 900, ...moneyColumnsSx }}>
                      {formatCurrency(account.amount)}
                    </TableCell>
                    <TableCell align="right" sx={moneyColumnsSx}>
                      {formatCurrency(account.paidAmount)}
                    </TableCell>
                    <TableCell align="right" sx={{ fontWeight: 900, ...moneyColumnsSx }}>
                      {formatCurrency(account.remainingAmount)}
                    </TableCell>
                    <TableCell align="right">
                      <Stack direction="row" spacing={0.5} justifyContent="flex-end">
                        <Tooltip title="Editar">
                          <IconButton size="small" onClick={() => editExpense(account)} disabled={expensesState.saving}>
                            <EditOutlinedIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                        <Tooltip title="Marcar como paga">
                          <span>
                            <IconButton size="small" onClick={() => settleExpense(account)} disabled={!isPendingFinancialAccount(account) || expensesState.saving}>
                              <CheckCircleOutlineOutlinedIcon fontSize="small" />
                            </IconButton>
                          </span>
                        </Tooltip>
                        <Tooltip title="Cancelar">
                          <span>
                            <IconButton size="small" onClick={() => cancelExpense(account)} disabled={account.status === 'cancelled' || expensesState.saving}>
                              <CancelOutlinedIcon fontSize="small" />
                            </IconButton>
                          </span>
                        </Tooltip>
                      </Stack>
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </TableContainer>
      ) : (
        <EmptyState
          icon={<ReceiptLongIcon fontSize="large" />}
          title="Nenhuma despesa no mês"
          helper="Lance despesas com competência mensal para refletir na DRE."
        />
      )}
    </SectionCard>
  );

  const renderQuickCategoryForm = () => quickExpenseForms.category ? (
    <Paper elevation={0} sx={{ p: 1.25, border: '1px solid', borderColor: 'divider', borderRadius: 2 }}>
      <Stack spacing={1}>
        <TextField
          size="small"
          label="Nova categoria"
          value={quickCategoryForm.name}
          onChange={(e) => setQuickCategoryForm((prev) => ({ ...prev, name: e.target.value }))}
        />
        <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1}>
          <FormControl size="small" sx={{ flex: 1 }}>
            <InputLabel id="quick-expense-category-dre">Grupo DRE</InputLabel>
            <Select
              labelId="quick-expense-category-dre"
              label="Grupo DRE"
              value={quickCategoryForm.dreGroup}
              onChange={(e) => {
                const dreGroup = e.target.value;
                setQuickCategoryForm((prev) => ({
                  ...prev,
                  dreGroup,
                  includeInDre: dreGroup === 'out_of_dre' ? false : prev.includeInDre,
                  costBehavior: ['sales_revenue', 'other_revenue', 'out_of_dre'].includes(dreGroup) ? 'none' : prev.costBehavior,
                }));
              }}
            >
              {DRE_GROUP_OPTIONS.filter((option) => !['sales_revenue', 'other_revenue'].includes(option.key)).map((option) => (
                <MenuItem key={option.key} value={option.key}>
                  {option.label}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
          <FormControl size="small" sx={{ flex: 1 }}>
            <InputLabel id="quick-expense-category-cost">Custo</InputLabel>
            <Select
              labelId="quick-expense-category-cost"
              label="Custo"
              value={quickCategoryForm.costBehavior}
              onChange={(e) => setQuickCategoryForm((prev) => ({ ...prev, costBehavior: e.target.value }))}
            >
              {COST_BEHAVIOR_OPTIONS.map((option) => (
                <MenuItem key={option.key} value={option.key}>
                  {option.label}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </Stack>
        <Stack direction="row" spacing={1} sx={{ flexWrap: 'wrap', rowGap: 1 }}>
          <Chip
            clickable
            color={quickCategoryForm.includeInDre ? 'success' : 'default'}
            variant={quickCategoryForm.includeInDre ? 'filled' : 'outlined'}
            label={quickCategoryForm.includeInDre ? 'Entra na DRE' : 'Fora da DRE'}
            onClick={() => setQuickCategoryForm((prev) => {
              const nextIncludeInDre = !prev.includeInDre;
              return {
                ...prev,
                includeInDre: nextIncludeInDre,
                dreGroup: nextIncludeInDre
                  ? prev.dreGroup === 'out_of_dre' ? 'operational_expense' : prev.dreGroup
                  : 'out_of_dre',
              };
            })}
            sx={{ borderRadius: 2, fontWeight: 800 }}
          />
          <Button size="small" variant="contained" onClick={saveQuickCategory} disabled={accountsState.saving || !quickCategoryForm.name.trim()} sx={{ borderRadius: 2, fontWeight: 800 }}>
            Salvar
          </Button>
          <Button size="small" variant="text" onClick={() => setQuickExpenseForms((prev) => ({ ...prev, category: false }))}>
            Cancelar
          </Button>
        </Stack>
      </Stack>
    </Paper>
  ) : null;

  const renderQuickSupplierForm = () => quickExpenseForms.supplier ? (
    <Paper elevation={0} sx={{ p: 1.25, border: '1px solid', borderColor: 'divider', borderRadius: 2 }}>
      <Stack spacing={1}>
        <TextField size="small" label="Novo fornecedor" value={quickSupplierForm.name} onChange={(e) => setQuickSupplierForm((prev) => ({ ...prev, name: e.target.value }))} />
        <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1}>
          <TextField size="small" label="CNPJ/CPF ou referência" value={quickSupplierForm.document} onChange={(e) => setQuickSupplierForm((prev) => ({ ...prev, document: e.target.value }))} sx={{ flex: 1 }} />
          <TextField size="small" label="Telefone" value={quickSupplierForm.phone} onChange={(e) => setQuickSupplierForm((prev) => ({ ...prev, phone: e.target.value }))} sx={{ flex: 1 }} />
        </Stack>
        <TextField size="small" label="E-mail" value={quickSupplierForm.email} onChange={(e) => setQuickSupplierForm((prev) => ({ ...prev, email: e.target.value }))} />
        <Stack direction="row" spacing={1} sx={{ flexWrap: 'wrap', rowGap: 1 }}>
          <Button size="small" variant="contained" onClick={saveQuickSupplier} disabled={accountsState.saving || !quickSupplierForm.name.trim()} sx={{ borderRadius: 2, fontWeight: 800 }}>
            Salvar
          </Button>
          <Button size="small" variant="text" onClick={() => setQuickExpenseForms((prev) => ({ ...prev, supplier: false }))}>
            Cancelar
          </Button>
        </Stack>
      </Stack>
    </Paper>
  ) : null;

  const renderQuickCostCenterForm = () => quickExpenseForms.costCenter ? (
    <Paper elevation={0} sx={{ p: 1.25, border: '1px solid', borderColor: 'divider', borderRadius: 2 }}>
      <Stack spacing={1}>
        <TextField size="small" label="Novo centro" value={quickCostCenterForm.name} onChange={(e) => setQuickCostCenterForm((prev) => ({ ...prev, name: e.target.value }))} />
        <Stack direction="row" spacing={1}>
          <TextField size="small" label="Código" value={quickCostCenterForm.code} onChange={(e) => setQuickCostCenterForm((prev) => ({ ...prev, code: e.target.value }))} sx={{ flex: 1 }} />
          <TextField size="small" label="Cor" type="color" value={quickCostCenterForm.color} onChange={(e) => setQuickCostCenterForm((prev) => ({ ...prev, color: e.target.value }))} InputLabelProps={{ shrink: true }} sx={{ width: 92 }} />
        </Stack>
        <Stack direction="row" spacing={1} sx={{ flexWrap: 'wrap', rowGap: 1 }}>
          <Button size="small" variant="contained" onClick={saveQuickCostCenter} disabled={accountsState.saving || !quickCostCenterForm.name.trim()} sx={{ borderRadius: 2, fontWeight: 800 }}>
            Salvar
          </Button>
          <Button size="small" variant="text" onClick={() => setQuickExpenseForms((prev) => ({ ...prev, costCenter: false }))}>
            Cancelar
          </Button>
        </Stack>
      </Stack>
    </Paper>
  ) : null;

  const renderExpenseForm = () => (
    <SectionCard title={expenseForm.id ? 'Editar despesa' : 'Nova despesa'}>
      <Stack spacing={1.25}>
        <TextField size="small" label="Descrição" required value={expenseForm.description} onChange={(e) => setExpenseForm((prev) => ({ ...prev, description: e.target.value }))} />
        <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1}>
          <TextField size="small" label="Valor" required value={expenseForm.amount} onChange={(e) => setExpenseForm((prev) => ({ ...prev, amount: e.target.value }))} sx={{ flex: 1 }} />
          <FormControl size="small" sx={{ flex: 1 }}>
            <InputLabel id="finance-expense-status-form">Status</InputLabel>
            <Select labelId="finance-expense-status-form" label="Status" value={expenseForm.status} onChange={(e) => setExpenseForm((prev) => ({ ...prev, status: e.target.value }))}>
              <MenuItem value="open">Aberta</MenuItem>
              <MenuItem value="partial">Parcial</MenuItem>
              <MenuItem value="paid">Paga</MenuItem>
              <MenuItem value="cancelled">Cancelada</MenuItem>
            </Select>
          </FormControl>
        </Stack>
        <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1}>
          <TextField
            size="small"
            label="Vencimento"
            type="date"
            required
            value={expenseForm.dueDate}
            onChange={(e) => setExpenseForm((prev) => ({ ...prev, dueDate: e.target.value, competenceDate: prev.competenceDate || e.target.value }))}
            InputLabelProps={{ shrink: true }}
            sx={{ flex: 1 }}
          />
          <TextField size="small" label="Competência" type="date" required value={expenseForm.competenceDate} onChange={(e) => setExpenseForm((prev) => ({ ...prev, competenceDate: e.target.value }))} InputLabelProps={{ shrink: true }} sx={{ flex: 1 }} />
        </Stack>
        <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1}>
          <TextField size="small" label="Valor pago" value={expenseForm.paidAmount} onChange={(e) => setExpenseForm((prev) => ({ ...prev, paidAmount: e.target.value }))} sx={{ flex: 1 }} />
          <TextField size="small" label="Forma de pagamento" value={expenseForm.paymentMethod} onChange={(e) => setExpenseForm((prev) => ({ ...prev, paymentMethod: e.target.value }))} sx={{ flex: 1 }} />
        </Stack>
        <Stack spacing={0.75}>
          <FormControl size="small">
            <InputLabel id="finance-expense-category">Categoria</InputLabel>
            <Select labelId="finance-expense-category" label="Categoria" value={expenseForm.categoryId} onChange={(e) => setExpenseForm((prev) => ({ ...prev, categoryId: e.target.value }))}>
              <MenuItem value="">Sem categoria</MenuItem>
              {expenseCategoryOptions.map((category) => (
                <MenuItem key={category.id} value={category.id}>
                  {category.name}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
          <Button size="small" variant="text" startIcon={<AddCircleOutlineOutlinedIcon />} onClick={() => setQuickExpenseForms((prev) => ({ ...prev, category: !prev.category }))} sx={{ alignSelf: 'flex-start', fontWeight: 800 }}>
            + adicionar categoria
          </Button>
          {renderQuickCategoryForm()}
        </Stack>
        <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1}>
          <Stack spacing={0.75} sx={{ flex: 1, minWidth: 0 }}>
            <FormControl size="small">
              <InputLabel id="finance-expense-party">Fornecedor</InputLabel>
              <Select labelId="finance-expense-party" label="Fornecedor" value={expenseForm.partyId} onChange={(e) => setExpenseForm((prev) => ({ ...prev, partyId: e.target.value }))}>
                <MenuItem value="">Não informado</MenuItem>
                {supplierOptions.map((party) => (
                  <MenuItem key={party.id} value={party.id}>
                    {party.name}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
            <Button size="small" variant="text" startIcon={<AddCircleOutlineOutlinedIcon />} onClick={() => setQuickExpenseForms((prev) => ({ ...prev, supplier: !prev.supplier }))} sx={{ alignSelf: 'flex-start', fontWeight: 800 }}>
              + adicionar fornecedor
            </Button>
            {renderQuickSupplierForm()}
          </Stack>
          <Stack spacing={0.75} sx={{ flex: 1, minWidth: 0 }}>
            <FormControl size="small">
              <InputLabel id="finance-expense-cost-center">Centro</InputLabel>
              <Select labelId="finance-expense-cost-center" label="Centro" value={expenseForm.costCenterId} onChange={(e) => setExpenseForm((prev) => ({ ...prev, costCenterId: e.target.value }))}>
                <MenuItem value="">Sem centro</MenuItem>
                {accountCostCenterOptions.map((center) => (
                  <MenuItem key={center.id} value={center.id}>
                    {center.name}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
            <Button size="small" variant="text" startIcon={<AddCircleOutlineOutlinedIcon />} onClick={() => setQuickExpenseForms((prev) => ({ ...prev, costCenter: !prev.costCenter }))} sx={{ alignSelf: 'flex-start', fontWeight: 800 }}>
              + adicionar centro
            </Button>
            {renderQuickCostCenterForm()}
          </Stack>
        </Stack>
        <TextField size="small" label="Referência/documento" value={expenseForm.reference} onChange={(e) => setExpenseForm((prev) => ({ ...prev, reference: e.target.value }))} />
        <TextField size="small" label="Observações" multiline minRows={2} value={expenseForm.notes} onChange={(e) => setExpenseForm((prev) => ({ ...prev, notes: e.target.value }))} />
        <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1}>
          <Button variant="contained" startIcon={<AddCircleOutlineOutlinedIcon />} onClick={saveExpense} disabled={expensesState.saving} sx={{ flex: 1, borderRadius: 2, fontWeight: 800 }}>
            {expenseForm.id ? 'Salvar despesa' : 'Lançar despesa'}
          </Button>
          <Button variant="outlined" onClick={() => setExpenseForm(getDefaultExpenseForm())} disabled={expensesState.saving} sx={{ borderRadius: 2, fontWeight: 800 }}>
            Limpar
          </Button>
        </Stack>
      </Stack>
    </SectionCard>
  );

  const renderExpenseLaunches = () => (
    <Box
      sx={{
        display: 'grid',
        gridTemplateColumns: { xs: '1fr', lg: 'minmax(340px, 0.42fr) minmax(0, 0.58fr)' },
        alignItems: 'start',
        gap: 2,
      }}
    >
      <Stack spacing={2} sx={{ minWidth: 0 }}>
        {renderExpenseForm()}
      </Stack>
      <Box sx={{ minWidth: 0 }}>
        {renderExpenseList()}
      </Box>
    </Box>
  );

  const renderExpenseCategories = () => (
    <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', lg: 'minmax(320px, 0.36fr) minmax(0, 0.64fr)' }, gap: 2, alignItems: 'start' }}>
      <SectionCard title={categoryEditingId ? 'Editar categoria' : 'Nova categoria'}>
        <Stack spacing={1.25}>
          <TextField size="small" label="Nome" required value={categoryForm.name} onChange={(e) => setCategoryForm((prev) => ({ ...prev, name: e.target.value }))} />
          <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1}>
            <FormControl size="small" sx={{ flex: 1 }}>
              <InputLabel id="expense-category-type">Tipo</InputLabel>
              <Select labelId="expense-category-type" label="Tipo" value={categoryForm.type} onChange={(e) => setCategoryForm((prev) => ({ ...prev, type: e.target.value }))}>
                <MenuItem value="payable">Pagar</MenuItem>
                <MenuItem value="receivable">Receber</MenuItem>
                <MenuItem value="both">Ambos</MenuItem>
              </Select>
            </FormControl>
            <TextField size="small" label="Cor" type="color" value={categoryForm.color} onChange={(e) => setCategoryForm((prev) => ({ ...prev, color: e.target.value }))} InputLabelProps={{ shrink: true }} sx={{ width: { xs: '100%', sm: 92 } }} />
          </Stack>
          <FormControl size="small">
            <InputLabel id="expense-category-dre-group">Grupo DRE</InputLabel>
            <Select
              labelId="expense-category-dre-group"
              label="Grupo DRE"
              value={categoryForm.dreGroup}
              onChange={(e) => {
                const dreGroup = e.target.value;
                setCategoryForm((prev) => ({
                  ...prev,
                  dreGroup,
                  includeInDre: dreGroup === 'out_of_dre' ? false : prev.includeInDre,
                  costBehavior: ['sales_revenue', 'other_revenue', 'out_of_dre'].includes(dreGroup) ? 'none' : prev.costBehavior,
                }));
              }}
            >
              {DRE_GROUP_OPTIONS.map((option) => (
                <MenuItem key={option.key} value={option.key}>{option.label}</MenuItem>
              ))}
            </Select>
          </FormControl>
          <FormControl size="small">
            <InputLabel id="expense-category-cost-behavior">Comportamento</InputLabel>
            <Select labelId="expense-category-cost-behavior" label="Comportamento" value={categoryForm.costBehavior} onChange={(e) => setCategoryForm((prev) => ({ ...prev, costBehavior: e.target.value }))}>
              {COST_BEHAVIOR_OPTIONS.map((option) => (
                <MenuItem key={option.key} value={option.key}>{option.label}</MenuItem>
              ))}
            </Select>
          </FormControl>
          <Stack direction="row" spacing={1} sx={{ flexWrap: 'wrap', rowGap: 1 }}>
            <Chip clickable color={categoryForm.includeInDre ? 'success' : 'default'} variant={categoryForm.includeInDre ? 'filled' : 'outlined'} label={categoryForm.includeInDre ? 'Entra na DRE' : 'Fora da DRE'} onClick={() => setCategoryForm((prev) => {
              const nextIncludeInDre = !prev.includeInDre;
              return { ...prev, includeInDre: nextIncludeInDre, dreGroup: nextIncludeInDre ? prev.dreGroup === 'out_of_dre' ? 'operational_expense' : prev.dreGroup : 'out_of_dre' };
            })} sx={{ borderRadius: 2, fontWeight: 800 }} />
            <Chip clickable color={categoryForm.isActive === false ? 'default' : 'primary'} variant={categoryForm.isActive === false ? 'outlined' : 'filled'} label={categoryForm.isActive === false ? 'Inativa' : 'Ativa'} onClick={() => setCategoryForm((prev) => ({ ...prev, isActive: prev.isActive === false }))} sx={{ borderRadius: 2, fontWeight: 800 }} />
          </Stack>
          <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1}>
            <Button variant="contained" onClick={saveCategory} disabled={accountsState.saving || !categoryForm.name.trim()} sx={{ borderRadius: 2, fontWeight: 800 }}>
              {categoryEditingId ? 'Salvar categoria' : 'Criar categoria'}
            </Button>
            <Button variant="outlined" onClick={() => { setCategoryEditingId(null); setCategoryForm(DEFAULT_CATEGORY_FORM); }} sx={{ borderRadius: 2, fontWeight: 800 }}>
              Limpar
            </Button>
          </Stack>
        </Stack>
      </SectionCard>
      <SectionCard
        title="Categorias de despesas"
        action={
          <FormControl size="small" sx={{ minWidth: 150 }}>
            <InputLabel id="expense-category-filter-type">Tipo</InputLabel>
            <Select labelId="expense-category-filter-type" label="Tipo" value={expenseCategoryFilterType} onChange={(e) => setExpenseCategoryFilterType(e.target.value)}>
              <MenuItem value="all">Todos</MenuItem>
              <MenuItem value="payable">Pagar</MenuItem>
              <MenuItem value="receivable">Receber</MenuItem>
              <MenuItem value="both">Ambos</MenuItem>
            </Select>
          </FormControl>
        }
      >
        <TableContainer sx={{ maxHeight: '58vh' }}>
          <Table stickyHeader size="small" sx={{ minWidth: 840 }}>
            <TableHead>
              <TableRow>
                <TableCell>Categoria</TableCell>
                <TableCell>Tipo</TableCell>
                <TableCell>Grupo DRE</TableCell>
                <TableCell>Custo</TableCell>
                <TableCell>Status</TableCell>
                <TableCell align="right">Ações</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {expenseManagedCategories.map((category) => (
                <TableRow key={category.id} hover>
                  <TableCell sx={{ fontWeight: 900 }}>{category.name}</TableCell>
                  <TableCell>{ACCOUNT_TYPE_LABELS[category.type] || category.type}</TableCell>
                  <TableCell><Chip size="small" variant="outlined" label={getDreGroupLabel(category.dreGroup)} sx={{ borderRadius: 2 }} /></TableCell>
                  <TableCell>{COST_BEHAVIOR_OPTIONS.find((option) => option.key === category.costBehavior)?.label || category.costBehavior}</TableCell>
                  <TableCell>
                    <Stack direction="row" spacing={0.75} sx={{ flexWrap: 'wrap', rowGap: 0.75 }}>
                      <Chip size="small" color={category.includeInDre === false || category.dreGroup === 'out_of_dre' ? 'default' : 'success'} variant="outlined" label={category.includeInDre === false || category.dreGroup === 'out_of_dre' ? 'Fora DRE' : 'DRE'} sx={{ borderRadius: 2 }} />
                      <Chip size="small" color={category.isActive === false ? 'default' : 'primary'} variant="outlined" label={category.isActive === false ? 'Inativa' : 'Ativa'} sx={{ borderRadius: 2 }} />
                    </Stack>
                  </TableCell>
                  <TableCell align="right">
                    <Tooltip title="Editar categoria">
                      <IconButton size="small" onClick={() => editCategory(category)}><EditOutlinedIcon fontSize="small" /></IconButton>
                    </Tooltip>
                  </TableCell>
                </TableRow>
              ))}
              {!expenseManagedCategories.length ? (
                <TableRow><TableCell colSpan={6}>Nenhuma categoria encontrada.</TableCell></TableRow>
              ) : null}
            </TableBody>
          </Table>
        </TableContainer>
      </SectionCard>
    </Box>
  );

  const renderExpenseSuppliers = () => (
    <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', lg: 'minmax(320px, 0.36fr) minmax(0, 0.64fr)' }, gap: 2, alignItems: 'start' }}>
      <SectionCard title={partyEditingId ? 'Editar fornecedor' : 'Novo fornecedor'}>
        <Stack spacing={1.25}>
          <TextField size="small" label="Nome" required value={partyForm.name} onChange={(e) => setPartyForm((prev) => ({ ...prev, name: e.target.value, type: 'supplier' }))} />
          <TextField size="small" label="CNPJ/CPF ou referência" value={partyForm.document} onChange={(e) => setPartyForm((prev) => ({ ...prev, document: e.target.value }))} />
          <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1}>
            <TextField size="small" label="Telefone" value={partyForm.phone} onChange={(e) => setPartyForm((prev) => ({ ...prev, phone: e.target.value }))} sx={{ flex: 1 }} />
            <TextField size="small" label="E-mail" value={partyForm.email} onChange={(e) => setPartyForm((prev) => ({ ...prev, email: e.target.value }))} sx={{ flex: 1 }} />
          </Stack>
          <TextField size="small" label="Observações" multiline minRows={2} value={partyForm.notes} onChange={(e) => setPartyForm((prev) => ({ ...prev, notes: e.target.value }))} />
          <Chip clickable color={partyForm.isActive === false ? 'default' : 'primary'} variant={partyForm.isActive === false ? 'outlined' : 'filled'} label={partyForm.isActive === false ? 'Inativo' : 'Ativo'} onClick={() => setPartyForm((prev) => ({ ...prev, isActive: prev.isActive === false }))} sx={{ alignSelf: 'flex-start', borderRadius: 2, fontWeight: 800 }} />
          <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1}>
            <Button variant="contained" onClick={saveParty} disabled={accountsState.saving || !partyForm.name.trim()} sx={{ borderRadius: 2, fontWeight: 800 }}>
              {partyEditingId ? 'Salvar fornecedor' : 'Criar fornecedor'}
            </Button>
            <Button variant="outlined" onClick={() => { setPartyEditingId(null); setPartyForm(DEFAULT_PARTY_FORM); }} sx={{ borderRadius: 2, fontWeight: 800 }}>
              Limpar
            </Button>
          </Stack>
        </Stack>
      </SectionCard>
      <SectionCard title="Fornecedores cadastrados">
        <TableContainer sx={{ maxHeight: '58vh' }}>
          <Table stickyHeader size="small" sx={{ minWidth: 820 }}>
            <TableHead>
              <TableRow>
                <TableCell>Fornecedor</TableCell>
                <TableCell>Documento</TableCell>
                <TableCell>Telefone</TableCell>
                <TableCell>E-mail</TableCell>
                <TableCell>Status</TableCell>
                <TableCell align="right">Ações</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {accountsState.parties.filter((party) => party.type === 'supplier').map((party) => (
                <TableRow key={party.id} hover>
                  <TableCell sx={{ fontWeight: 900 }}>{party.name}</TableCell>
                  <TableCell>{party.document || '-'}</TableCell>
                  <TableCell>{party.phone || '-'}</TableCell>
                  <TableCell>{party.email || '-'}</TableCell>
                  <TableCell><Chip size="small" variant="outlined" color={party.isActive === false ? 'default' : 'success'} label={party.isActive === false ? 'Inativo' : 'Ativo'} sx={{ borderRadius: 2 }} /></TableCell>
                  <TableCell align="right"><IconButton size="small" onClick={() => editParty(party)}><EditOutlinedIcon fontSize="small" /></IconButton></TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      </SectionCard>
    </Box>
  );

  const renderExpenseCostCenters = () => (
    <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', lg: 'minmax(320px, 0.36fr) minmax(0, 0.64fr)' }, gap: 2, alignItems: 'start' }}>
      <SectionCard title={costCenterEditingId ? 'Editar centro de custo' : 'Novo centro de custo'}>
        <Stack spacing={1.25}>
          <TextField size="small" label="Nome" required value={costCenterForm.name} onChange={(e) => setCostCenterForm((prev) => ({ ...prev, name: e.target.value }))} />
          <Stack direction="row" spacing={1}>
            <TextField size="small" label="Código" value={costCenterForm.code} onChange={(e) => setCostCenterForm((prev) => ({ ...prev, code: e.target.value }))} sx={{ flex: 1 }} />
            <TextField size="small" label="Cor" type="color" value={costCenterForm.color} onChange={(e) => setCostCenterForm((prev) => ({ ...prev, color: e.target.value }))} InputLabelProps={{ shrink: true }} sx={{ width: 92 }} />
          </Stack>
          <Chip clickable color={costCenterForm.isActive === false ? 'default' : 'primary'} variant={costCenterForm.isActive === false ? 'outlined' : 'filled'} label={costCenterForm.isActive === false ? 'Inativo' : 'Ativo'} onClick={() => setCostCenterForm((prev) => ({ ...prev, isActive: prev.isActive === false }))} sx={{ alignSelf: 'flex-start', borderRadius: 2, fontWeight: 800 }} />
          <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1}>
            <Button variant="contained" onClick={saveCostCenter} disabled={accountsState.saving || !costCenterForm.name.trim()} sx={{ borderRadius: 2, fontWeight: 800 }}>
              {costCenterEditingId ? 'Salvar centro' : 'Criar centro'}
            </Button>
            <Button variant="outlined" onClick={() => { setCostCenterEditingId(null); setCostCenterForm(DEFAULT_COST_CENTER_FORM); }} sx={{ borderRadius: 2, fontWeight: 800 }}>
              Limpar
            </Button>
          </Stack>
        </Stack>
      </SectionCard>
      <SectionCard title="Centros de custo cadastrados">
        <TableContainer sx={{ maxHeight: '58vh' }}>
          <Table stickyHeader size="small" sx={{ minWidth: 720 }}>
            <TableHead>
              <TableRow>
                <TableCell>Centro</TableCell>
                <TableCell>Código</TableCell>
                <TableCell>Cor</TableCell>
                <TableCell>Status</TableCell>
                <TableCell align="right">Ações</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {accountsState.costCenters.map((center) => (
                <TableRow key={center.id} hover>
                  <TableCell sx={{ fontWeight: 900 }}>{center.name}</TableCell>
                  <TableCell>{center.code || '-'}</TableCell>
                  <TableCell><Box sx={{ width: 22, height: 22, borderRadius: 1, backgroundColor: center.color || '#1976d2', border: '1px solid', borderColor: 'divider' }} /></TableCell>
                  <TableCell><Chip size="small" variant="outlined" color={center.isActive === false ? 'default' : 'success'} label={center.isActive === false ? 'Inativo' : 'Ativo'} sx={{ borderRadius: 2 }} /></TableCell>
                  <TableCell align="right"><IconButton size="small" onClick={() => editCostCenter(center)}><EditOutlinedIcon fontSize="small" /></IconButton></TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      </SectionCard>
    </Box>
  );

  const renderExpenseSubTabContent = () => {
    if (expenseSubTab === 'categorias') return renderExpenseCategories();
    if (expenseSubTab === 'fornecedores') return renderExpenseSuppliers();
    if (expenseSubTab === 'centros') return renderExpenseCostCenters();
    return renderExpenseLaunches();
  };

  return (
    <Box sx={financePageSx}>
      <Paper
        elevation={0}
        sx={(theme) => ({
          p: { xs: 1.5, md: 2 },
          borderRadius: 2,
          backgroundColor: alpha(theme.palette.success.main, 0.045),
          border: '1px solid',
          borderColor: 'divider',
          mb: 1.5,
        })}
      >
        <Stack
          direction={{ xs: 'column', lg: 'row' }}
          spacing={2}
          alignItems={{ lg: 'center' }}
          justifyContent="space-between"
          sx={{ minWidth: 0 }}
        >
          <Stack direction="row" alignItems="center" spacing={0.5} sx={{ minWidth: 0 }}>
            <PageMenuButton />
            <Typography variant="h4" sx={{ fontWeight: 900, letterSpacing: 0, overflowWrap: 'anywhere' }}>
              Financeiro
            </Typography>
          </Stack>

          <Stack
            direction="row"
            spacing={1}
            alignItems="center"
            justifyContent={{ xs: 'flex-start', lg: 'flex-end' }}
            useFlexGap
            sx={{ minWidth: 0, flexWrap: 'wrap', rowGap: 1 }}
          >
            {activeCsvExport ? (
              <Button
                variant="contained"
                startIcon={<DownloadIcon />}
                onClick={activeCsvExport.onClick}
                disabled={activeCsvExport.disabled}
                sx={{ borderRadius: 2, fontWeight: 800 }}
              >
                Exportar CSV
              </Button>
            ) : null}
          </Stack>
        </Stack>
      </Paper>

      <Paper elevation={0} sx={{ maxWidth: '100%', overflow: 'hidden', borderRadius: 2, border: '1px solid', borderColor: 'divider', mb: 2 }}>
        <Tabs
          value={activeTab}
          onChange={(_, value) => setActiveTab(value)}
          variant="scrollable"
          scrollButtons="auto"
          allowScrollButtonsMobile
          sx={financeTabsSx}
        >
          <Tab value="resumo" icon={<StorefrontOutlinedIcon />} iconPosition="start" label="Resumo" />
          <Tab value="despesas" icon={<ReceiptLongIcon />} iconPosition="start" label="Despesas" />
          <Tab value="dre" icon={<AssessmentOutlinedIcon />} iconPosition="start" label="DRE" />
          <Tab value="pagamentos" icon={<PaymentsOutlinedIcon />} iconPosition="start" label="Pagamentos" />
          <Tab value="gerencial" icon={<BalanceOutlinedIcon />} iconPosition="start" label="Gerencial" />
          {canViewIfoodFinancial ? (
            <Tab value="ifoodFinancial" icon={<CloudSyncOutlinedIcon />} iconPosition="start" label="iFood Financial oficial" />
          ) : null}
        </Tabs>
      </Paper>

      {showOperationalFilters ? (
      <Paper elevation={0} sx={{ p: 2, borderRadius: 2, border: '1px solid', borderColor: 'divider', mb: 2 }}>
        <Stack
          direction={{ xs: 'column', md: 'row' }}
          spacing={2}
          alignItems={{ md: 'flex-end' }}
          useFlexGap
          sx={{ minWidth: 0, flexWrap: 'wrap' }}
        >
          <TextField
            label="Início"
            type="date"
            value={draftFilters.start}
            onChange={(e) => setDraftFilters((prev) => ({ ...prev, start: e.target.value }))}
            InputLabelProps={{ shrink: true }}
            size="small"
            sx={{ width: { xs: '100%', sm: 190 } }}
          />
          <TextField
            label="Fim"
            type="date"
            value={draftFilters.end}
            onChange={(e) => setDraftFilters((prev) => ({ ...prev, end: e.target.value }))}
            InputLabelProps={{ shrink: true }}
            size="small"
            sx={{ width: { xs: '100%', sm: 190 } }}
          />

          <FormControl size="small" sx={{ width: { xs: '100%', sm: 220 } }}>
            <InputLabel id="financeiro-channel">Canal</InputLabel>
            <Select
              labelId="financeiro-channel"
              label="Canal"
              value={draftFilters.channel}
              onChange={(e) => setDraftFilters((prev) => ({ ...prev, channel: e.target.value }))}
            >
              <MenuItem value="all">Todos</MenuItem>
              {CHANNELS.map((channel) => (
                <MenuItem key={channel.key} value={channel.key}>
                  {channel.label}
                </MenuItem>
              ))}
            </Select>
          </FormControl>

          <Stack direction="row" spacing={1} alignItems="center" useFlexGap sx={{ minWidth: 0, flexWrap: 'wrap', rowGap: 1 }}>
            <Chip
              clickable
              color={draftFilters.onlyFinished ? 'success' : 'default'}
              variant={draftFilters.onlyFinished ? 'filled' : 'outlined'}
              label={draftFilters.onlyFinished ? 'Apenas concluídos' : 'Todos os status'}
              onClick={() => setDraftFilters((prev) => ({ ...prev, onlyFinished: !prev.onlyFinished }))}
              sx={{ fontWeight: 800, borderRadius: 2 }}
            />
          </Stack>

          <Stack direction="row" spacing={1} useFlexGap sx={{ minWidth: 0, flexWrap: 'wrap', rowGap: 1 }}>
            <Button size="small" variant="outlined" onClick={() => setPeriod('today')} sx={{ borderRadius: 2 }}>
              Hoje
            </Button>
            <Button size="small" variant="outlined" onClick={() => setPeriod('week')} sx={{ borderRadius: 2 }}>
              7 dias
            </Button>
            <Button size="small" variant="outlined" onClick={() => setPeriod('month')} sx={{ borderRadius: 2 }}>
              Mês atual
            </Button>
          </Stack>

          <Box sx={{ display: { xs: 'none', xl: 'block' }, flex: 1, minWidth: 0 }} />
          {filtersDirty ? <Chip size="small" color="primary" variant="outlined" label="Filtros pendentes" /> : null}
          <Button
            variant="contained"
            onClick={applyFilters}
            disabled={state.loading || !filtersDirty}
            sx={{ width: { xs: '100%', sm: 'auto' }, borderRadius: 2, fontWeight: 800, minWidth: 108 }}
          >
            Aplicar
          </Button>
        </Stack>
      </Paper>
      ) : null}

      {state.error && showOperationalEstimateAlert ? (
        <Alert severity="error" sx={{ mb: 2 }}>
          {state.error}
        </Alert>
      ) : null}

      {!state.error && !state.loading && showOperationalEstimateAlert ? (
        <Alert severity="info" icon={<FactCheckOutlinedIcon />} sx={{ mb: 2 }}>
          Estes valores são uma estimativa operacional calculada a partir dos pedidos. Eles ajudam a acompanhar venda,
          desconto, entrega e status, mas não substituem o extrato oficial do iFood Financial.
        </Alert>
      ) : null}

      {renderActiveTabMetrics()}

      {activeTab === 'resumo' ? (
        <Grid container spacing={2} sx={{ mb: 2 }}>
          <Grid item xs={12} lg={7}>
            <Paper elevation={0} sx={{ p: 2, borderRadius: 2, border: '1px solid', borderColor: 'divider', height: '100%' }}>
              <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 1.5 }}>
                <Typography variant="subtitle1" sx={{ fontWeight: 900 }}>
                  Vendas por canal
                </Typography>
                <Chip size="small" variant="outlined" label={formatCurrency(dashboard.gross)} />
              </Stack>
              {state.loading ? (
                <Stack spacing={1}>
                  <Skeleton height={30} />
                  <Skeleton height={30} />
                  <Skeleton height={30} />
                </Stack>
              ) : (
                <BarList rows={dashboard.channelRows} color="success" />
              )}
            </Paper>
          </Grid>

          <Grid item xs={12} lg={5}>
            <Paper elevation={0} sx={{ p: 2, borderRadius: 2, border: '1px solid', borderColor: 'divider', height: '100%' }}>
              <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 1.5 }}>
                <Typography variant="subtitle1" sx={{ fontWeight: 900 }}>
                  Qualidade dos dados
                </Typography>
                <Chip
                  size="small"
                  color={dashboard.qualityRows.length ? 'warning' : 'success'}
                  variant={dashboard.qualityRows.length ? 'filled' : 'outlined'}
                  label={`${dashboard.qualityRows.length} alerta(s)`}
                />
              </Stack>
              {state.loading ? (
                <Skeleton height={72} />
              ) : (
                <Stack spacing={1.5}>
                  <Stack direction="row" justifyContent="space-between" alignItems="center">
                    <Typography variant="body2" color="text.secondary" sx={{ fontWeight: 700 }}>
                      Registros sem alerta
                    </Typography>
                    <Typography variant="body2" sx={{ fontWeight: 900 }}>
                      {formatPercent(dashboard.qualityScore)}
                    </Typography>
                  </Stack>
                  <LinearProgress
                    variant="determinate"
                    value={Math.round(dashboard.qualityScore * 100)}
                    color={dashboard.qualityScore < 0.85 ? 'warning' : 'success'}
                    sx={{ height: 10, borderRadius: 999 }}
                  />
                  <Typography variant="body2" color="text.secondary">
                    {dashboard.qualityRows.length
                      ? 'Há registros com total, pagamento ou status que precisam de conferência.'
                      : 'Os registros carregados estão completos para a leitura operacional desta tela.'}
                  </Typography>
                </Stack>
              )}
            </Paper>
          </Grid>
        </Grid>
      ) : null}

      {activeTab === 'dre' ? (
        <Grid container spacing={2} sx={{ mb: 2 }}>
          <Grid item xs={12}>
            <Paper elevation={0} sx={{ p: 2, borderRadius: 2, border: '1px solid', borderColor: 'divider' }}>
              <Stack
                direction={{ xs: 'column', md: 'row' }}
                spacing={1.5}
                alignItems={{ xs: 'stretch', md: 'center' }}
                justifyContent="space-between"
              >
                <Box sx={{ minWidth: 0 }}>
                  <Typography variant="subtitle1" sx={{ fontWeight: 900 }}>
                    DRE mensal por competência
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    Período mensal usado para receitas, despesas e resultado gerencial.
                  </Typography>
                </Box>
                <TextField
                  size="small"
                  label="Competência"
                  type="month"
                  value={String(filters.start || '').slice(0, 7)}
                  onChange={(e) => applyCompetenceMonth(e.target.value)}
                  InputLabelProps={{ shrink: true }}
                  sx={{ width: { xs: '100%', sm: 190 } }}
                />
              </Stack>
            </Paper>
          </Grid>

          <Grid item xs={12} lg={7}>
            <SectionCard
              title="DRE gerencial"
              action={
                <Stack direction="row" spacing={1} alignItems="center">
                  {dreState.lastUpdatedAt ? (
                    <Chip size="small" variant="outlined" label={formatDateTime(dreState.lastUpdatedAt)} />
                  ) : null}
                  <Chip size="small" color="info" variant="outlined" label="Estimado" />
                </Stack>
              }
            >
              {dreState.error ? (
                <Alert severity="error" sx={{ mb: 2 }}>
                  {dreState.error}
                </Alert>
              ) : null}

              {!dreState.loading && dreState.data?.manualAccountsScope === 'excluded_by_channel_filter' ? (
                <Alert severity="info" sx={{ mb: 2 }}>
                  Contas manuais ficam fora quando a DRE está filtrada por canal.
                </Alert>
              ) : null}

              {!dreState.loading && toNumber(dreSummary.ignoredManualReceivables) > 0 ? (
                <Alert severity="warning" sx={{ mb: 2 }}>
                  Receitas manuais não marcadas para DRE foram ignoradas para evitar duplicar vendas de pedidos.
                </Alert>
              ) : null}

              {dreState.loading ? (
                <Stack spacing={1}>
                  <Skeleton height={32} />
                  <Skeleton height={32} />
                  <Skeleton height={32} />
                  <Skeleton height={32} />
                  <Skeleton height={32} />
                </Stack>
              ) : dreLines.length ? (
                <Stack spacing={1}>
                  {dreLines.map((line) => {
                    const amount = toNumber(line.amount);
                    const ratio = dreSummary.totalRevenue ? amount / dreSummary.totalRevenue : 0;
                    const emphasized = ['subtotal', 'result'].includes(line.type);
                    return (
                      <Box key={line.key || line.label}>
                        {['totalRevenue', 'cogs', 'contribution', 'netResult'].includes(line.key) ? <Divider sx={{ mb: 1 }} /> : null}
                        <Stack direction="row" spacing={1.5} justifyContent="space-between" alignItems="center">
                          <Stack direction="row" spacing={1} alignItems="center" sx={{ minWidth: 0, flex: 1 }}>
                            {line.type === 'result' ? <AssessmentOutlinedIcon fontSize="small" color="action" /> : null}
                            <Typography
                              variant="body2"
                              sx={{ fontWeight: emphasized ? 900 : 700, lineHeight: 1.25, overflowWrap: 'anywhere' }}
                            >
                              {line.label}
                            </Typography>
                          </Stack>
                          <Stack direction="row" spacing={2} alignItems="center">
                            <Typography
                              variant="body2"
                              color="text.secondary"
                              sx={{ width: 64, textAlign: 'right', ...moneyColumnsSx }}
                            >
                              {line.type === 'deduction' || line.type === 'expense' ? formatPercent(Math.abs(ratio)) : formatPercent(ratio)}
                            </Typography>
                            <Typography
                              variant="body2"
                              sx={{
                                width: 132,
                                textAlign: 'right',
                                fontWeight: emphasized ? 900 : 800,
                                color: amount < 0 ? 'error.main' : line.type === 'result' && amount > 0 ? 'success.main' : 'text.primary',
                                ...moneyColumnsSx,
                              }}
                            >
                              {formatCurrency(amount)}
                            </Typography>
                          </Stack>
                        </Stack>
                      </Box>
                    );
                  })}
                </Stack>
              ) : (
                <EmptyState
                  icon={<AssessmentOutlinedIcon fontSize="large" />}
                  title="Sem dados para a DRE"
                  helper="Sincronize o ledger ou cadastre contas para montar o resultado gerencial."
                />
              )}
            </SectionCard>
          </Grid>

          <Grid item xs={12} lg={5}>
            <Stack spacing={2}>
              <SectionCard
                title="Receita por canal"
                action={<Chip size="small" variant="outlined" label={`${dreSummary.orderCount || 0} pedido(s)`} />}
              >
                {dreState.loading ? (
                  <Skeleton height={92} />
                ) : (
                  <BarList rows={dreRevenueByChannel} color="success" emptyLabel="Sem receita reconhecida por canal" />
                )}
              </SectionCard>

              <SectionCard
                title="Custos e despesas por grupo"
                action={<Chip size="small" variant="outlined" label={`${dreSummary.accountCount || 0} conta(s)`} />}
              >
                {dreState.loading ? (
                  <Skeleton height={92} />
                ) : (
                  <BarList rows={dreExpensesByDreGroup} color="error" emptyLabel="Sem custos ou despesas no período" />
                )}
              </SectionCard>

              <SectionCard
                title="Qualidade da DRE"
                action={
                  <Chip
                    size="small"
                    color={dreQualityScore < 0.85 ? 'warning' : 'success'}
                    variant={dreQualityScore < 0.85 ? 'filled' : 'outlined'}
                    label={formatPercent(dreQualityScore)}
                  />
                }
              >
                <Stack spacing={1.25}>
                  <LinearProgress
                    variant="determinate"
                    value={Math.round(dreQualityScore * 100)}
                    color={dreQualityScore < 0.85 ? 'warning' : 'success'}
                    sx={{ height: 10, borderRadius: 999 }}
                  />
                  <Stack direction="row" spacing={1} sx={{ flexWrap: 'wrap', rowGap: 1 }}>
                    <Chip size="small" variant="outlined" label={`${dreSummary.missingCategoryCount || 0} sem categoria`} />
                    <Chip size="small" variant="outlined" label={`${dreSummary.missingDreGroupCount || 0} sem grupo DRE`} />
                    <Chip size="small" variant="outlined" label={`${formatCurrency(dreSummary.accountsOutsideDre || 0)} fora da DRE`} />
                    <Chip size="small" variant="outlined" label={`${dreSummary.qualityIssueCount || 0} alerta(s) no ledger`} />
                    <Chip size="small" variant="outlined" label={`${dreSummary.includedAccountCount || 0} conta(s) usadas`} />
                    <Chip size="small" icon={<PercentOutlinedIcon />} variant="outlined" label={`Despesa ${formatPercent(dreSummary.expenseRatio)}`} />
                  </Stack>
                </Stack>
              </SectionCard>
            </Stack>
          </Grid>
        </Grid>
      ) : null}

      {activeTab === 'despesas' ? (
        <Box sx={{ mb: 2 }}>
          <Paper elevation={0} sx={{ maxWidth: '100%', overflow: 'hidden', borderRadius: 2, border: '1px solid', borderColor: 'divider', mb: 2 }}>
            <Tabs
              value={expenseSubTab}
              onChange={(_, value) => setExpenseSubTab(value)}
              variant="scrollable"
              scrollButtons="auto"
              allowScrollButtonsMobile
              sx={financeTabsSx}
            >
              <Tab value="lancamentos" icon={<ReceiptLongIcon />} iconPosition="start" label="Lançamentos" />
              <Tab value="categorias" icon={<CategoryOutlinedIcon />} iconPosition="start" label="Categorias" />
              <Tab value="fornecedores" icon={<StorefrontOutlinedIcon />} iconPosition="start" label="Fornecedores" />
              <Tab value="centros" icon={<BalanceOutlinedIcon />} iconPosition="start" label="Centros de custo" />
            </Tabs>
          </Paper>
          {accountsState.error ? (
            <Alert severity="warning" sx={{ mb: 2 }}>
              {accountsState.error}
            </Alert>
          ) : null}
          {renderExpenseSubTabContent()}
        </Box>
      ) : null}

      {activeTab === 'gerencial' ? (
        <Box sx={{ mb: 2 }}>
          <Box
            sx={{
              display: 'grid',
              gridTemplateColumns: { xs: '1fr', lg: 'minmax(0, 2fr) minmax(320px, 1fr)' },
              gap: 2,
              alignItems: 'start',
            }}
          >
          <Box sx={{ minWidth: 0 }}>
            <SectionCard
              title="Centros de custo"
              action={
                <Stack direction="row" spacing={1} alignItems="center">
                  {managementState.lastUpdatedAt ? (
                    <Chip size="small" variant="outlined" label={formatDateTime(managementState.lastUpdatedAt)} />
                  ) : null}
                  <Chip size="small" variant="outlined" label={`${managementCostCenterRows.length} centro(s)`} />
                </Stack>
              }
            >
              {managementState.error ? (
                <Alert severity="error" sx={{ mb: 2 }}>
                  {managementState.error}
                </Alert>
              ) : null}

              {managementState.loading ? (
                <Stack spacing={1}>
                  <Skeleton height={34} />
                  <Skeleton height={34} />
                  <Skeleton height={34} />
                  <Skeleton height={34} />
                </Stack>
              ) : managementCostCenterRows.length ? (
                <TableContainer sx={{ maxHeight: '52vh' }}>
                  <Table stickyHeader size="small" sx={{ minWidth: 720 }}>
                    <TableHead>
                      <TableRow>
                        <TableCell>Centro</TableCell>
                        <TableCell>Código</TableCell>
                        <TableCell align="right">Despesas</TableCell>
                        <TableCell align="right">Contas</TableCell>
                        <TableCell>Status</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {managementCostCenterRows.map((center) => {
                        const centerColor = center.color || '#1976d2';
                        const uncategorized = center.label === 'Sem centro de custo';
                        return (
                          <TableRow key={center.id || center.label} hover>
                            <TableCell>
                              <Stack direction="row" spacing={1} alignItems="center" sx={{ minWidth: 0 }}>
                                <Box
                                  sx={{
                                    width: 12,
                                    height: 12,
                                    borderRadius: 1,
                                    backgroundColor: centerColor,
                                    flex: '0 0 auto',
                                  }}
                                />
                                <Typography variant="body2" sx={{ fontWeight: 900 }} noWrap>
                                  {center.label}
                                </Typography>
                              </Stack>
                            </TableCell>
                            <TableCell>
                              {center.code ? (
                                <Chip size="small" label={center.code} variant="outlined" sx={{ borderRadius: 1.5 }} />
                              ) : (
                                '-'
                              )}
                            </TableCell>
                            <TableCell align="right" sx={{ fontWeight: 900, ...moneyColumnsSx }}>
                              {formatCurrency(center.total)}
                            </TableCell>
                            <TableCell align="right" sx={moneyColumnsSx}>
                              {center.count || 0}
                            </TableCell>
                            <TableCell>
                              <Chip
                                size="small"
                                color={center.isActive === false ? 'default' : uncategorized ? 'warning' : 'success'}
                                variant={center.isActive === false || uncategorized ? 'outlined' : 'filled'}
                                label={center.isActive === false ? 'Inativo' : uncategorized ? 'Sem cadastro' : 'Ativo'}
                                sx={{ borderRadius: 2, fontWeight: 800 }}
                              />
                            </TableCell>
                          </TableRow>
                        );
                      })}
                    </TableBody>
                  </Table>
                </TableContainer>
              ) : (
                <EmptyState
                  icon={<BalanceOutlinedIcon fontSize="large" />}
                  title="Nenhum centro de custo"
                  helper="Cadastre centros e vincule contas para acompanhar despesas por área."
                />
              )}
            </SectionCard>
          </Box>

          <Box sx={{ minWidth: 0 }}>
            <Stack spacing={2}>
              <SectionCard
                title="Metas do período"
                action={<Chip size="small" color="info" variant="outlined" label={managementState.data?.channel === 'all' ? 'Todos canais' : normalizeChannelLabel(managementState.data?.channel)} />}
              >
                <Stack spacing={1.25}>
                  {managementState.loading ? (
                    <Stack spacing={1}>
                      <Skeleton height={42} />
                      <Skeleton height={42} />
                      <Skeleton height={42} />
                    </Stack>
                  ) : (
                    managementGoals.map((goal) => {
                      const progressPercent = Math.round(goal.progress * 100);
                      const goalColor = !goal.hasTarget ? 'primary' : goal.achieved ? 'success' : 'warning';
                      return (
                        <Box
                          key={goal.metric}
                          sx={(theme) => ({
                            p: 1,
                            borderRadius: 2,
                            border: '1px solid',
                            borderColor: 'divider',
                            backgroundColor: alpha(theme.palette.text.primary, 0.025),
                          })}
                        >
                          <Stack direction="row" spacing={1} alignItems="center" justifyContent="space-between" sx={{ mb: 0.75 }}>
                            <Box sx={{ minWidth: 0 }}>
                              <Typography variant="body2" sx={{ fontWeight: 900 }} noWrap>
                                {goal.label}
                              </Typography>
                              <Typography variant="caption" color="text.secondary" sx={moneyColumnsSx}>
                                Atual: {formatGoalValue(goal.actualValue, goal.format)}
                              </Typography>
                            </Box>
                            <Chip
                              size="small"
                              color={!goal.hasTarget ? 'default' : goal.achieved ? 'success' : 'warning'}
                              variant={!goal.hasTarget || !goal.achieved ? 'outlined' : 'filled'}
                              label={!goal.hasTarget ? 'Sem meta' : goal.achieved ? 'No alvo' : 'Ajustar'}
                              sx={{ borderRadius: 2, fontWeight: 800 }}
                            />
                          </Stack>
                          <Stack direction="row" spacing={1} alignItems="center">
                            <TextField
                              size="small"
                              label={goal.format === 'percent' ? 'Meta %' : 'Meta R$'}
                              value={managementForm.goals[goal.metric] ?? ''}
                              onChange={(e) =>
                                setManagementForm((prev) => ({
                                  ...prev,
                                  goals: {
                                    ...prev.goals,
                                    [goal.metric]: e.target.value,
                                  },
                                }))
                              }
                              inputProps={{ inputMode: 'decimal', style: { textAlign: 'right' } }}
                              sx={{ width: 118 }}
                            />
                            <Box sx={{ flex: 1 }}>
                              <LinearProgress
                                variant="determinate"
                                value={goal.hasTarget ? progressPercent : 0}
                                color={goalColor}
                                sx={{ height: 10, borderRadius: 999 }}
                              />
                            </Box>
                          </Stack>
                        </Box>
                      );
                    })
                  )}

                  <Button
                    variant="contained"
                    startIcon={<CheckCircleOutlineOutlinedIcon />}
                    onClick={saveManagementGoals}
                    disabled={managementState.loading || managementState.saving}
                    sx={{ borderRadius: 2, fontWeight: 800 }}
                  >
                    Salvar metas
                  </Button>
                </Stack>
              </SectionCard>

              <SectionCard title="Desvios relevantes">
                {managementState.loading ? (
                  <Skeleton height={92} />
                ) : managementDeviationRows.length ? (
                  <Stack spacing={1}>
                    {managementDeviationRows.map((goal) => (
                      <Stack key={goal.metric} direction="row" spacing={1} alignItems="center" justifyContent="space-between">
                        <Box sx={{ minWidth: 0 }}>
                          <Typography variant="body2" sx={{ fontWeight: 900 }} noWrap>
                            {goal.label}
                          </Typography>
                          <Typography variant="caption" color="text.secondary">
                            {goal.comparison === 'maximum' ? 'Acima do limite' : 'Abaixo da meta'}
                          </Typography>
                        </Box>
                        <Stack direction="row" spacing={1} alignItems="center">
                          <Chip
                            size="small"
                            color="warning"
                            variant="outlined"
                            label={goal.comparison === 'maximum' ? 'Acima' : 'Abaixo'}
                            sx={{ borderRadius: 2 }}
                          />
                          <Typography variant="body2" sx={{ fontWeight: 900, ...moneyColumnsSx }}>
                            {formatGoalValue(Math.abs(goal.variance), goal.format)}
                          </Typography>
                        </Stack>
                      </Stack>
                    ))}
                  </Stack>
                ) : (
                  <BarList rows={[]} color="success" emptyLabel="Sem desvios nas metas configuradas" />
                )}
              </SectionCard>

              <SectionCard title="Novo centro de custo" action={<AddCircleOutlineOutlinedIcon color="action" />}>
                <Stack spacing={1.25}>
                  {accountsState.error ? (
                    <Alert severity="warning">
                      {accountsState.error}
                    </Alert>
                  ) : null}
                  <TextField
                    size="small"
                    label="Nome"
                    value={costCenterForm.name}
                    onChange={(e) => setCostCenterForm((prev) => ({ ...prev, name: e.target.value }))}
                  />
                  <Stack direction="row" spacing={1}>
                    <TextField
                      size="small"
                      label="Código"
                      value={costCenterForm.code}
                      onChange={(e) => setCostCenterForm((prev) => ({ ...prev, code: e.target.value }))}
                      sx={{ flex: 1 }}
                    />
                    <TextField
                      size="small"
                      label="Cor"
                      type="color"
                      value={costCenterForm.color}
                      onChange={(e) => setCostCenterForm((prev) => ({ ...prev, color: e.target.value }))}
                      InputLabelProps={{ shrink: true }}
                      sx={{ width: 92 }}
                    />
                  </Stack>
                  <Button
                    size="small"
                    variant="outlined"
                    startIcon={<AddCircleOutlineOutlinedIcon />}
                    onClick={saveCostCenter}
                    disabled={accountsState.saving || !costCenterForm.name.trim()}
                    sx={{ borderRadius: 2, fontWeight: 800 }}
                  >
                    Salvar centro
                  </Button>
                </Stack>
              </SectionCard>
            </Stack>
          </Box>
          </Box>
        </Box>
      ) : null}

      {activeTab === 'pagamentos' ? (
        <Grid container spacing={2} sx={{ mb: 2 }}>
          <Grid item xs={12} md={8}>
            <Paper elevation={0} sx={{ p: 2, borderRadius: 2, border: '1px solid', borderColor: 'divider', height: '100%' }}>
              <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 1.5 }}>
                <Typography variant="subtitle1" sx={{ fontWeight: 900 }}>
                  Pagamento por método
                </Typography>
                <Chip size="small" variant="outlined" label={`${dashboard.paymentRows.length} método(s)`} />
              </Stack>
              {state.loading ? (
                <Stack spacing={1}>
                  <Skeleton height={30} />
                  <Skeleton height={30} />
                  <Skeleton height={30} />
                </Stack>
              ) : (
                <BarList rows={dashboard.paymentRows} color="primary" />
              )}
            </Paper>
          </Grid>

          <Grid item xs={12} md={4}>
            <Paper elevation={0} sx={{ p: 2, borderRadius: 2, border: '1px solid', borderColor: 'divider', height: '100%' }}>
              <Typography variant="subtitle1" sx={{ fontWeight: 900, mb: 1.5 }}>
                Conferência rápida
              </Typography>
              <Stack spacing={1}>
                {dashboard.paymentRows.slice(0, 5).map((row) => (
                  <Stack key={row.label} direction="row" justifyContent="space-between" alignItems="center">
                    <Typography variant="body2" sx={{ fontWeight: 700 }}>
                      {row.label}
                    </Typography>
                    <Typography variant="body2" sx={{ fontWeight: 900, ...moneyColumnsSx }}>
                      {formatCurrency(row.total)}
                    </Typography>
                  </Stack>
                ))}
                {!state.loading && dashboard.paymentRows.length === 0 ? (
                  <Typography variant="body2" color="text.secondary">
                    Nenhum pagamento no período.
                  </Typography>
                ) : null}
              </Stack>
            </Paper>
          </Grid>
        </Grid>
      ) : null}

      {activeTab === 'conciliacao' ? (
        <Grid container spacing={2} sx={{ mb: 2 }}>
          <Grid item xs={12} lg={8}>
            <SectionCard
              title="Conciliação por forma de pagamento"
              action={
                <Stack direction="row" spacing={1} alignItems="center">
                  {reconciliationState.lastUpdatedAt ? (
                    <Chip size="small" variant="outlined" label={formatDateTime(reconciliationState.lastUpdatedAt)} />
                  ) : null}
                  <Tooltip title="Atualizar conciliação">
                    <span>
                      <IconButton
                        size="small"
                        onClick={loadReconciliation}
                        disabled={reconciliationState.loading || reconciliationState.saving}
                      >
                        <RefreshIcon fontSize="small" />
                      </IconButton>
                    </span>
                  </Tooltip>
                </Stack>
              }
            >
              {reconciliationState.error ? (
                <Alert severity="error" sx={{ mb: 2 }}>
                  {reconciliationState.error}
                </Alert>
              ) : null}

              {reconciliationState.loading ? (
                <Stack spacing={1}>
                  <Skeleton height={34} />
                  <Skeleton height={34} />
                  <Skeleton height={34} />
                </Stack>
              ) : reconciliationRows.length ? (
                <TableContainer>
                  <Table size="small" stickyHeader sx={{ minWidth: 940 }}>
                    <TableHead>
                      <TableRow>
                        <TableCell>Forma</TableCell>
                        <TableCell align="right">Esperado</TableCell>
                        <TableCell align="right">Conferido</TableCell>
                        <TableCell align="right">Divergência</TableCell>
                        <TableCell align="right">Part.</TableCell>
                        <TableCell>Status</TableCell>
                        <TableCell align="right">Ações</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {reconciliationRows.map((row) => (
                        <TableRow key={row.key} hover>
                          <TableCell>
                            <Stack direction="row" spacing={1} alignItems="center">
                              <Chip size="small" label={row.label} variant="outlined" sx={{ borderRadius: 2 }} />
                              <Typography variant="caption" color="text.secondary">
                                {row.entryCount || 0}
                              </Typography>
                            </Stack>
                          </TableCell>
                          <TableCell align="right" sx={{ fontWeight: 900, ...moneyColumnsSx }}>
                            {formatCurrency(row.expectedAmount)}
                          </TableCell>
                          <TableCell align="right">
                            <TextField
                              size="small"
                              value={reconciliationForm.items[row.key]?.reportedAmount ?? ''}
                              onChange={(e) => setReconciliationForm((prev) => ({
                                ...prev,
                                items: {
                                  ...prev.items,
                                  [row.key]: {
                                    ...(prev.items[row.key] || {}),
                                    reportedAmount: e.target.value,
                                  },
                                },
                              }))}
                              inputProps={{ inputMode: 'decimal', style: { textAlign: 'right' } }}
                              sx={{ width: 126 }}
                            />
                          </TableCell>
                          <TableCell
                            align="right"
                            sx={{
                              fontWeight: 900,
                              color: Math.abs(row.differenceAmount) > 0.05 ? 'warning.main' : 'success.main',
                              ...moneyColumnsSx,
                            }}
                          >
                            {formatCurrency(row.differenceAmount)}
                          </TableCell>
                          <TableCell align="right" sx={moneyColumnsSx}>
                            {formatPercent(row.participation)}
                          </TableCell>
                          <TableCell>
                            <Chip
                              size="small"
                              color={getReconciliationStatusColor(row.status)}
                              variant={row.status === 'reconciled' ? 'filled' : 'outlined'}
                              label={getReconciliationStatusLabel(row.status)}
                              sx={{ borderRadius: 2 }}
                            />
                          </TableCell>
                          <TableCell align="right">
                            <Button
                              size="small"
                              variant="text"
                              onClick={() => setReconciliationForm((prev) => ({
                                ...prev,
                                items: {
                                  ...prev.items,
                                  [row.key]: {
                                    ...(prev.items[row.key] || {}),
                                    reportedAmount: String(row.expectedAmount),
                                  },
                                },
                              }))}
                              sx={{ fontWeight: 800 }}
                            >
                              Usar esperado
                            </Button>
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
              ) : (
                <EmptyState
                  icon={<CheckCircleOutlineOutlinedIcon fontSize="large" />}
                  title="Sem pagamentos reconhecidos"
                  helper="Sincronize o ledger para montar a conciliação."
                />
              )}
            </SectionCard>
          </Grid>

          <Grid item xs={12} lg={4}>
            <Stack spacing={2}>
              <SectionCard
                title="Resumo da conciliação"
                action={
                  <Chip
                    size="small"
                    color={getReconciliationStatusColor(reconciliationState.data?.status)}
                    variant="outlined"
                    label={getReconciliationStatusLabel(reconciliationState.data?.status)}
                  />
                }
              >
                <Stack spacing={1.25}>
                  <Stack direction="row" spacing={1} sx={{ flexWrap: 'wrap', rowGap: 1 }}>
                    <Chip size="small" variant="outlined" label={`${reconciliationSummary.methodCount} método(s)`} />
                    <Chip size="small" variant="outlined" label={`${reconciliationSummary.entryCount} venda(s)`} />
                    <Chip size="small" variant="outlined" label={`${reconciliationSummary.divergentCount} divergente(s)`} />
                  </Stack>
                  <TextField
                    label="Observações"
                    size="small"
                    fullWidth
                    multiline
                    minRows={3}
                    value={reconciliationForm.notes}
                    onChange={(e) => setReconciliationForm((prev) => ({ ...prev, notes: e.target.value }))}
                  />
                  <Button
                    variant="contained"
                    startIcon={<CheckCircleOutlineOutlinedIcon />}
                    onClick={saveReconciliation}
                    disabled={reconciliationState.saving || reconciliationState.loading || !reconciliationRows.length}
                    sx={{ borderRadius: 2, fontWeight: 800 }}
                  >
                    Salvar conciliação
                  </Button>
                </Stack>
              </SectionCard>

              <SectionCard title="Divergências">
                {reconciliationState.loading ? (
                  <Skeleton height={92} />
                ) : (
                  <BarList rows={reconciliationDifferenceRows} color="warning" emptyLabel="Sem divergências no período" />
                )}
              </SectionCard>
            </Stack>
          </Grid>
        </Grid>
      ) : null}

      {activeTab === 'ifoodFinancial' && canViewIfoodFinancial ? (
        <Grid container spacing={2} sx={{ mb: 2 }}>
          <Grid item xs={12}>
            <Alert severity="info" icon={<InfoOutlinedIcon />}>
              Modo de homologação Financial com dados oficiais do iFood e x-request-homologation: true. Sales e
              Reconciliation usam agosto/2025; Financial Events e Settlements usam exatamente 01/08/2025 a
              03/08/2025, conforme o questionário.
            </Alert>
          </Grid>

          <Grid item xs={12} lg={4}>
            <Stack spacing={2}>
              <SectionCard
                title="Conexão com iFood oficial"
                action={
                  <Chip
                    size="small"
                    color={ifoodFinancialState.context?.allowed ? 'success' : 'warning'}
                    variant={ifoodFinancialState.context?.allowed ? 'filled' : 'outlined'}
                    label={ifoodFinancialState.context?.allowed ? 'Habilitado' : 'Pendente'}
                  />
                }
              >
                {ifoodFinancialState.error ? (
                  <Alert severity="error" sx={{ mb: 2 }}>
                    {ifoodFinancialState.error}
                  </Alert>
                ) : null}

                {ifoodFinancialState.contextLoading ? (
                  <Stack spacing={1}>
                    <Skeleton height={38} />
                    <Skeleton height={38} />
                    <Skeleton height={38} />
                  </Stack>
                ) : (
                  <Stack spacing={1.5}>
                    {!ifoodFinancialState.context?.allowed ? (
                      <Alert severity="warning">
                        {ifoodFinancialState.context?.reason || 'Credenciais ou merchants ainda não liberados para este tenant.'}
                      </Alert>
                    ) : null}

                    <FormControl size="small" fullWidth>
                      <InputLabel id="ifood-financial-merchant">Loja iFood</InputLabel>
                      <Select
                        labelId="ifood-financial-merchant"
                        label="Loja iFood"
                        value={ifoodFinancialMerchantId}
                        onChange={(event) => setIfoodFinancialMerchantId(event.target.value)}
                      >
                        {ifoodFinancialMerchants.map((merchant) => (
                          <MenuItem key={merchant.merchantId || merchant.id} value={merchant.merchantId || merchant.id}>
                            {merchant.label || compactIdentifier(merchant.merchantId || merchant.id)}
                          </MenuItem>
                        ))}
                      </Select>
                    </FormControl>

                    <Stack direction="row" spacing={1} sx={{ flexWrap: 'wrap', rowGap: 1 }}>
                      <Chip
                        size="small"
                        variant="outlined"
                        label={ifoodFinancialState.context?.credentials?.hasClientId ? 'Client ID OK' : 'Client ID ausente'}
                      />
                      <Chip
                        size="small"
                        variant="outlined"
                        label={ifoodFinancialState.context?.credentials?.hasClientSecret ? 'Secret OK' : 'Secret ausente'}
                      />
                      <Chip
                        size="small"
                        color={ifoodFinancialState.context?.tokenInfo?.isValid ? 'success' : 'default'}
                        variant="outlined"
                        label={ifoodFinancialState.context?.tokenInfo?.isValid ? 'Token válido' : 'Token não carregado'}
                      />
                      <Tooltip title="Períodos fixos do questionário de homologação iFood Financial, sem mistura com filtros operacionais.">
                        <Chip
                          size="small"
                          color="primary"
                          variant="filled"
                          label={`Homologação ${IFOOD_FINANCIAL_HOMOLOGATION_LABEL}`}
                        />
                      </Tooltip>
                      {ifoodFinancialState.livePreview ? (
                        <Chip
                          size="small"
                          color="info"
                          variant="filled"
                          label="Consulta ao vivo"
                        />
                      ) : null}
                    </Stack>

                    <Stack direction="row" spacing={1} sx={{ flexWrap: 'wrap', rowGap: 1 }}>
                      <Button
                        variant="contained"
                        startIcon={<CloudSyncOutlinedIcon />}
                        onClick={syncIfoodFinancial}
                        disabled={!ifoodFinancialMerchantId || ifoodFinancialState.syncing || ifoodFinancialState.loading}
                        sx={{ borderRadius: 2, fontWeight: 800 }}
                      >
                        {ifoodFinancialState.syncing ? 'Sincronizando' : 'Sincronizar'}
                      </Button>
                      <Button
                        variant="outlined"
                        startIcon={<RefreshIcon />}
                        onClick={loadIfoodFinancialData}
                        disabled={!ifoodFinancialMerchantId || ifoodFinancialState.loading || ifoodFinancialState.syncing}
                        sx={{ borderRadius: 2, fontWeight: 800 }}
                      >
                        Atualizar
                      </Button>
                    </Stack>

                    {ifoodFinancialLatestSync ? (
                      <Alert severity={ifoodFinancialLatestSync.status === 'success' ? 'success' : 'warning'}>
                        Última busca oficial concluída em: {formatDateTime(ifoodFinancialLatestSync.finishedAt || ifoodFinancialLatestSync.startedAt)}
                      </Alert>
                    ) : null}

                    {ifoodFinancialState.syncResult?.errors?.length ? (
                      <Alert severity="warning">
                        {ifoodFinancialState.syncResult.errors.length} módulo(s) retornaram alerta na última sincronização.
                      </Alert>
                    ) : null}

                    {ifoodFinancialState.liveErrors?.length ? (
                      <Alert severity="info">
                        {ifoodFinancialState.liveErrors.length} módulo(s) da consulta ao vivo retornaram sem dados ou alerta.
                      </Alert>
                    ) : null}
                  </Stack>
                )}
              </SectionCard>

              <SectionCard
                title="Cobertura Financial"
                action={<Chip size="small" variant="outlined" label={`${ifoodFinancialTotals.records || 0} registro(s)`} />}
              >
                {ifoodFinancialState.loading || ifoodFinancialState.syncing ? (
                  <Skeleton height={118} />
                ) : (
                  <BarList
                    rows={ifoodFinancialTypeRows}
                    color="primary"
                    valueFormatter={(value) => `${value} registro(s)`}
                    emptyLabel="Sem registros Financial sincronizados"
                  />
                )}
              </SectionCard>

              <SectionCard title="Status dos lançamentos" action={<AccountBalanceOutlinedIcon color="action" />}>
                {ifoodFinancialState.loading || ifoodFinancialState.syncing ? (
                  <Skeleton height={118} />
                ) : (
                  <BarList
                    rows={ifoodFinancialStatusRows}
                    color="warning"
                    valueFormatter={(value) => `${value} registro(s)`}
                    emptyLabel="Sem status para o período"
                  />
                )}
              </SectionCard>
            </Stack>
          </Grid>

          <Grid item xs={12} lg={8}>
            <SectionCard
              title="Auditoria técnica · extrato oficial iFood"
              action={
                <Stack direction="row" spacing={1} alignItems="center" sx={{ flexWrap: 'wrap', rowGap: 1 }}>
                  {ifoodFinancialState.lastUpdatedAt ? (
                    <Chip size="small" variant="outlined" label={formatDateTime(ifoodFinancialState.lastUpdatedAt)} />
                  ) : null}
                  {ifoodFinancialState.livePreview ? (
                    <Chip size="small" color="info" variant="outlined" label="Dados ao vivo" />
                  ) : null}
                  <TextField
                    size="small"
                    label="Pedido"
                    value={ifoodFinancialOrderSearch}
                    onChange={(event) => setIfoodFinancialOrderSearch(event.target.value)}
                    onKeyDown={(event) => {
                      if (event.key === 'Enter') {
                        loadIfoodFinancialOrderDetails(ifoodFinancialOrderSearch);
                      }
                    }}
                    sx={{ width: 150 }}
                  />
                  <Button
                    variant="outlined"
                    size="small"
                    startIcon={<SearchOutlinedIcon />}
                    disabled={!ifoodFinancialMerchantId || ifoodFinancialOrderDetailState.loading || !ifoodFinancialOrderSearch.trim()}
                    onClick={() => loadIfoodFinancialOrderDetails(ifoodFinancialOrderSearch)}
                    sx={{ borderRadius: 2, fontWeight: 850 }}
                  >
                    Buscar
                  </Button>
                  <FormControl size="small" sx={{ width: 174 }}>
                    <InputLabel id="ifood-financial-record-type">Tipo</InputLabel>
                    <Select
                      labelId="ifood-financial-record-type"
                      label="Tipo"
                      value={ifoodFinancialRecordType}
                      onChange={(event) => setIfoodFinancialRecordType(event.target.value)}
                    >
                      {IFOOD_FINANCIAL_RECORD_TYPES.map((option) => (
                        <MenuItem key={option.key} value={option.key}>
                          {option.label}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </Stack>
              }
            >
              <Grid container spacing={2}>
                <Grid item xs={12} md={hasIfoodFinancialOrderDetail ? 7 : 12}>
                  {ifoodFinancialState.loading || ifoodFinancialState.syncing ? (
                    <Stack spacing={1}>
                      <Skeleton height={34} />
                      <Skeleton height={34} />
                      <Skeleton height={34} />
                      <Skeleton height={34} />
                    </Stack>
                  ) : ifoodFinancialState.records.length ? (
                    <TableContainer sx={{ maxHeight: '58vh' }}>
                      <Table stickyHeader size="small" sx={{ minWidth: 980 }}>
                        <TableHead>
                          <TableRow>
                            <TableCell>Data</TableCell>
                            <TableCell>Tipo de lançamento</TableCell>
                            <TableCell>Descrição</TableCell>
                            <TableCell>Status</TableCell>
                            <TableCell align="right">Valor lançado</TableCell>
                            <TableCell align="right">Impacto no repasse</TableCell>
                            <TableCell>Pedido</TableCell>
                            <TableCell>Pagamento</TableCell>
                          </TableRow>
                        </TableHead>
                        <TableBody>
                          {ifoodFinancialState.records.map((record) => {
                            const details = record.financialDetails || {};
                            const orderKey = getIfoodFinancialOrderKey(record);
                            const selected = ifoodFinancialOrderDetailState.selectedExternalId === (record.externalId || record.id);
                            const hasTransferImpact = getIfoodFinancialHasTransferImpact(record);
                            return (
                              <TableRow
                                key={record.id || record.externalId}
                                hover
                                selected={selected}
                                onClick={() => {
                                  setIfoodFinancialOrderSearch(orderKey);
                                  loadIfoodFinancialOrderDetails(orderKey, record);
                                }}
                                sx={{ cursor: 'pointer' }}
                              >
                                <TableCell sx={{ whiteSpace: 'nowrap' }}>{formatDateOnly(record.recordDate)}</TableCell>
                                <TableCell>
                                  <Chip
                                    size="small"
                                    color={getIfoodFinancialRecordTypeColor(record.recordType)}
                                    variant="outlined"
                                    label={getIfoodFinancialRecordTypeLabel(record.recordType)}
                                    sx={{ borderRadius: 2, fontWeight: 800 }}
                                  />
                                </TableCell>
                                <TableCell sx={{ minWidth: 190 }}>
                                  <Typography variant="body2" sx={{ fontWeight: 800 }}>
                                    {getIfoodFinancialDescription(record)}
                                  </Typography>
                                  <Typography variant="caption" color="text.secondary">
                                    {compactIdentifier(record.externalId)}
                                  </Typography>
                                </TableCell>
                                <TableCell>
                                  <Chip size="small" variant="outlined" label={getIfoodFinancialStatus(record)} sx={{ borderRadius: 2 }} />
                                </TableCell>
                                <TableCell align="right" sx={{ fontWeight: 900, ...moneyColumnsSx }}>
                                  {formatCurrency(getIfoodFinancialRecordAmount(record))}
                                </TableCell>
                                <TableCell
                                  align="right"
                                  sx={{
                                    fontWeight: 900,
                                    color: hasTransferImpact ? 'success.main' : 'text.secondary',
                                    ...moneyColumnsSx,
                                  }}
                                >
                                  {formatCurrency(getIfoodFinancialImpactValue(record))}
                                </TableCell>
                                <TableCell sx={{ whiteSpace: 'nowrap' }}>{compactIdentifier(orderKey)}</TableCell>
                                <TableCell>{details.payments?.methods?.[0]?.method || '-'}</TableCell>
                              </TableRow>
                            );
                          })}
                        </TableBody>
                      </Table>
                    </TableContainer>
                  ) : (
                    <EmptyState
                      icon={<CloudSyncOutlinedIcon fontSize="large" />}
                      title="Sem lançamentos oficiais"
                      helper="A API oficial do iFood ainda não retornou vendas, ajustes ou repasses nos períodos fixos da homologação. Atualize ou sincronize novamente."
                    />
                  )}
                </Grid>

                {hasIfoodFinancialOrderDetail ? (
                  <Grid item xs={12} md={5}>
                    <IfoodFinancialOrderDetailPanel state={ifoodFinancialOrderDetailState} />
                  </Grid>
                ) : null}
              </Grid>
            </SectionCard>
          </Grid>
        </Grid>
      ) : null}

      {activeTab === 'operacao' ? (
        <Grid container spacing={2} sx={{ mb: 2 }}>
          <Grid item xs={12} md={7}>
            <Paper elevation={0} sx={{ p: 2, borderRadius: 2, border: '1px solid', borderColor: 'divider', height: '100%' }}>
              <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 1.5 }}>
                <Typography variant="subtitle1" sx={{ fontWeight: 900 }}>
                  Entrega x retirada
                </Typography>
                <Chip size="small" variant="outlined" label={`${dashboard.fulfillmentRows.length} tipo(s)`} />
              </Stack>
              {state.loading ? (
                <Stack spacing={1}>
                  <Skeleton height={30} />
                  <Skeleton height={30} />
                </Stack>
              ) : (
                <BarList rows={dashboard.fulfillmentRows} color="info" />
              )}
            </Paper>
          </Grid>

          <Grid item xs={12} md={5}>
            <Paper elevation={0} sx={{ p: 2, borderRadius: 2, border: '1px solid', borderColor: 'divider', height: '100%' }}>
              <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 1.5 }}>
                <ErrorOutlineOutlinedIcon color={dashboard.canceledRows.length ? 'error' : 'disabled'} />
                <Typography variant="subtitle1" sx={{ fontWeight: 900 }}>
                  Cancelamentos e ocorrências
                </Typography>
              </Stack>
              {state.loading ? (
                <Skeleton height={74} />
              ) : (
                <Stack spacing={1}>
                  <Stack direction="row" justifyContent="space-between">
                    <Typography variant="body2" color="text.secondary" sx={{ fontWeight: 700 }}>
                      Pedidos cancelados
                    </Typography>
                    <Typography variant="body2" sx={{ fontWeight: 900 }}>
                      {dashboard.canceledRows.length}
                    </Typography>
                  </Stack>
                  <Stack direction="row" justifyContent="space-between">
                    <Typography variant="body2" color="text.secondary" sx={{ fontWeight: 700 }}>
                      Valor associado
                    </Typography>
                    <Typography variant="body2" sx={{ fontWeight: 900, ...moneyColumnsSx }}>
                      {formatCurrency(dashboard.canceledTotal)}
                    </Typography>
                  </Stack>
                  <Divider />
                  <Typography variant="body2" color="text.secondary">
                    Cancelados ficam separados do total vendido nesta tela.
                  </Typography>
                </Stack>
              )}
            </Paper>
          </Grid>
        </Grid>
      ) : null}

      {activeTab === 'dados' ? (
        <Grid container spacing={2} sx={{ mb: 2 }}>
          <Grid item xs={12} md={7}>
            <Paper elevation={0} sx={{ p: 2, borderRadius: 2, border: '1px solid', borderColor: 'divider', height: '100%' }}>
              <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 1.5 }}>
                <Typography variant="subtitle1" sx={{ fontWeight: 900 }}>
                  Alertas por tipo
                </Typography>
                <Chip size="small" variant="outlined" label={`${qualityCountRows.length} tipo(s)`} />
              </Stack>
              {state.loading ? (
                <Stack spacing={1}>
                  <Skeleton height={30} />
                  <Skeleton height={30} />
                </Stack>
              ) : (
                <BarList
                  rows={qualityCountRows}
                  color="warning"
                  valueFormatter={(value) => `${value} registro(s)`}
                  emptyLabel="Sem alertas de qualidade no período"
                />
              )}
            </Paper>
          </Grid>

          <Grid item xs={12} md={5}>
            <Paper elevation={0} sx={{ p: 2, borderRadius: 2, border: '1px solid', borderColor: 'divider', height: '100%' }}>
              <Typography variant="subtitle1" sx={{ fontWeight: 900, mb: 1.5 }}>
                Leitura do período
              </Typography>
              <Stack spacing={1}>
                <Stack direction="row" justifyContent="space-between">
                  <Typography variant="body2" color="text.secondary" sx={{ fontWeight: 700 }}>
                    Registros carregados
                  </Typography>
                  <Typography variant="body2" sx={{ fontWeight: 900 }}>
                    {enrichedMovements.length}
                  </Typography>
                </Stack>
                <Stack direction="row" justifyContent="space-between">
                  <Typography variant="body2" color="text.secondary" sx={{ fontWeight: 700 }}>
                    Sem alerta
                  </Typography>
                  <Typography variant="body2" sx={{ fontWeight: 900 }}>
                    {enrichedMovements.length - dashboard.qualityRows.length}
                  </Typography>
                </Stack>
                <Stack direction="row" justifyContent="space-between">
                  <Typography variant="body2" color="text.secondary" sx={{ fontWeight: 700 }}>
                    Com alerta
                  </Typography>
                  <Typography variant="body2" sx={{ fontWeight: 900 }}>
                    {dashboard.qualityRows.length}
                  </Typography>
                </Stack>
              </Stack>
            </Paper>
          </Grid>
        </Grid>
      ) : null}

      {activeTab === 'ledger' ? (
        <Grid container spacing={2} sx={{ mb: 2 }}>
          <Grid item xs={12} md={4}>
            <CompactStat label="Entradas" value={formatCurrency(ledgerSummary.credit)} color="success" />
          </Grid>
          <Grid item xs={12} md={4}>
            <CompactStat label="Saídas" value={formatCurrency(ledgerSummary.debit)} color="error" />
          </Grid>
          <Grid item xs={12} md={4}>
            <CompactStat label="Saldo" value={formatCurrency(ledgerSummary.balance)} color="primary" />
          </Grid>

          <Grid item xs={12}>
            <SectionCard
              title="Ledger financeiro"
              action={
                <Stack direction="row" spacing={1} alignItems="center">
                  {ledgerState.lastUpdatedAt ? (
                    <Chip size="small" variant="outlined" label={formatDateTime(ledgerState.lastUpdatedAt)} />
                  ) : null}
                  <Button
                    size="small"
                    variant="outlined"
                    startIcon={<SyncOutlinedIcon />}
                    onClick={syncLedger}
                    disabled={ledgerState.loading || ledgerState.syncing}
                    sx={{ borderRadius: 2, fontWeight: 800 }}
                  >
                    Sincronizar
                  </Button>
                </Stack>
              }
            >
              {ledgerState.error ? (
                <Alert severity="error" sx={{ mb: 2 }}>
                  {ledgerState.error}
                </Alert>
              ) : null}

              {ledgerState.loading || ledgerState.syncing ? (
                <Stack spacing={1}>
                  <Skeleton height={32} />
                  <Skeleton height={32} />
                  <Skeleton height={32} />
                  <Skeleton height={32} />
                </Stack>
              ) : ledgerEntries.length ? (
                <TableContainer sx={{ maxHeight: '52vh' }}>
                  <Table stickyHeader size="small" sx={{ minWidth: 980 }}>
                    <TableHead>
                      <TableRow>
                        <TableCell>Data</TableCell>
                        <TableCell>Tipo</TableCell>
                        <TableCell>Origem</TableCell>
                        <TableCell>Referência</TableCell>
                        <TableCell>Pagamento</TableCell>
                        <TableCell>Status</TableCell>
                        <TableCell align="right">Entrada</TableCell>
                        <TableCell align="right">Saída</TableCell>
                        <TableCell align="right">Saldo</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {ledgerEntries.map((entry, index) => (
                        <TableRow key={entry.id || entry.ledgerId || `${entry.entryAt}:${entry.description}:${index}`} hover>
                          <TableCell sx={{ whiteSpace: 'nowrap' }}>{formatDateTime(entry.entryAt)}</TableCell>
                          <TableCell>
                            <Chip size="small" label={entry.type} variant="outlined" sx={{ borderRadius: 2 }} />
                          </TableCell>
                          <TableCell>{entry.channelLabel}</TableCell>
                          <TableCell sx={{ minWidth: 190, fontWeight: 800 }}>{entry.description}</TableCell>
                          <TableCell>{entry.paymentLabel}</TableCell>
                          <TableCell>{entry.statusLabel}</TableCell>
                          <TableCell align="right" sx={{ color: 'success.main', fontWeight: 900, ...moneyColumnsSx }}>
                            {entry.credit ? formatCurrency(entry.credit) : '-'}
                          </TableCell>
                          <TableCell align="right" sx={{ color: 'error.main', fontWeight: 900, ...moneyColumnsSx }}>
                            {entry.debit ? formatCurrency(entry.debit) : '-'}
                          </TableCell>
                          <TableCell align="right" sx={{ fontWeight: 900, ...moneyColumnsSx }}>
                            {entry.balance || entry.balance === 0 ? formatCurrency(entry.balance) : '-'}
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
              ) : (
                <EmptyState
                  icon={<AccountBalanceWalletOutlinedIcon fontSize="large" />}
                  title="Ledger sem lançamentos"
                  helper="O período selecionado ainda não retornou registros."
                />
              )}
            </SectionCard>
          </Grid>
        </Grid>
      ) : null}

      {activeTab === 'contas' ? (
        <Grid container spacing={2} sx={{ mb: 2 }}>
          <Grid item xs={12} lg={8}>
            <SectionCard
              title="Contas financeiras"
              action={
                <Stack direction="row" spacing={1} alignItems="center">
                  {accountsState.lastUpdatedAt ? (
                    <Chip size="small" variant="outlined" label={formatDateTime(accountsState.lastUpdatedAt)} />
                  ) : null}
                  <Tooltip title="Gerar recorrências até o fim do período">
                    <span>
                      <Button
                        size="small"
                        variant="outlined"
                        startIcon={<AutorenewOutlinedIcon />}
                        onClick={generateRecurring}
                        disabled={accountsState.saving}
                        sx={{ borderRadius: 2, fontWeight: 800 }}
                      >
                        Gerar
                      </Button>
                    </span>
                  </Tooltip>
                </Stack>
              }
            >
              {accountsState.error ? (
                <Alert severity="error" sx={{ mb: 2 }}>
                  {accountsState.error}
                </Alert>
              ) : null}

              <Stack spacing={1.5} sx={{ mb: 2 }}>
                <Stack direction="row" spacing={1} sx={{ flexWrap: 'wrap', rowGap: 1 }}>
                  {ACCOUNT_QUICK_FILTERS.map((filter) => (
                    <Chip
                      key={filter.key}
                      clickable
                      label={filter.label}
                      color={accountFilters.quick === filter.key ? 'primary' : 'default'}
                      variant={accountFilters.quick === filter.key ? 'filled' : 'outlined'}
                      onClick={() =>
                        setAccountFilters((prev) => ({
                          ...prev,
                          quick: filter.key,
                          type: filter.type,
                          status: filter.status,
                        }))
                      }
                      sx={{ borderRadius: 2, fontWeight: 800 }}
                    />
                  ))}
                </Stack>

                <Stack direction={{ xs: 'column', md: 'row' }} spacing={1}>
                  <TextField
                    size="small"
                    label="Buscar"
                    value={accountFilters.q}
                    onChange={(e) => setAccountFilters((prev) => ({ ...prev, q: e.target.value }))}
                    sx={{ flex: 1 }}
                  />
                  <FormControl size="small" sx={{ minWidth: { xs: '100%', md: 210 } }}>
                    <InputLabel id="finance-account-category">Categoria</InputLabel>
                    <Select
                      labelId="finance-account-category"
                      label="Categoria"
                      value={accountFilters.categoryId}
                      onChange={(e) => setAccountFilters((prev) => ({ ...prev, categoryId: e.target.value }))}
                    >
                      <MenuItem value="all">Todas</MenuItem>
                      {accountsState.categories.map((category) => (
                        <MenuItem key={category.id} value={category.id}>
                          {category.name}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                  <FormControl size="small" sx={{ minWidth: { xs: '100%', md: 210 } }}>
                    <InputLabel id="finance-account-cost-center">Centro</InputLabel>
                    <Select
                      labelId="finance-account-cost-center"
                      label="Centro"
                      value={accountFilters.costCenterId}
                      onChange={(e) => setAccountFilters((prev) => ({ ...prev, costCenterId: e.target.value }))}
                    >
                      <MenuItem value="all">Todos</MenuItem>
                      {accountCostCenterOptions.map((center) => (
                        <MenuItem key={center.id} value={center.id}>
                          {center.name}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                  <Button
                    variant="contained"
                    onClick={loadAccounts}
                    disabled={accountsState.loading}
                    sx={{ borderRadius: 2, fontWeight: 800, minWidth: 108 }}
                  >
                    Aplicar
                  </Button>
                </Stack>
              </Stack>

              {accountsState.loading ? (
                <Stack spacing={1}>
                  <Skeleton height={32} />
                  <Skeleton height={32} />
                  <Skeleton height={32} />
                  <Skeleton height={32} />
                </Stack>
              ) : financialAccounts.length ? (
                <TableContainer sx={{ maxHeight: '58vh' }}>
                  <Table stickyHeader size="small" sx={{ minWidth: 1140 }}>
                    <TableHead>
                      <TableRow>
                        <TableCell>Vencimento</TableCell>
                        <TableCell>Tipo</TableCell>
                        <TableCell>Descrição</TableCell>
                        <TableCell>Categoria</TableCell>
                        <TableCell>Centro</TableCell>
                        <TableCell>Pessoa/origem</TableCell>
                        <TableCell>Status</TableCell>
                        <TableCell>Recorrência</TableCell>
                        <TableCell align="right">Valor</TableCell>
                        <TableCell align="right">Ações</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {financialAccounts.map((account) => {
                        const overdue = isOverdueFinancialAccount(account);
                        return (
                          <TableRow
                            key={account.id}
                            hover
                            sx={(theme) => ({
                              backgroundColor: overdue ? alpha(theme.palette.error.main, 0.045) : 'inherit',
                            })}
                          >
                            <TableCell sx={{ whiteSpace: 'nowrap' }}>{formatDateOnly(account.dueDate)}</TableCell>
                            <TableCell>
                              <Chip
                                size="small"
                                color={account.type === 'receivable' ? 'success' : 'error'}
                                variant="outlined"
                                label={normalizeAccountTypeLabel(account.type)}
                                sx={{ borderRadius: 2, fontWeight: 800 }}
                              />
                            </TableCell>
                            <TableCell sx={{ minWidth: 190, fontWeight: 800 }}>
                              {account.description}
                              {overdue ? (
                                <Tooltip title="Conta vencida">
                                  <WarningAmberOutlinedIcon color="error" fontSize="small" sx={{ ml: 0.75, verticalAlign: 'middle' }} />
                                </Tooltip>
                              ) : null}
                            </TableCell>
                            <TableCell>
                              <Chip
                                size="small"
                                variant="outlined"
                                label={account.categoryName || 'Sem categoria'}
                                sx={{ borderRadius: 2 }}
                              />
                            </TableCell>
                            <TableCell>
                              <Chip
                                size="small"
                                variant="outlined"
                                label={account.costCenterName || 'Sem centro'}
                                sx={{ borderRadius: 2 }}
                              />
                            </TableCell>
                            <TableCell>{account.partyName || '-'}</TableCell>
                            <TableCell>
                              <Chip
                                size="small"
                                color={getAccountStatusColor(account)}
                                variant={account.status === 'paid' ? 'filled' : 'outlined'}
                                label={normalizeAccountStatusLabel(account)}
                                sx={{ borderRadius: 2, fontWeight: 800 }}
                              />
                            </TableCell>
                            <TableCell>
                              {account.recurringRuleId ? (
                                <Chip
                                  size="small"
                                  icon={<AutorenewOutlinedIcon />}
                                  label={FREQUENCY_LABELS[account.recurringFrequency] || 'Recorrente'}
                                  variant="outlined"
                                  sx={{ borderRadius: 2 }}
                                />
                              ) : (
                                '-'
                              )}
                            </TableCell>
                            <TableCell align="right" sx={{ fontWeight: 900, ...moneyColumnsSx }}>
                              {formatCurrency(account.remainingAmount || account.amount)}
                            </TableCell>
                            <TableCell align="right">
                              <Tooltip title="Baixar conta">
                                <span>
                                  <IconButton
                                    size="small"
                                    onClick={() => settleAccount(account)}
                                    disabled={!isPendingFinancialAccount(account) || accountsState.saving}
                                  >
                                    <CheckCircleOutlineOutlinedIcon fontSize="small" />
                                  </IconButton>
                                </span>
                              </Tooltip>
                            </TableCell>
                          </TableRow>
                        );
                      })}
                    </TableBody>
                  </Table>
                </TableContainer>
              ) : (
                <EmptyState
                  icon={<RequestQuoteOutlinedIcon fontSize="large" />}
                  title="Nenhuma conta no período"
                  helper="Crie contas manuais ou gere recorrências para preencher esta visão."
                />
              )}
            </SectionCard>
          </Grid>

          <Grid item xs={12} lg={4}>
            <Stack spacing={2}>
              <SectionCard title="Nova conta">
                <Stack spacing={1.25}>
                  <Stack direction="row" spacing={1}>
                    <FormControl size="small" sx={{ flex: 1 }}>
                      <InputLabel id="finance-account-type">Tipo</InputLabel>
                      <Select
                        labelId="finance-account-type"
                        label="Tipo"
                        value={accountForm.type}
                        onChange={(e) => {
                          const nextType = e.target.value;
                          setAccountForm((prev) => {
                            const selected = accountsState.categories.find((category) => String(category.id) === String(prev.categoryId));
                            const keepCategory = !selected || selected.type === 'both' || selected.type === nextType;
                            return { ...prev, type: nextType, categoryId: keepCategory ? prev.categoryId : '' };
                          });
                        }}
                      >
                        <MenuItem value="payable">Pagar</MenuItem>
                        <MenuItem value="receivable">Receber</MenuItem>
                      </Select>
                    </FormControl>
                    <TextField
                      size="small"
                      label="Valor"
                      value={accountForm.amount}
                      onChange={(e) => setAccountForm((prev) => ({ ...prev, amount: e.target.value }))}
                      sx={{ flex: 1 }}
                    />
                  </Stack>
                  <TextField
                    size="small"
                    label="Descrição"
                    value={accountForm.description}
                    onChange={(e) => setAccountForm((prev) => ({ ...prev, description: e.target.value }))}
                  />
                  <TextField
                    size="small"
                    label="Vencimento"
                    type="date"
                    value={accountForm.dueDate}
                    onChange={(e) => setAccountForm((prev) => ({ ...prev, dueDate: e.target.value }))}
                    InputLabelProps={{ shrink: true }}
                  />
                  <FormControl size="small">
                    <InputLabel id="finance-new-account-category">Categoria</InputLabel>
                    <Select
                      labelId="finance-new-account-category"
                      label="Categoria"
                      value={accountForm.categoryId}
                      onChange={(e) => setAccountForm((prev) => ({ ...prev, categoryId: e.target.value }))}
                    >
                      <MenuItem value="">Sem categoria</MenuItem>
                      {accountCategoryOptions.map((category) => (
                        <MenuItem key={category.id} value={category.id}>
                          {category.name}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                  <FormControl size="small">
                    <InputLabel id="finance-new-account-cost-center">Centro de custo</InputLabel>
                    <Select
                      labelId="finance-new-account-cost-center"
                      label="Centro de custo"
                      value={accountForm.costCenterId}
                      onChange={(e) => setAccountForm((prev) => ({ ...prev, costCenterId: e.target.value }))}
                    >
                      <MenuItem value="">Sem centro</MenuItem>
                      {accountCostCenterOptions.map((center) => (
                        <MenuItem key={center.id} value={center.id}>
                          {center.name}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                  <FormControl size="small">
                    <InputLabel id="finance-new-account-party">Pessoa/origem</InputLabel>
                    <Select
                      labelId="finance-new-account-party"
                      label="Pessoa/origem"
                      value={accountForm.partyId}
                      onChange={(e) => setAccountForm((prev) => ({ ...prev, partyId: e.target.value }))}
                    >
                      <MenuItem value="">Não informado</MenuItem>
                      {accountsState.parties.map((party) => (
                        <MenuItem key={party.id} value={party.id}>
                          {party.name}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                  <Button
                    variant="contained"
                    startIcon={<AddCircleOutlineOutlinedIcon />}
                    onClick={saveAccount}
                    disabled={accountsState.saving}
                    sx={{ borderRadius: 2, fontWeight: 800 }}
                  >
                    Criar conta
                  </Button>
                </Stack>
              </SectionCard>

              <SectionCard title="Categorias" action={<CategoryOutlinedIcon color="action" />}>
                <Stack spacing={1.25}>
                  <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1}>
                    <TextField
                      size="small"
                      label="Nova categoria"
                      value={categoryForm.name}
                      onChange={(e) => setCategoryForm((prev) => ({ ...prev, name: e.target.value }))}
                      sx={{ flex: 1 }}
                    />
                    <FormControl size="small" sx={{ minWidth: 126 }}>
                      <InputLabel id="finance-category-type">Tipo</InputLabel>
                      <Select
                        labelId="finance-category-type"
                        label="Tipo"
                        value={categoryForm.type}
                        onChange={(e) => setCategoryForm((prev) => ({ ...prev, type: e.target.value }))}
                      >
                        <MenuItem value="both">Ambos</MenuItem>
                        <MenuItem value="payable">Pagar</MenuItem>
                        <MenuItem value="receivable">Receber</MenuItem>
                      </Select>
                    </FormControl>
                  </Stack>
                  <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1}>
                    <FormControl size="small" sx={{ flex: 1 }}>
                      <InputLabel id="finance-category-dre-group">DRE</InputLabel>
                      <Select
                        labelId="finance-category-dre-group"
                        label="DRE"
                        value={categoryForm.dreGroup}
                        onChange={(e) => {
                          const dreGroup = e.target.value;
                          setCategoryForm((prev) => ({
                            ...prev,
                            dreGroup,
                            costBehavior: ['sales_revenue', 'other_revenue', 'out_of_dre'].includes(dreGroup)
                              ? 'none'
                              : prev.costBehavior === 'none'
                                ? 'variable'
                                : prev.costBehavior,
                          }));
                        }}
                      >
                        {DRE_GROUP_OPTIONS.map((option) => (
                          <MenuItem key={option.key} value={option.key}>
                            {option.label}
                          </MenuItem>
                        ))}
                      </Select>
                    </FormControl>
                    <FormControl size="small" sx={{ minWidth: 142 }}>
                      <InputLabel id="finance-category-cost-behavior">Custo</InputLabel>
                      <Select
                        labelId="finance-category-cost-behavior"
                        label="Custo"
                        value={categoryForm.costBehavior}
                        onChange={(e) => setCategoryForm((prev) => ({ ...prev, costBehavior: e.target.value }))}
                      >
                        {COST_BEHAVIOR_OPTIONS.map((option) => (
                          <MenuItem key={option.key} value={option.key}>
                            {option.label}
                          </MenuItem>
                        ))}
                      </Select>
                    </FormControl>
                  </Stack>
                  <Button
                    size="small"
                    variant="outlined"
                    onClick={saveCategory}
                    disabled={accountsState.saving || !categoryForm.name.trim()}
                    sx={{ borderRadius: 2, fontWeight: 800 }}
                  >
                    Salvar categoria
                  </Button>
                  <Divider />
                  {categoryOverviewRows.length ? (
                    <Stack spacing={1}>
                      {categoryOverviewRows.map((category) => (
                        <Box
                          key={category.id || category.name}
                          sx={(theme) => ({
                            p: 1,
                            borderRadius: 2,
                            border: '1px solid',
                            borderColor: 'divider',
                            backgroundColor: alpha(theme.palette.text.primary, 0.025),
                          })}
                        >
                          <Stack direction="row" spacing={1} alignItems="center" justifyContent="space-between">
                            <Stack spacing={0.5} sx={{ minWidth: 0 }}>
                              <Typography variant="body2" sx={{ fontWeight: 900 }} noWrap>
                                {category.name}
                              </Typography>
                              <Stack direction="row" spacing={0.75} sx={{ flexWrap: 'wrap', rowGap: 0.75 }}>
                                <Chip size="small" label={getDreGroupLabel(category.dreGroup)} variant="outlined" sx={{ height: 22, borderRadius: 1.5 }} />
                                <Chip size="small" label={ACCOUNT_TYPE_LABELS[category.type] || 'Ambos'} variant="outlined" sx={{ height: 22, borderRadius: 1.5 }} />
                              </Stack>
                            </Stack>
                            <Stack alignItems="flex-end" spacing={0.25}>
                              <Typography variant="body2" sx={{ fontWeight: 900, ...moneyColumnsSx }}>
                                {formatCurrency(category.total)}
                              </Typography>
                              <Typography variant="caption" color="text.secondary">
                                {category.count || 0} conta(s)
                              </Typography>
                            </Stack>
                          </Stack>
                        </Box>
                      ))}
                    </Stack>
                  ) : (
                    <BarList rows={accountCategoryRows} color="secondary" emptyLabel="Sem valores por categoria" />
                  )}
                </Stack>
              </SectionCard>

              <SectionCard title="Centros de custo" action={<BalanceOutlinedIcon color="action" />}>
                <Stack spacing={1}>
                  {costCenterOverviewRows.length ? (
                    costCenterOverviewRows.slice(0, 6).map((center) => (
                      <Stack key={center.id || center.label} direction="row" spacing={1} alignItems="center" justifyContent="space-between">
                        <Stack direction="row" spacing={1} alignItems="center" sx={{ minWidth: 0 }}>
                          <Box
                            sx={{
                              width: 10,
                              height: 10,
                              borderRadius: 1,
                              backgroundColor: center.color || '#1976d2',
                              flex: '0 0 auto',
                            }}
                          />
                          <Typography variant="body2" sx={{ fontWeight: 800 }} noWrap>
                            {center.label}
                          </Typography>
                        </Stack>
                        <Typography variant="body2" sx={{ fontWeight: 900, ...moneyColumnsSx }}>
                          {formatCurrency(center.total)}
                        </Typography>
                      </Stack>
                    ))
                  ) : (
                    <Typography variant="body2" color="text.secondary">
                      Nenhum centro cadastrado.
                    </Typography>
                  )}
                </Stack>
              </SectionCard>

              <SectionCard title="Pessoas e fornecedores">
                <Stack spacing={1.25}>
                  <Stack direction="row" spacing={1}>
                    <TextField
                      size="small"
                      label="Nome"
                      value={partyForm.name}
                      onChange={(e) => setPartyForm((prev) => ({ ...prev, name: e.target.value }))}
                      sx={{ flex: 1 }}
                    />
                    <FormControl size="small" sx={{ width: 128 }}>
                      <InputLabel id="finance-party-type">Tipo</InputLabel>
                      <Select
                        labelId="finance-party-type"
                        label="Tipo"
                        value={partyForm.type}
                        onChange={(e) => setPartyForm((prev) => ({ ...prev, type: e.target.value }))}
                      >
                        <MenuItem value="supplier">Fornecedor</MenuItem>
                        <MenuItem value="customer">Cliente</MenuItem>
                        <MenuItem value="other">Outro</MenuItem>
                      </Select>
                    </FormControl>
                  </Stack>
                  <Button
                    size="small"
                    variant="outlined"
                    onClick={saveParty}
                    disabled={accountsState.saving || !partyForm.name.trim()}
                    sx={{ borderRadius: 2, fontWeight: 800 }}
                  >
                    Salvar pessoa/origem
                  </Button>
                  <Stack spacing={0.75}>
                    {accountsState.parties.slice(0, 5).map((party) => (
                      <Stack key={party.id} direction="row" justifyContent="space-between" alignItems="center">
                        <Typography variant="body2" sx={{ fontWeight: 800 }} noWrap>
                          {party.name}
                        </Typography>
                        <Chip size="small" variant="outlined" label={party.type === 'supplier' ? 'Fornecedor' : party.type === 'customer' ? 'Cliente' : 'Outro'} />
                      </Stack>
                    ))}
                    {!accountsState.parties.length ? (
                      <Typography variant="body2" color="text.secondary">
                        Nenhum cadastro ainda.
                      </Typography>
                    ) : null}
                  </Stack>
                </Stack>
              </SectionCard>

              <SectionCard title="Recorrências" action={<AutorenewOutlinedIcon color="action" />}>
                <Stack spacing={1.25}>
                  <Stack direction="row" spacing={1}>
                    <FormControl size="small" sx={{ width: 116 }}>
                      <InputLabel id="finance-recurring-type">Tipo</InputLabel>
                      <Select
                        labelId="finance-recurring-type"
                        label="Tipo"
                        value={recurringForm.type}
                        onChange={(e) => setRecurringForm((prev) => ({ ...prev, type: e.target.value }))}
                      >
                        <MenuItem value="payable">Pagar</MenuItem>
                        <MenuItem value="receivable">Receber</MenuItem>
                      </Select>
                    </FormControl>
                    <TextField
                      size="small"
                      label="Valor"
                      value={recurringForm.amount}
                      onChange={(e) => setRecurringForm((prev) => ({ ...prev, amount: e.target.value }))}
                      sx={{ flex: 1 }}
                    />
                  </Stack>
                  <TextField
                    size="small"
                    label="Descrição"
                    value={recurringForm.description}
                    onChange={(e) => setRecurringForm((prev) => ({ ...prev, description: e.target.value }))}
                  />
                  <Stack direction="row" spacing={1}>
                    <TextField
                      size="small"
                      label="Próximo vencimento"
                      type="date"
                      value={recurringForm.nextDueDate}
                      onChange={(e) => setRecurringForm((prev) => ({ ...prev, nextDueDate: e.target.value }))}
                      InputLabelProps={{ shrink: true }}
                      sx={{ flex: 1 }}
                    />
                    <FormControl size="small" sx={{ width: 132 }}>
                      <InputLabel id="finance-frequency">Frequência</InputLabel>
                      <Select
                        labelId="finance-frequency"
                        label="Frequência"
                        value={recurringForm.frequency}
                        onChange={(e) => setRecurringForm((prev) => ({ ...prev, frequency: e.target.value }))}
                      >
                        {Object.entries(FREQUENCY_LABELS).map(([value, label]) => (
                          <MenuItem key={value} value={value}>
                            {label}
                          </MenuItem>
                        ))}
                      </Select>
                    </FormControl>
                  </Stack>
                  <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1}>
                    <FormControl size="small" sx={{ flex: 1 }}>
                      <InputLabel id="finance-recurring-category">Categoria</InputLabel>
                      <Select
                        labelId="finance-recurring-category"
                        label="Categoria"
                        value={recurringForm.categoryId}
                        onChange={(e) => setRecurringForm((prev) => ({ ...prev, categoryId: e.target.value }))}
                      >
                        <MenuItem value="">Sem categoria</MenuItem>
                        {recurringCategoryOptions.map((category) => (
                          <MenuItem key={category.id} value={category.id}>
                            {category.name}
                          </MenuItem>
                        ))}
                      </Select>
                    </FormControl>
                    <FormControl size="small" sx={{ flex: 1 }}>
                      <InputLabel id="finance-recurring-cost-center">Centro</InputLabel>
                      <Select
                        labelId="finance-recurring-cost-center"
                        label="Centro"
                        value={recurringForm.costCenterId}
                        onChange={(e) => setRecurringForm((prev) => ({ ...prev, costCenterId: e.target.value }))}
                      >
                        <MenuItem value="">Sem centro</MenuItem>
                        {accountCostCenterOptions.map((center) => (
                          <MenuItem key={center.id} value={center.id}>
                            {center.name}
                          </MenuItem>
                        ))}
                      </Select>
                    </FormControl>
                  </Stack>
                  <Button
                    size="small"
                    variant="outlined"
                    onClick={saveRecurringRule}
                    disabled={accountsState.saving || !recurringForm.description.trim() || !recurringForm.amount}
                    sx={{ borderRadius: 2, fontWeight: 800 }}
                  >
                    Salvar recorrência
                  </Button>
                  <Divider />
                  <Stack spacing={0.75}>
                    {accountsState.recurringRules.slice(0, 5).map((rule) => (
                      <Stack key={rule.id} direction="row" justifyContent="space-between" alignItems="center" spacing={1}>
                        <Box sx={{ minWidth: 0 }}>
                          <Typography variant="body2" sx={{ fontWeight: 800 }} noWrap>
                            {rule.description}
                          </Typography>
                          <Typography variant="caption" color="text.secondary">
                            {FREQUENCY_LABELS[rule.frequency] || rule.frequency} · {formatDateOnly(rule.nextDueDate)}
                          </Typography>
                        </Box>
                        <Typography variant="body2" sx={{ fontWeight: 900, ...moneyColumnsSx }}>
                          {formatCurrency(rule.amount)}
                        </Typography>
                      </Stack>
                    ))}
                    {!accountsState.recurringRules.length ? (
                      <Typography variant="body2" color="text.secondary">
                        Nenhuma recorrência ativa.
                      </Typography>
                    ) : null}
                  </Stack>
                </Stack>
              </SectionCard>
            </Stack>
          </Grid>
        </Grid>
      ) : null}

      {activeTab === 'caixa' ? (
        <Grid container spacing={2} sx={{ mb: 2 }}>
          <Grid item xs={12} sm={6} lg={3}>
            <CompactStat label="Vendas" value={formatCurrency(cashClosingSummary.sales)} color="success" />
          </Grid>
          <Grid item xs={12} sm={6} lg={3}>
            <CompactStat label="Esperado" value={formatCurrency(cashClosingSummary.expected)} color="primary" />
          </Grid>
          <Grid item xs={12} sm={6} lg={3}>
            <CompactStat label="Conferido" value={formatCurrency(cashClosingSummary.counted)} color="info" />
          </Grid>
          <Grid item xs={12} sm={6} lg={3}>
            <CompactStat
              label="Diferença"
              value={formatCurrency(cashClosingSummary.difference)}
              color={cashClosingSummary.difference ? 'warning' : 'success'}
            />
          </Grid>

          <Grid item xs={12} lg={5}>
            <SectionCard
              title="Conferência do caixa"
              action={
                <Stack direction="row" spacing={1} alignItems="center">
                  <Chip size="small" variant="outlined" label={filters.end} />
                  <Tooltip title="Atualizar fechamentos">
                    <span>
                      <IconButton size="small" onClick={loadCashClosing} disabled={cashClosingState.loading || cashClosingState.saving}>
                        <RefreshIcon fontSize="small" />
                      </IconButton>
                    </span>
                  </Tooltip>
                </Stack>
              }
            >
              {cashClosingState.error ? (
                <Alert severity="error" sx={{ mb: 2 }}>
                  {cashClosingState.error}
                </Alert>
              ) : null}

              {cashClosingState.loading ? (
                <Stack spacing={1}>
                  <Skeleton height={32} />
                  <Skeleton height={32} />
                  <Skeleton height={32} />
                </Stack>
              ) : (
                <Stack spacing={1.5}>
                  {cashClosingRecord?.status === 'closed' ? (
                    <Alert severity="success" sx={{ borderRadius: 2 }}>
                      Caixa fechado por {cashClosingRecord.operator} em {formatDateTime(cashClosingRecord.closingAt)}.
                    </Alert>
                  ) : null}

                  <Grid container spacing={1.5}>
                    <Grid item xs={12} sm={6}>
                      <TextField
                        label="Saldo inicial"
                        size="small"
                        fullWidth
                        value={cashForm.openingBalance}
                        onChange={(e) => setCashForm((prev) => ({ ...prev, openingBalance: e.target.value }))}
                      />
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      <TextField
                        label="Valor contado"
                        size="small"
                        fullWidth
                        value={cashForm.reportedTotal}
                        onChange={(e) => setCashForm((prev) => ({ ...prev, reportedTotal: e.target.value }))}
                      />
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      <TextField
                        label="Suprimento"
                        size="small"
                        fullWidth
                        value={cashForm.supplyAmount}
                        onChange={(e) => setCashForm((prev) => ({ ...prev, supplyAmount: e.target.value }))}
                      />
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      <TextField
                        label="Sangria"
                        size="small"
                        fullWidth
                        value={cashForm.withdrawalAmount}
                        onChange={(e) => setCashForm((prev) => ({ ...prev, withdrawalAmount: e.target.value }))}
                      />
                    </Grid>
                    <Grid item xs={12}>
                      <TextField
                        label="Observações"
                        size="small"
                        fullWidth
                        multiline
                        minRows={3}
                        value={cashForm.notes}
                        onChange={(e) => setCashForm((prev) => ({ ...prev, notes: e.target.value }))}
                      />
                    </Grid>
                  </Grid>

                  <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1} justifyContent="flex-end">
                    <Button
                      variant="outlined"
                      onClick={() => saveCashClosing('open')}
                      disabled={cashClosingState.saving}
                      sx={{ borderRadius: 2, fontWeight: 800 }}
                    >
                      Salvar conferência
                    </Button>
                    <Button
                      variant="contained"
                      onClick={() => saveCashClosing('closed')}
                      disabled={cashClosingState.saving}
                      sx={{ borderRadius: 2, fontWeight: 800 }}
                    >
                      Fechar caixa
                    </Button>
                  </Stack>
                </Stack>
              )}
            </SectionCard>
          </Grid>

          <Grid item xs={12} lg={7}>
            <SectionCard
              title="Composição esperada"
              action={<Chip size="small" variant="outlined" label={`${cashExpected.entryCount || 0} venda(s)`} />}
            >
              {cashClosingState.loading ? (
                <Stack spacing={1}>
                  <Skeleton height={32} />
                  <Skeleton height={32} />
                  <Skeleton height={32} />
                </Stack>
              ) : (
                <TableContainer>
                  <Table size="small" sx={{ minWidth: 620 }}>
                    <TableHead>
                      <TableRow>
                        <TableCell>Forma</TableCell>
                        <TableCell align="right">Valor</TableCell>
                        <TableCell align="right">Participação</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {cashCompositionRows.map((row) => {
                        const ratio = cashClosingSummary.sales ? row.value / cashClosingSummary.sales : 0;
                        return (
                          <TableRow key={row.label} hover>
                            <TableCell>
                              <Chip size="small" label={row.label} color={row.color} variant="outlined" sx={{ borderRadius: 2 }} />
                            </TableCell>
                            <TableCell align="right" sx={{ fontWeight: 900, ...moneyColumnsSx }}>
                              {formatCurrency(row.value)}
                            </TableCell>
                            <TableCell align="right" sx={moneyColumnsSx}>
                              {formatPercent(ratio)}
                            </TableCell>
                          </TableRow>
                        );
                      })}
                      <TableRow>
                        <TableCell sx={{ fontWeight: 900 }}>Esperado</TableCell>
                        <TableCell align="right" sx={{ fontWeight: 900, ...moneyColumnsSx }}>
                          {formatCurrency(cashClosingSummary.expected)}
                        </TableCell>
                        <TableCell align="right">-</TableCell>
                      </TableRow>
                    </TableBody>
                  </Table>
                </TableContainer>
              )}
            </SectionCard>
          </Grid>
        </Grid>
      ) : null}

      <Paper
        elevation={0}
        sx={{
          borderRadius: 2,
          border: '1px solid',
          borderColor: 'divider',
          display: activeTab === 'ledger' || activeTab === 'dre' || activeTab === 'despesas' || activeTab === 'gerencial' || activeTab === 'conciliacao' || activeTab === 'ifoodFinancial' || activeTab === 'caixa' || activeTab === 'contas' ? 'none' : 'block',
        }}
      >
        <Box sx={{ p: 2 }}>
          <Stack
            direction={{ xs: 'column', sm: 'row' }}
            alignItems={{ sm: 'center' }}
            justifyContent="space-between"
            spacing={1}
            sx={{ mb: 1 }}
          >
            <Box>
              <Typography variant="subtitle1" sx={{ fontWeight: 900 }}>
                Movimentações
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Valores por pedido antes da conciliação oficial: bruto, desconto, entrega, total operacional, pagamento e status.
              </Typography>
            </Box>
            <Chip size="small" variant="outlined" label={`${enrichedMovements.length} registro(s)`} />
          </Stack>
          <Divider />
        </Box>

        <TableContainer sx={{ maxHeight: '62vh' }}>
          <Table stickyHeader size="small" sx={{ minWidth: 1160 }}>
            <TableHead>
              <TableRow>
                <TableCell>Data</TableCell>
                <TableCell>Canal</TableCell>
                <TableCell>Pedido</TableCell>
                <TableCell>Cliente</TableCell>
                <TableCell align="right">Bruto do pedido</TableCell>
                <TableCell align="right">Descontos</TableCell>
                <TableCell align="right">Entrega cobrada</TableCell>
                <TableCell align="right">Total operacional</TableCell>
                <TableCell>Pagamento</TableCell>
                <TableCell>Entrega/retirada</TableCell>
                <TableCell>Status</TableCell>
                <TableCell>Dados</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {state.loading
                ? Array.from({ length: 7 }).map((_, idx) => (
                    <TableRow key={`skeleton-${idx}`}>
                      <TableCell colSpan={tableColSpan}>
                        <Skeleton height={28} />
                      </TableCell>
                    </TableRow>
                  ))
                : enrichedMovements.map((movement) => {
                    const channel = CHANNELS.find((item) => item.key === movement.channel);
                    return (
                      <TableRow key={movement.id || `${movement.channel}:${movement.orderId}`} hover>
                        <TableCell sx={{ whiteSpace: 'nowrap' }}>{formatDateTime(movement.movementAt)}</TableCell>
                        <TableCell>
                          <Chip
                            size="small"
                            label={normalizeChannelLabel(movement.channel, movement.salesChannel)}
                            color={channel?.color || 'default'}
                            variant="outlined"
                            sx={{ borderRadius: 2, fontWeight: 800 }}
                          />
                        </TableCell>
                        <TableCell sx={{ fontWeight: 800, whiteSpace: 'nowrap' }}>
                          {movement.displayId || movement.orderId}
                        </TableCell>
                        <TableCell sx={{ minWidth: 150 }}>{movement.customerName || '-'}</TableCell>
                        <TableCell align="right" sx={moneyColumnsSx}>
                          {movement.amountKnown === false ? '—' : formatCurrency(movement.amounts.gross)}
                        </TableCell>
                        <TableCell align="right" sx={moneyColumnsSx}>
                          {movement.amounts.discount ? formatCurrency(movement.amounts.discount) : '-'}
                        </TableCell>
                        <TableCell align="right" sx={moneyColumnsSx}>
                          {movement.amounts.delivery ? formatCurrency(movement.amounts.delivery) : '-'}
                        </TableCell>
                        <TableCell align="right" sx={{ fontWeight: 900, ...moneyColumnsSx }}>
                          {movement.amountKnown === false ? 'Não informado pela 99Food' : formatCurrency(movement.amounts.total)}
                        </TableCell>
                        <TableCell>
                          <Chip size="small" label={movement.paymentLabel} variant="outlined" sx={{ borderRadius: 2 }} />
                        </TableCell>
                        <TableCell>{movement.fulfillmentLabel}</TableCell>
                        <TableCell>
                          <Chip
                            size="small"
                            label={movement.statusLabel}
                            color={movement.canceled ? 'error' : 'default'}
                            variant={movement.canceled ? 'filled' : 'outlined'}
                            sx={{ borderRadius: 2, fontWeight: 800 }}
                          />
                        </TableCell>
                        <TableCell>
                          {movement.qualityFlags.length ? (
                            <Tooltip title={movement.qualityFlags.join(' | ')}>
                              <Chip size="small" color="warning" label={`${movement.qualityFlags.length} alerta(s)`} />
                            </Tooltip>
                          ) : (
                            <Chip size="small" color="success" variant="outlined" label="OK" />
                          )}
                        </TableCell>
                      </TableRow>
                    );
                  })}
              {!state.loading && enrichedMovements.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={tableColSpan}>
                    <Box sx={{ py: 4, textAlign: 'center' }}>
                      <Typography variant="subtitle1" sx={{ fontWeight: 900 }}>
                        Nenhuma movimentação no período
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        Ajuste o período ou selecione outro canal para visualizar.
                      </Typography>
                    </Box>
                  </TableCell>
                </TableRow>
              ) : null}
            </TableBody>
          </Table>
        </TableContainer>
      </Paper>
    </Box>
  );
};

export default Financeiro;
